import type { EAppointmentType } from "@/types/backoffice/dto/appointment/appointment-type-dto.ts";
import type { EInstallSatisfactionApplianceInstalledDto } from "@/types/backoffice/dto/survey/install-satisfaction/install-satisfaction-appliance-installed-dto.ts";

import { joinDataTestId } from "@/constants/data-test-ids.utils";
import { activeSubscriptionCardDataTestIds } from "@/constants/data-test-ids/components/active-subscription-card-data-test-ids.ts";
import { appointmentCalendarDataTestIds } from "@/constants/data-test-ids/components/appointment-calendar-data-test-ids.ts";
import { buttonSelectDataTestIds } from "@/constants/data-test-ids/components/button-select-data-test-ids.ts";
import { editApplianceEstimatesModalForm } from "@/constants/data-test-ids/components/edit-appliance-estimates-modal-form-data-test-ids.ts";
import { formErrorWitnessDataTestIds } from "@/constants/data-test-ids/components/form-error-witness-data-test-ids";
import { slotButtonDataTestIds } from "@/constants/data-test-ids/components/slot-button-data-test-ids.ts";
import { starsRatingDataTestIds } from "@/constants/data-test-ids/components/stars-rating-data-test-ids.ts";
import { subscriptionEmptyStateCardDataTestIds } from "@/constants/data-test-ids/components/subscription-empty-state-card-data-test-ids.ts";
import { subscriptionHistoryDataTestIds } from "@/constants/data-test-ids/components/subscription-history-data-test-ids.ts";
import { weekChipDataTestIds } from "@/constants/data-test-ids/components/week-chip-data-test-ids.ts";
import { weekGridDataTestIds } from "@/constants/data-test-ids/components/week-grid-data-test-ids.ts";
import { EApplianceEstimateCode } from "@/domains/site/site.types.ts";

export const DATA_TEST_IDS = {
  views: {
    siteDetails: {
      view: "site-details" as const,
      spinner: (): string => joinDataTestId(DATA_TEST_IDS.views.siteDetails.view, "spinner"),
      siteReinterventionSidebar: (): string =>
        joinDataTestId(DATA_TEST_IDS.views.siteDetails.view, "site-reintervention-sidebar"),
    },
    siteDetailsConsumptionView: {
      view: "site-details-consumption-view" as const,
      consumptionsCard: (): string =>
        joinDataTestId(DATA_TEST_IDS.views.siteDetailsConsumptionView.view, "consumptions-card"),
    },
    siteDetailsSupportView: {
      view: "site-details-support-view" as const,
      supportFormCard: (): string =>
        joinDataTestId(DATA_TEST_IDS.views.siteDetailsDeliveryPointView.view, "support-form-card"),
    },
    siteDetailsDeliveryPointView: {
      view: "site-details-delivery-point-view" as const,
      deliveryPointSiteInfoMessage: (): string =>
        joinDataTestId(DATA_TEST_IDS.views.siteDetailsDeliveryPointView.view, "delivery-point-site-info-message"),
      deliveryPointAssignCard: (): string =>
        joinDataTestId(DATA_TEST_IDS.views.siteDetailsDeliveryPointView.view, "delivery-point-assign-card"),
    },
    siteDetailsOverviewView: {
      view: "site-details-overview-view" as const,
      siteAlerts: (): string => joinDataTestId(DATA_TEST_IDS.views.siteDetailsOverviewView.view, "site-alerts"),
      deactivatedSiteLabel: (): string =>
        joinDataTestId(DATA_TEST_IDS.views.siteDetailsOverviewView.view, "deactivated-site-label"),
      myvAccessButton: (): string =>
        joinDataTestId(DATA_TEST_IDS.views.siteDetailsOverviewView.view, "myv-access-button"),
      siteDetailsCard: (): string =>
        joinDataTestId(DATA_TEST_IDS.views.siteDetailsOverviewView.view, "site-details-card"),
      skeleton: (): string => joinDataTestId(DATA_TEST_IDS.views.siteDetailsOverviewView.view, "skeleton"),
      subscriptionSkeleton: (): string =>
        joinDataTestId(DATA_TEST_IDS.views.siteDetailsOverviewView.view, "subscription-skeleton"),
      subscriberDetailsCard: (): string =>
        joinDataTestId(DATA_TEST_IDS.views.siteDetailsOverviewView.view, "subscriber-details-card"),
      attachSubscriberCard: (): string =>
        joinDataTestId(DATA_TEST_IDS.views.siteDetailsOverviewView.view, "attach-subscriber-card"),
      acquisitionWorkingConditionCard: (): string =>
        joinDataTestId(DATA_TEST_IDS.views.siteDetailsOverviewView.view, "acquisition-working-condition-card"),
      modulationWorkingConditionCard: (): string =>
        joinDataTestId(DATA_TEST_IDS.views.siteDetailsOverviewView.view, "modulation-working-condition-card"),
      siteDeliveryPointCard: (): string =>
        joinDataTestId(DATA_TEST_IDS.views.siteDetailsOverviewView.view, "site-delivery-point-card"),
      siteEscCard: (): string => joinDataTestId(DATA_TEST_IDS.views.siteDetailsOverviewView.view, "site-esc-card"),
      sitePartnershipCard: (): string =>
        joinDataTestId(DATA_TEST_IDS.views.siteDetailsOverviewView.view, "site-partnership-card"),
      siteEventsCardRefactored: (): string =>
        joinDataTestId(DATA_TEST_IDS.views.siteDetailsOverviewView.view, "site-events-card-refactored"),
      siteConsumptionsCard: (): string =>
        joinDataTestId(DATA_TEST_IDS.views.siteDetailsOverviewView.view, "site-consumptions-card"),
      siteAutoDiagCard: (): string =>
        joinDataTestId(DATA_TEST_IDS.views.siteDetailsOverviewView.view, "site-auto-diag-card"),
      sitePushButtonCard: (): string =>
        joinDataTestId(DATA_TEST_IDS.views.siteDetailsOverviewView.view, "site-push-button-card"),
      siteRecentActionsCard: (): string =>
        joinDataTestId(DATA_TEST_IDS.views.siteDetailsOverviewView.view, "site-recent-actions-card"),
      siteConstraintsSummaryCard: (): string =>
        joinDataTestId(DATA_TEST_IDS.views.siteDetailsOverviewView.view, "site-constraints-summary-card"),
    },
    siteDetailsRotationView: {
      view: "site-details-rotation-view" as const,
      rotationFormCard: (): string =>
        joinDataTestId(DATA_TEST_IDS.views.siteDetailsRotationView.view, "rotation-form-card"),
    },
    siteDetailsEligibilityView: {
      view: "site-details-eligibility-view" as const,
      card: (): string => joinDataTestId(DATA_TEST_IDS.views.siteDetailsEligibilityView.view, "card"),
      cardHeader: (): string => joinDataTestId(DATA_TEST_IDS.views.siteDetailsEligibilityView.view, "card-header"),
      cardTitle: (): string => joinDataTestId(DATA_TEST_IDS.views.siteDetailsEligibilityView.view, "card-title"),
      cardContent: (): string => joinDataTestId(DATA_TEST_IDS.views.siteDetailsEligibilityView.view, "card-content"),
      siteEscEligibilityForm: (): string =>
        joinDataTestId(DATA_TEST_IDS.views.siteDetailsEligibilityView.view, "site-esc-eligibility-form"),
      skeleton: (): string => joinDataTestId(DATA_TEST_IDS.views.siteDetailsEligibilityView.view, "skeleton"),
    },
    siteDetailsGalleryView: {
      view: "site-details-gallery-view" as const,
      toggleFilter: (): string => joinDataTestId(DATA_TEST_IDS.views.siteDetailsGalleryView.view, "toggle-filter"),
      toggleLayout: (): string => joinDataTestId(DATA_TEST_IDS.views.siteDetailsGalleryView.view, "toggle-layout"),
      title: (): string => joinDataTestId(DATA_TEST_IDS.views.siteDetailsGalleryView.view, "title"),
      uploadPhotoBtn: (): string => joinDataTestId(DATA_TEST_IDS.views.siteDetailsGalleryView.view, "upload-photo-btn"),
      sizeSmallBtn: (): string => joinDataTestId(DATA_TEST_IDS.views.siteDetailsGalleryView.view, "size-small-btn"),
      sizeMediumBtn: (): string => joinDataTestId(DATA_TEST_IDS.views.siteDetailsGalleryView.view, "size-medium-btn"),
      sizeLargeBtn: (): string => joinDataTestId(DATA_TEST_IDS.views.siteDetailsGalleryView.view, "size-large-btn"),
      filterByDateBtn: (): string =>
        joinDataTestId(DATA_TEST_IDS.views.siteDetailsGalleryView.view, "filter-by-date-btn"),
      filterByTypeBtn: (): string =>
        joinDataTestId(DATA_TEST_IDS.views.siteDetailsGalleryView.view, "filter-by-type-btn"),
      sortByChangedAscBtn: (): string =>
        joinDataTestId(DATA_TEST_IDS.views.siteDetailsGalleryView.view, "sort-by-changed-asc-btn"),
      sortByChangedDescBtn: (): string =>
        joinDataTestId(DATA_TEST_IDS.views.siteDetailsGalleryView.view, "sort-by-changed-desc-btn"),
      photoSelect: (): string => joinDataTestId(DATA_TEST_IDS.views.siteDetailsGalleryView.view, "photo-select"),
      skeletons: (): string => joinDataTestId(DATA_TEST_IDS.views.siteDetailsGalleryView.view, "skeletons"),
      rowDateLabel: (): string => joinDataTestId(DATA_TEST_IDS.views.siteDetailsGalleryView.view, "row-date-label"),
      rowLabel: (): string => joinDataTestId(DATA_TEST_IDS.views.siteDetailsGalleryView.view, "row-label"),
      sitePhotoImage: (): string => joinDataTestId(DATA_TEST_IDS.views.siteDetailsGalleryView.view, "site-photo-image"),
    },
    loginView: {
      view: "login-view" as const,
      languageSelect: (): string => joinDataTestId(DATA_TEST_IDS.views.loginView.view, "language-select"),
      logoImg: (): string => joinDataTestId(DATA_TEST_IDS.views.loginView.view, "logo-img"),
      welcomeTitle: (): string => joinDataTestId(DATA_TEST_IDS.views.loginView.view, "welcome-title"),
      connectSubtitle: (): string => joinDataTestId(DATA_TEST_IDS.views.loginView.view, "connect-subtitle"),
      loginForm: (): string => joinDataTestId(DATA_TEST_IDS.views.loginView.view, "login-form"),
      loginIllustration: (): string => joinDataTestId(DATA_TEST_IDS.views.loginView.view, "login-illustration"),
    },
    createSiteView: {
      view: "create-site-view",
      stepper: (): string => joinDataTestId(DATA_TEST_IDS.views.createSiteView.view, "stepper"),
      stepperPanelSubscriber: (): string =>
        joinDataTestId(DATA_TEST_IDS.views.createSiteView.view, "stepper-panel-subscriber"),
      stepperPanelSite: (): string => joinDataTestId(DATA_TEST_IDS.views.createSiteView.view, "stepper-panel-site"),
      subscriberCard: (): string => joinDataTestId(DATA_TEST_IDS.views.createSiteView.view, "subscriber-card"),
      subscriberCardHeader: (): string =>
        joinDataTestId(DATA_TEST_IDS.views.createSiteView.view, "subscriber-card-header"),
      subscriberCardTitle: (): string =>
        joinDataTestId(DATA_TEST_IDS.views.createSiteView.view, "subscriber-card-title"),
      subscriberCardContent: (): string =>
        joinDataTestId(DATA_TEST_IDS.views.createSiteView.view, "subscriber-card-content"),
      siteCard: (): string => joinDataTestId(DATA_TEST_IDS.views.createSiteView.view, "site-card"),
      siteCardHeader: (): string => joinDataTestId(DATA_TEST_IDS.views.createSiteView.view, "site-card-header"),
      siteCardTitle: (): string => joinDataTestId(DATA_TEST_IDS.views.createSiteView.view, "site-card-title"),
      createSiteForm: (): string => joinDataTestId(DATA_TEST_IDS.views.createSiteView.view, "create-site-form"),
      createSubscriberForm: (): string =>
        joinDataTestId(DATA_TEST_IDS.views.createSiteView.view, "create-subscriber-form"),
      previousBtn: (): string => joinDataTestId(DATA_TEST_IDS.views.createSiteView.view, "previous-btn"),
    },
    dashboardView: {
      view: "dashboard-view" as const,
      limitWarning: (): string => joinDataTestId(DATA_TEST_IDS.views.dashboardView.view, "limit-warning"),
      openedFoldersCard: (): string => joinDataTestId(DATA_TEST_IDS.views.dashboardView.view, "opened-folders-card"),
    },
    searchSiteView: {
      view: "search-site-view" as const,
      createSiteBtn: (): string => joinDataTestId(DATA_TEST_IDS.views.searchSiteView.view, "create-site-button"),
      hasReachedMaxInterventionLimitWarning: (): string =>
        joinDataTestId(DATA_TEST_IDS.views.searchSiteView.view, "has-reached-max-intervention-limit-warning"),
      searchSiteTabs: (): string => joinDataTestId(DATA_TEST_IDS.views.searchSiteView.view, "search-site-tabs"),
    },
    siteDetailsOffersEligibilityView: {
      view: "site-details-offers-eligibility-view" as const,
      offerDetailsConditionsCard: (): string =>
        joinDataTestId(DATA_TEST_IDS.views.siteDetailsOffersEligibilityView.view, "offer-details-conditions-card"),
      offersEligibilityCard: (): string =>
        joinDataTestId(DATA_TEST_IDS.views.siteDetailsOffersEligibilityView.view, "offers-eligibility-card"),
    },
    siteWorkingConditionModulationView: {
      view: "site-working-condition-modulation-view" as const,
    },
    siteWorkingConditionAcquisitionView: {
      view: "site-working-condition-acquisition-view" as const,
      gatewayRequestHistoryCard: (): string =>
        joinDataTestId(DATA_TEST_IDS.views.siteWorkingConditionAcquisitionView.view, "gateway-request-history-card"),
    },
    siteDetailsAppointmentView: {
      view: "site-details-appointment-view" as const,
      eligibleApplianceCountCard: (): string =>
        joinDataTestId(DATA_TEST_IDS.views.siteDetailsAppointmentView.view, "eligible-appliance-count-card"),
      applianceLeftToInstallCountCard: (): string =>
        joinDataTestId(DATA_TEST_IDS.views.siteDetailsAppointmentView.view, "appliance-left-to-install-count-card"),
      applianceLeftToInstallCountCardRadiatorsNumber: (): string =>
        joinDataTestId("appliance-left-to-install-count-card", "radiators-number"),
      applianceAlreadyInstalledCountCard: (): string =>
        joinDataTestId(DATA_TEST_IDS.views.siteDetailsAppointmentView.view, "appliance-already-installed-count-card"),
      applianceAlreadyInstalledCountCardApplianceNumber: (): string =>
        joinDataTestId("appliance-already-installed-count-card", "appliance-number"),
      appointmentBookingFormCardToUpdate: (): string =>
        joinDataTestId(DATA_TEST_IDS.views.siteDetailsAppointmentView.view, "appointment-booking-form-card-to-update"),
      appointmentBookingFormCardNoUpdate: (): string =>
        joinDataTestId(DATA_TEST_IDS.views.siteDetailsAppointmentView.view, "appointment-booking-form-card-no-update"),
      appointmentHistoryCard: (): string =>
        joinDataTestId(DATA_TEST_IDS.views.siteDetailsAppointmentView.view, "appointment-history-card"),
    },
    siteDetailsSiteEditView: {
      view: "site-details-site-edit-view" as const,
      card: (): string => joinDataTestId(DATA_TEST_IDS.views.siteDetailsSiteEditView.view, "card"),
      cardHeader: (): string => joinDataTestId(DATA_TEST_IDS.views.siteDetailsSiteEditView.view, "card-header"),
      cardContent: (): string => joinDataTestId(DATA_TEST_IDS.views.siteDetailsSiteEditView.view, "card-content"),
      createSiteForm: (): string => joinDataTestId(DATA_TEST_IDS.views.siteDetailsSiteEditView.view, "form"),
    },
    siteDetailsOperationView: {
      view: "site-details-operation-view" as const,
    },
    siteDetailsDtdView: {
      view: "site-details-dtd-view" as const,
      dtdCard: (): string => joinDataTestId(DATA_TEST_IDS.views.siteDetailsDtdView.view, "dtd-card"),
    },
    siteDetailsDocumentsView: {
      view: "site-details-documents-view" as const,
      siteDocument: (): string => joinDataTestId(DATA_TEST_IDS.views.siteDetailsDocumentsView.view, "site-document"),
    },
    siteDetailsSensorsView: {
      view: "site-details-sensors-view" as const,
      activeInSideCountCard: (): string =>
        joinDataTestId(DATA_TEST_IDS.views.siteDetailsSensorsView.view, "active-in-side-count-card"),
      sentByCustomerServiceCountCard: (): string =>
        joinDataTestId(DATA_TEST_IDS.views.siteDetailsSensorsView.view, "sent-by-customer-service-count-card"),
      linkedToDeviceCountCard: (): string =>
        joinDataTestId(DATA_TEST_IDS.views.siteDetailsSensorsView.view, "linked-to-device-count-card"),
      sensorRequestFormCard: (): string =>
        joinDataTestId(DATA_TEST_IDS.views.siteDetailsSensorsView.view, "sensor-request-form-card"),
      sensorRequestHistoryCard: (): string =>
        joinDataTestId(DATA_TEST_IDS.views.siteDetailsSensorsView.view, "sensor-request-history-card"),
    },
    siteDetailsSubscriberAttachView: {
      view: "site-details-subscriber-attach-view" as const,
      tabView: (): string => joinDataTestId(DATA_TEST_IDS.views.siteDetailsSubscriberAttachView.view, "tab-view"),
      createPanel: (): string =>
        joinDataTestId(DATA_TEST_IDS.views.siteDetailsSubscriberAttachView.view, "create-panel"),
      attachPanel: (): string =>
        joinDataTestId(DATA_TEST_IDS.views.siteDetailsSubscriberAttachView.view, "attach-panel"),
      advancedSubscriberSearch: (): string =>
        joinDataTestId(DATA_TEST_IDS.views.siteDetailsSubscriberAttachView.view, "advanced-subscriber-search"),
      form: (): string => joinDataTestId(DATA_TEST_IDS.views.siteDetailsSubscriberAttachView.view, "form"),
      card: (): string => joinDataTestId(DATA_TEST_IDS.views.siteDetailsSubscriberAttachView.view, "card"),
      cardHeader: (): string => joinDataTestId(DATA_TEST_IDS.views.siteDetailsSubscriberAttachView.view, "card-header"),
      cardTitle: (): string => joinDataTestId(DATA_TEST_IDS.views.siteDetailsSubscriberAttachView.view, "card-title"),
      cardContent: (): string =>
        joinDataTestId(DATA_TEST_IDS.views.siteDetailsSubscriberAttachView.view, "card-content"),
    },
    siteDetailsSubscriberEditView: {
      view: "site-details-subscriber-edit-view" as const,
      skeleton: (): string => joinDataTestId(DATA_TEST_IDS.views.siteDetailsSubscriberEditView.view, "skeleton"),
    },
    siteDetailsComfortView: {
      view: "site-details-comfort-view" as const,
      comfortFormCard: (): string =>
        joinDataTestId(DATA_TEST_IDS.views.siteDetailsComfortView.view, "comfort-form-card"),
      comfortHistoryCard: (): string =>
        joinDataTestId(DATA_TEST_IDS.views.siteDetailsComfortView.view, "comfort-history-card"),
    },
    siteDetailsSubscriberFeedbackView: {
      view: "site-details-subscriber-feedback-view" as const,
      subscriberFeedbackCard: (): string =>
        joinDataTestId(DATA_TEST_IDS.views.siteDetailsSubscriberFeedbackView.view, "subscriber-feedback-card"),
    },
    siteDetailsLivevxView: {
      view: "site-details-livevx-view" as const,
      liveVxCard: (): string => joinDataTestId(DATA_TEST_IDS.views.siteDetailsLivevxView.view, "livevx-card"),
      autoDiagCard: (): string => joinDataTestId(DATA_TEST_IDS.views.siteDetailsLivevxView.view, "autodia-card"),
    },
    siteDetailsCloseCallView: {
      view: "site-details-close-call-view" as const,
      card: (): string => joinDataTestId(DATA_TEST_IDS.views.siteDetailsCloseCallView.view, "card"),
      cardTitle: (): string => joinDataTestId(DATA_TEST_IDS.views.siteDetailsCloseCallView.view, "card-title"),
      cardHeader: (): string => joinDataTestId(DATA_TEST_IDS.views.siteDetailsCloseCallView.view, "card-header"),
      cardContent: (): string => joinDataTestId(DATA_TEST_IDS.views.siteDetailsCloseCallView.view, "card-content"),
      form: (): string => joinDataTestId(DATA_TEST_IDS.views.siteDetailsCloseCallView.view, "form"),
    },
    organizationSupportView: {
      view: "organization-support-view" as const,
      card: (): string => joinDataTestId(DATA_TEST_IDS.views.organizationSupportView.view, "card"),
    },
    organizationOverviewView: {
      view: "organization-overview-view" as const,
      hospitalityAccessBtn: (): string =>
        joinDataTestId(DATA_TEST_IDS.views.organizationOverviewView.view, "hospitality-access-btn"),
      sitesTable: (): string => joinDataTestId(DATA_TEST_IDS.views.organizationOverviewView.view, "sites-table"),
    },
    organizationDetailsBaseRoute: {
      view: "organization-details-base-route" as const,
    },
    organizationPilotOversightView: {
      view: "organization-pilot-oversight-view" as const,
      card: (): string => joinDataTestId(DATA_TEST_IDS.views.organizationPilotOversightView.view, "card"),
    },
    searchAccommodationSiteView: {
      view: "search-accommodation-site-view" as const,
      table: (): string => joinDataTestId(DATA_TEST_IDS.views.searchAccommodationSiteView.view, "table"),
    },
  },

  layouts: {
    siteDetailsLayout: {
      layout: "site-details-layout" as const,
      pageTitle: (): string => joinDataTestId(DATA_TEST_IDS.layouts.siteDetailsLayout.layout, "page-title"),
    },
  },

  components: {
    confirmDialog: {
      component: "confirm-dialog",
      acceptButton: "confirm-dialog-accept-button",
      rejectButton: "confirm-dialog-reject-button",
    },
    toast: {
      component: "toast",
      message: (): string => joinDataTestId(DATA_TEST_IDS.components.toast.component, "message"),
    },
    subscriberFeedbackAbuseReportForm: {
      component: "subscriber-feedback-abuse-report-form",
      form: (): string => joinDataTestId(DATA_TEST_IDS.components.subscriberFeedbackAbuseReportForm.component, "form"),
      abusiveReportTypeLabel: (): string =>
        joinDataTestId(
          DATA_TEST_IDS.components.subscriberFeedbackAbuseReportForm.component,
          "abusive-report-type-label",
        ),
      abusiveReportTypeDropdown: (): string =>
        joinDataTestId(
          DATA_TEST_IDS.components.subscriberFeedbackAbuseReportForm.component,
          "abusive-report-type-dropdown",
        ),
      appointmentLabel: (): string =>
        joinDataTestId(DATA_TEST_IDS.components.subscriberFeedbackAbuseReportForm.component, "appointment-label"),
      appointmentDropdown: (): string =>
        joinDataTestId(DATA_TEST_IDS.components.subscriberFeedbackAbuseReportForm.component, "appointment-dropdown"),
      reasonLabel: (): string =>
        joinDataTestId(DATA_TEST_IDS.components.subscriberFeedbackAbuseReportForm.component, "reason-label"),
      reasonDropdown: (): string =>
        joinDataTestId(DATA_TEST_IDS.components.subscriberFeedbackAbuseReportForm.component, "reason-dropdown"),
      commentLabel: (): string =>
        joinDataTestId(DATA_TEST_IDS.components.subscriberFeedbackAbuseReportForm.component, "comment-label"),
      commentTextarea: (): string =>
        joinDataTestId(DATA_TEST_IDS.components.subscriberFeedbackAbuseReportForm.component, "comment-textarea"),
      submitButton: (): string =>
        joinDataTestId(DATA_TEST_IDS.components.subscriberFeedbackAbuseReportForm.component, "submit-button"),
      fieldError: (name: string): string =>
        joinDataTestId(DATA_TEST_IDS.components.subscriberFeedbackAbuseReportForm.component, "field-error", name),
    },
    subscriberFeedbackSatisfactionSurveyForm: {
      component: "subscriber-feedback-satisfaction-survey-form",
      form: (): string =>
        joinDataTestId(DATA_TEST_IDS.components.subscriberFeedbackSatisfactionSurveyForm.component, "form"),
      interventionLabel: (): string =>
        joinDataTestId(
          DATA_TEST_IDS.components.subscriberFeedbackSatisfactionSurveyForm.component,
          "intervention-label",
        ),
      interventionDropdown: (): string =>
        joinDataTestId(
          DATA_TEST_IDS.components.subscriberFeedbackSatisfactionSurveyForm.component,
          "intervention-dropdown",
        ),
      interventionError: (): string =>
        joinDataTestId(
          DATA_TEST_IDS.components.subscriberFeedbackSatisfactionSurveyForm.component,
          "intervention-error",
        ),
      ratingsTable: (): string =>
        joinDataTestId(DATA_TEST_IDS.components.subscriberFeedbackSatisfactionSurveyForm.component, "ratings-table"),
      ratingRadio: (criterion: string, value: number | string): string =>
        joinDataTestId(
          DATA_TEST_IDS.components.subscriberFeedbackSatisfactionSurveyForm.component,
          "rating-radio",
          criterion,
          String(value),
        ),
      promptnessError: (): string =>
        joinDataTestId(DATA_TEST_IDS.components.subscriberFeedbackSatisfactionSurveyForm.component, "promptness-error"),
      appliancesInstalled: (value: EInstallSatisfactionApplianceInstalledDto): string =>
        joinDataTestId(
          DATA_TEST_IDS.components.subscriberFeedbackSatisfactionSurveyForm.component,
          "appliances-installed",
          value,
        ),
      commentLabel: (): string =>
        joinDataTestId(DATA_TEST_IDS.components.subscriberFeedbackSatisfactionSurveyForm.component, "comment-label"),
      commentTextarea: (): string =>
        joinDataTestId(DATA_TEST_IDS.components.subscriberFeedbackSatisfactionSurveyForm.component, "comment-textarea"),
      submitButton: (): string =>
        joinDataTestId(DATA_TEST_IDS.components.subscriberFeedbackSatisfactionSurveyForm.component, "submit-button"),
      fieldError: (name: string): string =>
        joinDataTestId(
          DATA_TEST_IDS.components.subscriberFeedbackSatisfactionSurveyForm.component,
          "field-error",
          name,
        ),
    },
    attachSubscriberCard: {
      component: "attach-subscriber-card",
      card: (): string => joinDataTestId(DATA_TEST_IDS.components.attachSubscriberCard.component, "card"),
      headerTitle: (): string =>
        joinDataTestId(DATA_TEST_IDS.components.attachSubscriberCard.component, "header-title"),
      body: (): string => joinDataTestId(DATA_TEST_IDS.components.attachSubscriberCard.component, "body"),
      title: (): string => joinDataTestId(DATA_TEST_IDS.components.attachSubscriberCard.component, "title"),
      description: (): string => joinDataTestId(DATA_TEST_IDS.components.attachSubscriberCard.component, "description"),
      ctaButton: (): string => joinDataTestId(DATA_TEST_IDS.components.attachSubscriberCard.component, "cta-button"),
    },
    subscriberResetPasswordButton: {
      component: "subscriber-reset-password-button",
      missingMailText: (): string =>
        joinDataTestId(DATA_TEST_IDS.components.subscriberResetPasswordButton.component, "missing-mail-text"),
      button: (): string => joinDataTestId(DATA_TEST_IDS.components.subscriberResetPasswordButton.component, "button"),
    },
    subscriberAgreementButton: {
      component: "subscriber-agreement-button",
      missingPdlText: (): string =>
        joinDataTestId(DATA_TEST_IDS.components.subscriberAgreementButton.component, "missing-pdl-text"),
      missingMailText: (): string =>
        joinDataTestId(DATA_TEST_IDS.components.subscriberAgreementButton.component, "missing-mail-text"),
      button: (): string => joinDataTestId(DATA_TEST_IDS.components.subscriberAgreementButton.component, "button"),
    },
    subscriberTransferSignatureButton: {
      component: "subscriber-transfer-signature-button",
      loadingText: (): string =>
        joinDataTestId(DATA_TEST_IDS.components.subscriberTransferSignatureButton.component, "loading-text"),
      errorText: (): string =>
        joinDataTestId(DATA_TEST_IDS.components.subscriberTransferSignatureButton.component, "error-text"),
      missingDataText: (): string =>
        joinDataTestId(DATA_TEST_IDS.components.subscriberTransferSignatureButton.component, "missing-data-text"),
      pendingSignatureText: (): string =>
        joinDataTestId(DATA_TEST_IDS.components.subscriberTransferSignatureButton.component, "pending-signature-text"),
      button: (): string =>
        joinDataTestId(DATA_TEST_IDS.components.subscriberTransferSignatureButton.component, "button"),
      openButton: (): string =>
        joinDataTestId(DATA_TEST_IDS.components.subscriberTransferSignatureButton.component, "open-button"),
      cancelOngoingButton: (): string =>
        joinDataTestId(DATA_TEST_IDS.components.subscriberTransferSignatureButton.component, "cancel-ongoing-button"),
      dialog: (): string =>
        joinDataTestId(DATA_TEST_IDS.components.subscriberTransferSignatureButton.component, "dialog"),
      dialogTitle: (): string =>
        joinDataTestId(DATA_TEST_IDS.components.subscriberTransferSignatureButton.component, "dialog-title"),
      dialogDetails: (): string =>
        joinDataTestId(DATA_TEST_IDS.components.subscriberTransferSignatureButton.component, "dialog-details"),
      upgradeToPremiumCheckbox: (): string =>
        joinDataTestId(
          DATA_TEST_IDS.components.subscriberTransferSignatureButton.component,
          "upgrade-to-premium-checkbox",
        ),
      energyOffersCheckbox: (): string =>
        joinDataTestId(DATA_TEST_IDS.components.subscriberTransferSignatureButton.component, "energy-offers-checkbox"),
      partnerOffersCheckbox: (): string =>
        joinDataTestId(DATA_TEST_IDS.components.subscriberTransferSignatureButton.component, "partner-offers-checkbox"),
      cancelButton: (): string =>
        joinDataTestId(DATA_TEST_IDS.components.subscriberTransferSignatureButton.component, "cancel-button"),
      confirmButton: (): string =>
        joinDataTestId(DATA_TEST_IDS.components.subscriberTransferSignatureButton.component, "confirm-button"),
    },
    subscriberPowerTypeDropdown: {
      component: "subscriber-power-type-dropdown",
      dropdown: (): string =>
        joinDataTestId(DATA_TEST_IDS.components.subscriberPowerTypeDropdown.component, "dropdown"),
    },
    subscriberEditFormCard: {
      component: "subscriber-edit-form-card",
      card: (): string => joinDataTestId(DATA_TEST_IDS.components.subscriberEditFormCard.component, "card"),
      header: (): string => joinDataTestId(DATA_TEST_IDS.components.subscriberEditFormCard.component, "header"),
      headerTitle: (): string => joinDataTestId(DATA_TEST_IDS.components.subscriberEditFormCard.component, "title"),
      content: (): string => joinDataTestId(DATA_TEST_IDS.components.subscriberEditFormCard.component, "content"),
      form: (): string => joinDataTestId(DATA_TEST_IDS.components.subscriberEditFormCard.component, "form"),
      civilityLabel: (): string =>
        joinDataTestId(DATA_TEST_IDS.components.subscriberEditFormCard.component, "civility-label"),
      civilityDropdown: (): string =>
        joinDataTestId(DATA_TEST_IDS.components.subscriberEditFormCard.component, "civility-dropdown"),
      firstnameLabel: (): string =>
        joinDataTestId(DATA_TEST_IDS.components.subscriberEditFormCard.component, "firstname-label"),
      firstnameInput: (): string =>
        joinDataTestId(DATA_TEST_IDS.components.subscriberEditFormCard.component, "firstname-input"),
      lastnameLabel: (): string =>
        joinDataTestId(DATA_TEST_IDS.components.subscriberEditFormCard.component, "lastname-label"),
      lastnameInput: (): string =>
        joinDataTestId(DATA_TEST_IDS.components.subscriberEditFormCard.component, "lastname-input"),
      defaultMailLabel: (): string =>
        joinDataTestId(DATA_TEST_IDS.components.subscriberEditFormCard.component, "default-mail-label"),
      defaultMailInput: (): string =>
        joinDataTestId(DATA_TEST_IDS.components.subscriberEditFormCard.component, "default-mail-input"),
      addMailButton: (): string =>
        joinDataTestId(DATA_TEST_IDS.components.subscriberEditFormCard.component, "add-mail-button"),
      addPhoneButton: (): string =>
        joinDataTestId(DATA_TEST_IDS.components.subscriberEditFormCard.component, "add-phone-button"),
      additionalMailsLabel: (): string =>
        joinDataTestId(DATA_TEST_IDS.components.subscriberEditFormCard.component, "additional-mails-label"),
      additionalMailInput: (index: number | string): string =>
        joinDataTestId(
          DATA_TEST_IDS.components.subscriberEditFormCard.component,
          "additional-mail-input",
          String(index),
        ),
      removeMailBtn: (index: number | string): string =>
        joinDataTestId(DATA_TEST_IDS.components.subscriberEditFormCard.component, "remove-mail-btn", String(index)),
      removePhoneBtn: (index: number | string): string =>
        joinDataTestId(DATA_TEST_IDS.components.subscriberEditFormCard.component, "remove-phone-btn", String(index)),
      additionalMailError: (index: number | string): string =>
        joinDataTestId(
          DATA_TEST_IDS.components.subscriberEditFormCard.component,
          "additional-mail-error",
          String(index),
        ),
      defaultPhoneLabel: (): string =>
        joinDataTestId(DATA_TEST_IDS.components.subscriberEditFormCard.component, "default-phone-label"),
      defaultPhoneInput: (): string =>
        joinDataTestId(DATA_TEST_IDS.components.subscriberEditFormCard.component, "default-phone-input"),
      additionalPhonesLabel: (): string =>
        joinDataTestId(DATA_TEST_IDS.components.subscriberEditFormCard.component, "additional-phones-label"),
      additionalPhoneInput: (index: number | string): string =>
        joinDataTestId(
          DATA_TEST_IDS.components.subscriberEditFormCard.component,
          "additional-phone-input",
          String(index),
        ),
      additionalPhoneError: (index: number | string): string =>
        joinDataTestId(
          DATA_TEST_IDS.components.subscriberEditFormCard.component,
          "additional-phone-error",
          String(index),
        ),
      subscriptionLabel: (): string =>
        joinDataTestId(DATA_TEST_IDS.components.subscriberEditFormCard.component, "subscription-label"),
      radioPremium: (): string =>
        joinDataTestId(DATA_TEST_IDS.components.subscriberEditFormCard.component, "radio-premium"),
      radioPremiumPendingSignature: (): string =>
        joinDataTestId(DATA_TEST_IDS.components.subscriberEditFormCard.component, "radio-premium-pending-signature"),
      radioBasic: (): string =>
        joinDataTestId(DATA_TEST_IDS.components.subscriberEditFormCard.component, "radio-basic"),
      cancelButton: (): string =>
        joinDataTestId(DATA_TEST_IDS.components.subscriberEditFormCard.component, "cancel-button"),
      cancelLink: (): string =>
        joinDataTestId(DATA_TEST_IDS.components.subscriberEditFormCard.component, "cancel-link"),
      submitButton: (): string =>
        joinDataTestId(DATA_TEST_IDS.components.subscriberEditFormCard.component, "submit-button"),
      fieldError: (name: string): string =>
        joinDataTestId(DATA_TEST_IDS.components.subscriberEditFormCard.component, "field-error", name),
    },
    planningDayRow: {
      component: "planning-day-row",
      row: (day: number | string): string =>
        joinDataTestId(DATA_TEST_IDS.components.planningDayRow.component, "row", String(day)),
      dayLabel: (day: number | string): string =>
        joinDataTestId(DATA_TEST_IDS.components.planningDayRow.component, "day-label", String(day)),
      barContainer: (day: number | string): string =>
        joinDataTestId(DATA_TEST_IDS.components.planningDayRow.component, "bar-container", String(day)),
      scheduleItem: (day: number | string, index: number | string): string =>
        joinDataTestId(DATA_TEST_IDS.components.planningDayRow.component, "schedule-item", String(day), String(index)),
      tooltip: (day: number | string, index: number | string): string =>
        joinDataTestId(DATA_TEST_IDS.components.planningDayRow.component, "tooltip", String(day), String(index)),
      tooltipTitle: (day: number | string, index: number | string): string =>
        joinDataTestId(DATA_TEST_IDS.components.planningDayRow.component, "tooltip-title", String(day), String(index)),
      tooltipTime: (day: number | string, index: number | string): string =>
        joinDataTestId(DATA_TEST_IDS.components.planningDayRow.component, "tooltip-time", String(day), String(index)),
    },
    subscriberEditOfferUpdateModal: {
      component: "subscriber-edit-offer-update-modal",
      dialog: (): string => joinDataTestId(DATA_TEST_IDS.components.subscriberEditOfferUpdateModal.component, "dialog"),
      headerTitle: (): string =>
        joinDataTestId(DATA_TEST_IDS.components.subscriberEditOfferUpdateModal.component, "header-title"),
      content: (): string =>
        joinDataTestId(DATA_TEST_IDS.components.subscriberEditOfferUpdateModal.component, "content"),
      list: (): string => joinDataTestId(DATA_TEST_IDS.components.subscriberEditOfferUpdateModal.component, "list"),
      featureItem: (index: number | string): string =>
        joinDataTestId(
          DATA_TEST_IDS.components.subscriberEditOfferUpdateModal.component,
          "feature-item",
          String(index),
        ),
      confirmButton: (): string =>
        joinDataTestId(DATA_TEST_IDS.components.subscriberEditOfferUpdateModal.component, "confirm-button"),
      premiumServiceButton: (): string =>
        joinDataTestId(DATA_TEST_IDS.components.subscriberEditOfferUpdateModal.component, "premium-service-button"),
    },
    subscriberMyvoltalisPlanning: {
      component: "subscriber-myvoltalis-planning",
      card: (): string => joinDataTestId(DATA_TEST_IDS.components.subscriberMyvoltalisPlanning.component, "card"),
      header: (): string => joinDataTestId(DATA_TEST_IDS.components.subscriberMyvoltalisPlanning.component, "header"),
      title: (): string => joinDataTestId(DATA_TEST_IDS.components.subscriberMyvoltalisPlanning.component, "title"),
      planningDropdown: (): string =>
        joinDataTestId(DATA_TEST_IDS.components.subscriberMyvoltalisPlanning.component, "planning-dropdown"),
      content: (): string => joinDataTestId(DATA_TEST_IDS.components.subscriberMyvoltalisPlanning.component, "content"),
      table: (): string => joinDataTestId(DATA_TEST_IDS.components.subscriberMyvoltalisPlanning.component, "table"),
      headerRow: (): string =>
        joinDataTestId(DATA_TEST_IDS.components.subscriberMyvoltalisPlanning.component, "header-row"),
      emptyState: (): string =>
        joinDataTestId(DATA_TEST_IDS.components.subscriberMyvoltalisPlanning.component, "empty-state"),
      emptyTitle: (): string =>
        joinDataTestId(DATA_TEST_IDS.components.subscriberMyvoltalisPlanning.component, "empty-title"),
      emptyDesc: (): string =>
        joinDataTestId(DATA_TEST_IDS.components.subscriberMyvoltalisPlanning.component, "empty-desc"),
    },
    subscriberPsbButton: {
      component: "subscriber-psb-button",
      setButton: (): string => joinDataTestId(DATA_TEST_IDS.components.subscriberPsbButton.component, "set-button"),
      unsetButton: (): string => joinDataTestId(DATA_TEST_IDS.components.subscriberPsbButton.component, "unset-button"),
    },
    subscriberVipButton: {
      component: "subscriber-vip-button",
      setButton: (): string => joinDataTestId(DATA_TEST_IDS.components.subscriberVipButton.component, "set-button"),
      unsetButton: (): string => joinDataTestId(DATA_TEST_IDS.components.subscriberVipButton.component, "unset-button"),
    },
    subscriberDetailsCard: {
      component: "subscriber-details-card",
      card: (): string => joinDataTestId(DATA_TEST_IDS.components.subscriberDetailsCard.component, "card"),
      header: (): string => joinDataTestId(DATA_TEST_IDS.components.subscriberDetailsCard.component, "header"),
      title: (): string => joinDataTestId(DATA_TEST_IDS.components.subscriberDetailsCard.component, "title"),
      editBtn: (): string => joinDataTestId(DATA_TEST_IDS.components.subscriberDetailsCard.component, "edit-btn"),
      content: (): string => joinDataTestId(DATA_TEST_IDS.components.subscriberDetailsCard.component, "content"),
      idTitle: (): string => joinDataTestId(DATA_TEST_IDS.components.subscriberDetailsCard.component, "id-title"),
      idValue: (): string => joinDataTestId(DATA_TEST_IDS.components.subscriberDetailsCard.component, "id-value"),
      fullnameTitle: (): string =>
        joinDataTestId(DATA_TEST_IDS.components.subscriberDetailsCard.component, "fullname-title"),
      fullnameValue: (): string =>
        joinDataTestId(DATA_TEST_IDS.components.subscriberDetailsCard.component, "fullname-value"),
      emailTitle: (): string => joinDataTestId(DATA_TEST_IDS.components.subscriberDetailsCard.component, "email-title"),
      emailItem: (index: number | string): string =>
        joinDataTestId(DATA_TEST_IDS.components.subscriberDetailsCard.component, "email-item", String(index)),
      phoneTitle: (): string => joinDataTestId(DATA_TEST_IDS.components.subscriberDetailsCard.component, "phone-title"),
      phoneItem: (index: number | string): string =>
        joinDataTestId(DATA_TEST_IDS.components.subscriberDetailsCard.component, "phone-item", String(index)),
      passwordTitle: (): string =>
        joinDataTestId(DATA_TEST_IDS.components.subscriberDetailsCard.component, "password-title"),
      agreementTitle: (): string =>
        joinDataTestId(DATA_TEST_IDS.components.subscriberDetailsCard.component, "agreement-title"),
      psbTitle: (): string => joinDataTestId(DATA_TEST_IDS.components.subscriberDetailsCard.component, "psb-title"),
      vipTitle: (): string => joinDataTestId(DATA_TEST_IDS.components.subscriberDetailsCard.component, "vip-title"),
      subscriptionTitle: (): string =>
        joinDataTestId(DATA_TEST_IDS.components.subscriberDetailsCard.component, "subscription-title"),
      subscriptionValue: (): string =>
        joinDataTestId(DATA_TEST_IDS.components.subscriberDetailsCard.component, "subscription-value"),
      badgesRow: (): string => joinDataTestId(DATA_TEST_IDS.components.subscriberDetailsCard.component, "badges-row"),
    },
    rotationFormCard: {
      component: "rotation-form-card",
      card: (): string => joinDataTestId(DATA_TEST_IDS.components.rotationFormCard.component, "card"),
      headerTitle: (): string => joinDataTestId(DATA_TEST_IDS.components.rotationFormCard.component, "header-title"),
      detachSubscriberSwitch: (): string =>
        joinDataTestId(DATA_TEST_IDS.components.rotationFormCard.component, "detach-subscriber-switch"),
      detachSubscriberError: (): string =>
        joinDataTestId(DATA_TEST_IDS.components.rotationFormCard.component, "detach-subscriber-error"),
      content: (): string => joinDataTestId(DATA_TEST_IDS.components.rotationFormCard.component, "content"),
      plannedAppointmentMessage: (): string =>
        joinDataTestId(DATA_TEST_IDS.components.rotationFormCard.component, "planned-appointment-message"),
      deleteAppointmentButton: (): string =>
        joinDataTestId(DATA_TEST_IDS.components.rotationFormCard.component, "delete-appointment-button"),
      form: (): string => joinDataTestId(DATA_TEST_IDS.components.rotationFormCard.component, "form"),
      commentLabel: (): string => joinDataTestId(DATA_TEST_IDS.components.rotationFormCard.component, "comment-label"),
      commentTextarea: (): string =>
        joinDataTestId(DATA_TEST_IDS.components.rotationFormCard.component, "comment-textarea"),
      confirmRotationButton: (): string =>
        joinDataTestId(DATA_TEST_IDS.components.rotationFormCard.component, "confirm-rotation-button"),
      detachSubscriberLabel: (): string =>
        joinDataTestId(DATA_TEST_IDS.components.rotationFormCard.component, "detach-subscriber-label"),
      submitButton: (): string => joinDataTestId(DATA_TEST_IDS.components.rotationFormCard.component, "submit-button"),
      fieldError: (name: string): string =>
        joinDataTestId(DATA_TEST_IDS.components.rotationFormCard.component, "field-error", name),
    },
    uploadPhotosModal: {
      component: "upload-photos-modal",
      dialog: (): string => joinDataTestId(DATA_TEST_IDS.components.uploadPhotosModal.component, "dialog"),
      form: (): string => joinDataTestId(DATA_TEST_IDS.components.uploadPhotosModal.component, "form"),
      typeLabel: (): string => joinDataTestId(DATA_TEST_IDS.components.uploadPhotosModal.component, "type-label"),
      typeDropdown: (): string => joinDataTestId(DATA_TEST_IDS.components.uploadPhotosModal.component, "type-dropdown"),
      filesDropzone: (): string =>
        joinDataTestId(DATA_TEST_IDS.components.uploadPhotosModal.component, "files-dropzone"),
      uploadButton: (): string => joinDataTestId(DATA_TEST_IDS.components.uploadPhotosModal.component, "upload-button"),
      fieldError: (name: string): string =>
        joinDataTestId(DATA_TEST_IDS.components.uploadPhotosModal.component, "field-error", name),
    },
    updateOcrStatusDialog: {
      component: "update-ocr-status-dialog",
      dialog: (): string => joinDataTestId(DATA_TEST_IDS.components.updateOcrStatusDialog.component, "dialog"),
      currentStatusLabel: (): string =>
        joinDataTestId(DATA_TEST_IDS.components.updateOcrStatusDialog.component, "current-status-label"),
      currentStatusValue: (): string =>
        joinDataTestId(DATA_TEST_IDS.components.updateOcrStatusDialog.component, "current-status-value"),
      newStatusLabel: (): string =>
        joinDataTestId(DATA_TEST_IDS.components.updateOcrStatusDialog.component, "new-status-label"),
      newStatusDropdown: (): string =>
        joinDataTestId(DATA_TEST_IDS.components.updateOcrStatusDialog.component, "new-status-dropdown"),
      cancelButton: (): string =>
        joinDataTestId(DATA_TEST_IDS.components.updateOcrStatusDialog.component, "cancel-button"),
      saveButton: (): string => joinDataTestId(DATA_TEST_IDS.components.updateOcrStatusDialog.component, "save-button"),
    },
    sitePhotoImg: {
      component: "site-photo-img",
      container: (): string => joinDataTestId(DATA_TEST_IDS.components.sitePhotoImg.component, "container"),
      imageContainer: (): string => joinDataTestId(DATA_TEST_IDS.components.sitePhotoImg.component, "image-container"),
      loadingSkeleton: (): string =>
        joinDataTestId(DATA_TEST_IDS.components.sitePhotoImg.component, "loading-skeleton"),
      errorContainer: (): string => joinDataTestId(DATA_TEST_IDS.components.sitePhotoImg.component, "error-container"),
      noUrlSkeleton: (): string => joinDataTestId(DATA_TEST_IDS.components.sitePhotoImg.component, "no-url-skeleton"),
      image: (): string => joinDataTestId(DATA_TEST_IDS.components.sitePhotoImg.component, "image"),
      pdfContainer: (): string => joinDataTestId(DATA_TEST_IDS.components.sitePhotoImg.component, "pdf-container"),
      pdfIcon: (): string => joinDataTestId(DATA_TEST_IDS.components.sitePhotoImg.component, "pdf-icon"),
      dateText: (): string => joinDataTestId(DATA_TEST_IDS.components.sitePhotoImg.component, "date-text"),
      personText: (): string => joinDataTestId(DATA_TEST_IDS.components.sitePhotoImg.component, "person-text"),
      typeAndLocalisationText: (): string =>
        joinDataTestId(DATA_TEST_IDS.components.sitePhotoImg.component, "type-and-localisation-text"),
      typeText: (): string => joinDataTestId(DATA_TEST_IDS.components.sitePhotoImg.component, "type-text"),
      localisationText: (): string =>
        joinDataTestId(DATA_TEST_IDS.components.sitePhotoImg.component, "localisation-text"),
      modulatorText: (): string => joinDataTestId(DATA_TEST_IDS.components.sitePhotoImg.component, "modulator-text"),
      ocrResultStatus: (): string =>
        joinDataTestId(DATA_TEST_IDS.components.sitePhotoImg.component, "ocr-result-status"),
      geolocationText: (): string =>
        joinDataTestId(DATA_TEST_IDS.components.sitePhotoImg.component, "geolocation-text"),
      openstreetmapBtn: (): string =>
        joinDataTestId(DATA_TEST_IDS.components.sitePhotoImg.component, "openstreetmap-btn"),
    },
    offersEligibilityCard: {
      component: "offers-eligibility-card",
      card: (): string => joinDataTestId(DATA_TEST_IDS.components.offersEligibilityCard.component, "card"),
      cardTitle: (): string => joinDataTestId(DATA_TEST_IDS.components.offersEligibilityCard.component, "card-title"),
      cardHeader: (): string => joinDataTestId(DATA_TEST_IDS.components.offersEligibilityCard.component, "card-header"),
      content: (): string => joinDataTestId(DATA_TEST_IDS.components.offersEligibilityCard.component, "content"),
      offerDropdown: (): string =>
        joinDataTestId(DATA_TEST_IDS.components.offersEligibilityCard.component, "offer-dropdown"),
    },
    offerDetailsConditionsCard: {
      component: "offer-details-conditions-card",
      card: (): string => joinDataTestId(DATA_TEST_IDS.components.offerDetailsConditionsCard.component, "card"),
      cardTitle: (): string =>
        joinDataTestId(DATA_TEST_IDS.components.offerDetailsConditionsCard.component, "header-title"),
      cardHeader: (): string =>
        joinDataTestId(DATA_TEST_IDS.components.offerDetailsConditionsCard.component, "card-header"),
      content: (): string => joinDataTestId(DATA_TEST_IDS.components.offerDetailsConditionsCard.component, "content"),
      table: (): string => joinDataTestId(DATA_TEST_IDS.components.offerDetailsConditionsCard.component, "table"),
      tableHeader: (): string =>
        joinDataTestId(DATA_TEST_IDS.components.offerDetailsConditionsCard.component, "table-header"),
      tableHeaderTitle: (): string =>
        joinDataTestId(DATA_TEST_IDS.components.offerDetailsConditionsCard.component, "table-header-title"),
      row: (index: number | string): string =>
        joinDataTestId(DATA_TEST_IDS.components.offerDetailsConditionsCard.component, "row", String(index)),
      rowCell: (column: string): string =>
        joinDataTestId(DATA_TEST_IDS.components.offerDetailsConditionsCard.component, "row-cell", column),
    },
    zendeskStatus: {
      component: "zendesk-status",
      container: (): string => joinDataTestId(DATA_TEST_IDS.components.zendeskStatus.component, "container"),
      colorDot: (): string => joinDataTestId(DATA_TEST_IDS.components.zendeskStatus.component, "color-dot"),
      shortLetter: (): string => joinDataTestId(DATA_TEST_IDS.components.zendeskStatus.component, "short-letter"),
      label: (): string => joinDataTestId(DATA_TEST_IDS.components.zendeskStatus.component, "label"),
    },
    refTownInfoAutocomplete: {
      component: "ref-town-info-autocomplete",
      input: (): string => joinDataTestId(DATA_TEST_IDS.components.refTownInfoAutocomplete.component, "input"),
      row: (index: number | string): string =>
        joinDataTestId(DATA_TEST_IDS.components.refTownInfoAutocomplete.component, "row", String(index)),
      rowCell: (index: number | string, column: string): string =>
        joinDataTestId(DATA_TEST_IDS.components.refTownInfoAutocomplete.component, "row", String(index), column),
    },
    siteCloseCallButton: {
      component: "site-close-call-button",
      button: (): string => joinDataTestId(DATA_TEST_IDS.components.siteCloseCallButton.component, "button"),
    },
    advancedSiteSearch: {
      component: "advanced-site-search",
      form: (): string => joinDataTestId(DATA_TEST_IDS.components.advancedSiteSearch.component, "form"),
      card: (): string => joinDataTestId(DATA_TEST_IDS.components.advancedSiteSearch.component, "card"),
      cardHeader: (): string => joinDataTestId(DATA_TEST_IDS.components.advancedSiteSearch.component, "card-header"),
      cardTitle: (): string => joinDataTestId(DATA_TEST_IDS.components.advancedSiteSearch.component, "card-title"),
      cardContent: (): string => joinDataTestId(DATA_TEST_IDS.components.advancedSiteSearch.component, "card-content"),
      selectFieldsTitle: (): string =>
        joinDataTestId(DATA_TEST_IDS.components.advancedSiteSearch.component, "select-fields-title"),
      addFieldButton: (): string =>
        joinDataTestId(DATA_TEST_IDS.components.advancedSiteSearch.component, "add-field-button"),
      fieldRow: (index: number | string): string =>
        joinDataTestId(DATA_TEST_IDS.components.advancedSiteSearch.component, "field-row", String(index)),
      fieldDropdown: (index: number | string): string =>
        joinDataTestId(DATA_TEST_IDS.components.advancedSiteSearch.component, "field-dropdown", String(index)),
      fieldOption: (field: string): string =>
        joinDataTestId(DATA_TEST_IDS.components.advancedSiteSearch.component, "field-option", String(field)),
      filterTypeDropdown: (index: number | string): string =>
        joinDataTestId(DATA_TEST_IDS.components.advancedSiteSearch.component, "filter-type-dropdown", String(index)),
      filterTypeOption: (filter: string): string =>
        joinDataTestId(DATA_TEST_IDS.components.advancedSiteSearch.component, "filter-type-option", String(filter)),
      valueInput: (index: number | string): string =>
        joinDataTestId(DATA_TEST_IDS.components.advancedSiteSearch.component, "value-input", String(index)),
      removeFieldButton: (index: number | string): string =>
        joinDataTestId(DATA_TEST_IDS.components.advancedSiteSearch.component, "remove-field-button", String(index)),
      submitButton: (): string =>
        joinDataTestId(DATA_TEST_IDS.components.advancedSiteSearch.component, "submit-button"),
      countryBasedInputPhone: (): string =>
        joinDataTestId(DATA_TEST_IDS.components.advancedSiteSearch.component, "country-based-input-phone"),
      resultsTitle: (): string =>
        joinDataTestId(DATA_TEST_IDS.components.advancedSiteSearch.component, "results-title"),
      pendingSpinner: (): string =>
        joinDataTestId(DATA_TEST_IDS.components.advancedSiteSearch.component, "pending-spinner"),
      noResultsTitle: (): string =>
        joinDataTestId(DATA_TEST_IDS.components.advancedSiteSearch.component, "no-results-title"),
      noResultsDesc: (): string =>
        joinDataTestId(DATA_TEST_IDS.components.advancedSiteSearch.component, "no-results-desc"),
      table: (): string => joinDataTestId(DATA_TEST_IDS.components.advancedSiteSearch.component, "table"),
      row: (id: string | number): string =>
        joinDataTestId(DATA_TEST_IDS.components.advancedSiteSearch.component, "row", String(id)),
      rowCell: (column: string): string =>
        joinDataTestId(DATA_TEST_IDS.components.advancedSiteSearch.component, "row", column),
    },
    editHeatpumpDeclarationModal: {
      component: "edit-heatpump-declaration-modal",
      dialog: (): string => joinDataTestId(DATA_TEST_IDS.components.editHeatpumpDeclarationModal.component, "dialog"),
    },
    siteEscEligibilityForm: {
      component: "site-esc-eligibility-form",
      form: (): string => joinDataTestId(DATA_TEST_IDS.components.siteEscEligibilityForm.component, "form"),
      title: (name: string): string =>
        joinDataTestId(DATA_TEST_IDS.components.siteEscEligibilityForm.component, "title", name),
      radio: (name: string, value: string): string =>
        joinDataTestId(DATA_TEST_IDS.components.siteEscEligibilityForm.component, "radio", name, value),
      error: (name: string): string =>
        joinDataTestId(DATA_TEST_IDS.components.siteEscEligibilityForm.component, "error", name),
      unqualifiedHeatPumpWarning: (): string =>
        joinDataTestId(DATA_TEST_IDS.components.siteEscEligibilityForm.component, "unqualified-heat-pump-warning"),
      qualifyHeatPumpButton: (): string =>
        joinDataTestId(DATA_TEST_IDS.components.siteEscEligibilityForm.component, "qualify-heat-pump-button"),
      backButton: (): string =>
        joinDataTestId(DATA_TEST_IDS.components.siteEscEligibilityForm.component, "back-button"),
      submitButton: (): string =>
        joinDataTestId(DATA_TEST_IDS.components.siteEscEligibilityForm.component, "submit-button"),
    },
    siteAcquisitionWorkingConditionCard: {
      component: "site-acquisition-working-condition-card",
      coloredBorder: (): string => DATA_TEST_IDS.views.siteDetailsOverviewView.acquisitionWorkingConditionCard(),
      title: (): string =>
        joinDataTestId(DATA_TEST_IDS.components.siteAcquisitionWorkingConditionCard.component, "title"),
      description: (): string =>
        joinDataTestId(DATA_TEST_IDS.components.siteAcquisitionWorkingConditionCard.component, "description"),
      linkButton: (): string =>
        joinDataTestId(DATA_TEST_IDS.components.siteAcquisitionWorkingConditionCard.component, "link-button"),
      chart: (): string =>
        joinDataTestId(DATA_TEST_IDS.components.siteAcquisitionWorkingConditionCard.component, "chart"),
      chartCircle: (): string =>
        joinDataTestId(DATA_TEST_IDS.components.siteAcquisitionWorkingConditionCard.component, "chart-circle"),
    },
    editAddressForm: {
      component: "edit-address-form",
      form: (): string => joinDataTestId(DATA_TEST_IDS.components.editAddressForm.component, "form"),
      submitButton: (): string => joinDataTestId(DATA_TEST_IDS.components.editAddressForm.component, "submit-button"),
    },
    editSiteGeolocalizedAddressForm: {
      component: "edit-site-geolocalized-address-form",
      form: (): string => joinDataTestId(DATA_TEST_IDS.components.editSiteGeolocalizedAddressForm.component, "form"),
      addressInput: (): string =>
        joinDataTestId(DATA_TEST_IDS.components.editSiteGeolocalizedAddressForm.component, "address-input"),
      submitButton: (): string =>
        joinDataTestId(DATA_TEST_IDS.components.editSiteGeolocalizedAddressForm.component, "submit-button"),
    },
    editSiteGeolocalizedAddressQuickSuggestionsForm: {
      component: "edit-site-geolocalized-address-quick-suggestions-form",
      form: (): string =>
        joinDataTestId(DATA_TEST_IDS.components.editSiteGeolocalizedAddressQuickSuggestionsForm.component, "form"),
      loadingRow: (index: number | string): string =>
        joinDataTestId(
          DATA_TEST_IDS.components.editSiteGeolocalizedAddressQuickSuggestionsForm.component,
          "loading-row",
          String(index),
        ),
      row: (index: number | string): string =>
        joinDataTestId(
          DATA_TEST_IDS.components.editSiteGeolocalizedAddressQuickSuggestionsForm.component,
          "row",
          String(index),
        ),
      radio: (index: number | string): string =>
        joinDataTestId(
          DATA_TEST_IDS.components.editSiteGeolocalizedAddressQuickSuggestionsForm.component,
          "radio",
          String(index),
        ),
      label: (index: number | string): string =>
        joinDataTestId(
          DATA_TEST_IDS.components.editSiteGeolocalizedAddressQuickSuggestionsForm.component,
          "label",
          String(index),
        ),
      error: (name: string): string =>
        joinDataTestId(
          DATA_TEST_IDS.components.editSiteGeolocalizedAddressQuickSuggestionsForm.component,
          "error",
          name,
        ),
      submitButton: (): string =>
        joinDataTestId(
          DATA_TEST_IDS.components.editSiteGeolocalizedAddressQuickSuggestionsForm.component,
          "submit-button",
        ),
      skeleton: (): string =>
        joinDataTestId(DATA_TEST_IDS.components.editSiteGeolocalizedAddressQuickSuggestionsForm.component, "skeleton"),
    },
    addressAutocomplete: {
      component: "address-autocomplete",
      label: (): string => joinDataTestId(DATA_TEST_IDS.components.addressAutocomplete.component, "label"),
      complement: (): string => joinDataTestId(DATA_TEST_IDS.components.addressAutocomplete.component, "complement"),
    },
    appointmentAddressBookedModal: {
      component: "appointment-address-booked-modal",
      messageTitle: (): string =>
        joinDataTestId(DATA_TEST_IDS.components.appointmentAddressBookedModal.component, "message-title"),
      messageWarning: (): string =>
        joinDataTestId(DATA_TEST_IDS.components.appointmentAddressBookedModal.component, "message-warning"),
      validateBtn: (): string =>
        joinDataTestId(DATA_TEST_IDS.components.appointmentAddressBookedModal.component, "validate-btn"),
    },
    appointmentNoPdlContractModal: {
      component: "appointment-no-pdl-contract-modal",
      messageDesc: (): string =>
        joinDataTestId(DATA_TEST_IDS.components.appointmentNoPdlContractModal.component, "message-desc"),
      loadingSkeleton: (): string =>
        joinDataTestId(DATA_TEST_IDS.components.appointmentNoPdlContractModal.component, "loading-skeleton"),
      warningMessage: (): string =>
        joinDataTestId(DATA_TEST_IDS.components.appointmentNoPdlContractModal.component, "warning-message"),
      doNotSendBtn: (): string =>
        joinDataTestId(DATA_TEST_IDS.components.appointmentNoPdlContractModal.component, "do-not-send-btn"),
      sendMailBtn: (): string =>
        joinDataTestId(DATA_TEST_IDS.components.appointmentNoPdlContractModal.component, "send-mail-btn"),
    },
    createSiteFormCoordinatesCategory: {
      component: "create-site-form-coordinates-category",
      addressInput: (): string =>
        joinDataTestId(DATA_TEST_IDS.components.createSiteFormCoordinatesCategory.component, "address-input"),
      addressComplementInput: (): string =>
        joinDataTestId(
          DATA_TEST_IDS.components.createSiteFormCoordinatesCategory.component,
          "address-complement-input",
        ),
      digicodeInput: (): string =>
        joinDataTestId(DATA_TEST_IDS.components.createSiteFormCoordinatesCategory.component, "digicode-input"),
      stairwayInput: (): string =>
        joinDataTestId(DATA_TEST_IDS.components.createSiteFormCoordinatesCategory.component, "stairway-input"),
      levelInput: (): string =>
        joinDataTestId(DATA_TEST_IDS.components.createSiteFormCoordinatesCategory.component, "level-input"),
      buildingInput: (): string =>
        joinDataTestId(DATA_TEST_IDS.components.createSiteFormCoordinatesCategory.component, "building-input"),
      addressTypeInput: (): string =>
        joinDataTestId(DATA_TEST_IDS.components.createSiteFormCoordinatesCategory.component, "address-type-input"),
      postalAddressInput: (): string =>
        joinDataTestId(DATA_TEST_IDS.components.createSiteFormCoordinatesCategory.component, "postalAddress-input"),
      selectCountryInput: (): string =>
        joinDataTestId(DATA_TEST_IDS.components.createSiteFormCoordinatesCategory.component, "select-country-input"),
      zipCodeInput: (): string =>
        joinDataTestId(DATA_TEST_IDS.components.createSiteFormCoordinatesCategory.component, "zip-code-input"),
      zipCodeTextInput: (): string =>
        joinDataTestId(DATA_TEST_IDS.components.createSiteFormCoordinatesCategory.component, "zip-code-text-input"),
      cityInput: (): string =>
        joinDataTestId(DATA_TEST_IDS.components.createSiteFormCoordinatesCategory.component, "city-input"),
      cityTextInput: (): string =>
        joinDataTestId(DATA_TEST_IDS.components.createSiteFormCoordinatesCategory.component, "city-text-input"),
    },
    siteRawForm: {
      component: "site-raw-form",
      createSiteBtn: (): string => joinDataTestId(DATA_TEST_IDS.components.siteRawForm.component, "create-site-btn"),
    },
    createSiteForm: {
      component: "create-site-form",
      form: (): string => joinDataTestId(DATA_TEST_IDS.components.createSiteForm.component, "form"),
      siteType: (): string => joinDataTestId(DATA_TEST_IDS.components.createSiteForm.component, "site-type"),
      siteTypeOption: (type: "residential" | "tertiary"): string =>
        joinDataTestId(DATA_TEST_IDS.components.createSiteForm.component, "site-type-option", type),
      externalId: (): string => joinDataTestId(DATA_TEST_IDS.components.createSiteForm.component, "external-id"),
      companyName: (): string => joinDataTestId(DATA_TEST_IDS.components.createSiteForm.component, "company-name"),
      corporateName: (): string => joinDataTestId(DATA_TEST_IDS.components.createSiteForm.component, "corporate-name"),
      coordinateCategory: (): string =>
        joinDataTestId(DATA_TEST_IDS.components.createSiteForm.component, "coordinate-category"),
      partnerShipCategory: (): string =>
        joinDataTestId(DATA_TEST_IDS.components.createSiteForm.component, "partner-ship-category"),
      siteTypeCategory: (): string =>
        joinDataTestId(DATA_TEST_IDS.components.createSiteForm.component, "parnter-ship-category"),
      customNetworkManagerDropdown: (): string =>
        joinDataTestId(DATA_TEST_IDS.components.createSiteForm.component, "custom-network-manager-dropdown"),
      customNetworkManagerDropdownInput: (): string =>
        joinDataTestId(DATA_TEST_IDS.components.createSiteForm.component, "custom-network-manager-dropdown-input"),
      customNetworkManagerDropdownPanel: (): string =>
        joinDataTestId(DATA_TEST_IDS.components.createSiteForm.component, "custom-network-manager-dropdown-panel"),
      customNetworkManagerDropdownOption: (value: string): string =>
        joinDataTestId(
          DATA_TEST_IDS.components.createSiteForm.component,
          "custom-network-manager-dropdown-option",
          value,
        ),
      siteTypeLayoutPanel: (): string =>
        joinDataTestId(DATA_TEST_IDS.components.createSiteForm.component, "site-type-layout-panel"),
      siteTypeLayoutParentOption: (description: string): string =>
        joinDataTestId(
          DATA_TEST_IDS.components.createSiteForm.component,
          "site-type-layout-parent-option",
          description,
        ),
      siteTypeLayoutChildOption: (description: string): string =>
        joinDataTestId(DATA_TEST_IDS.components.createSiteForm.component, "site-type-layout-child-option", description),
      technicalCategory: (): string =>
        joinDataTestId(DATA_TEST_IDS.components.createSiteForm.component, "technical-category"),
      additionalInfoCategory: (): string =>
        joinDataTestId(DATA_TEST_IDS.components.createSiteForm.component, "additional-info-category"),
    },
    createSiteFormTechnicalCategory: {
      component: "create-site-form-technical-category",
      title: (): string => joinDataTestId(DATA_TEST_IDS.components.createSiteFormTechnicalCategory.component, "title"),
      subscribedPowerTypeInput: (): string =>
        joinDataTestId(
          DATA_TEST_IDS.components.createSiteFormTechnicalCategory.component,
          "subscribed-power-type-input",
        ),
      subscribedPowerTypeOption: (value: string): string =>
        joinDataTestId(
          DATA_TEST_IDS.components.createSiteFormTechnicalCategory.component,
          "subscribed-power-type-option",
          value,
        ),
      subscribedPowerTypePanel: (): string =>
        joinDataTestId(
          DATA_TEST_IDS.components.createSiteFormTechnicalCategory.component,
          "subscribed-power-type-panel",
        ),
      eligibleApplianceNumberInput: (): string =>
        joinDataTestId(
          DATA_TEST_IDS.components.createSiteFormTechnicalCategory.component,
          "eligible-appliance-number-input",
        ),
      heatPumpInSiteLabel: (): string =>
        joinDataTestId(DATA_TEST_IDS.components.createSiteFormTechnicalCategory.component, "heat-pump-in-site-label"),
      heatPumpInSiteInput: (): string =>
        joinDataTestId(DATA_TEST_IDS.components.createSiteFormTechnicalCategory.component, "heat-pump-in-site-input"),
      heatPumpInSiteRadio: (value: string | boolean): string =>
        joinDataTestId(
          DATA_TEST_IDS.components.createSiteFormTechnicalCategory.component,
          "heat-pump-in-site-radio",
          String(value),
        ),
      heatPumpBrandInput: (): string =>
        joinDataTestId(DATA_TEST_IDS.components.createSiteFormTechnicalCategory.component, "heat-pump-brand-input"),
      heatPumpModelInput: (): string =>
        joinDataTestId(DATA_TEST_IDS.components.createSiteFormTechnicalCategory.component, "heat-pump-model-input"),
      addBrandButton: (): string =>
        joinDataTestId(DATA_TEST_IDS.components.createSiteFormTechnicalCategory.component, "add-brand-button"),
      addModelButton: (): string =>
        joinDataTestId(DATA_TEST_IDS.components.createSiteFormTechnicalCategory.component, "add-model-button"),
      notEligibleWarning: (): string =>
        joinDataTestId(DATA_TEST_IDS.components.createSiteFormTechnicalCategory.component, "not-eligible-warning"),
      applianceEstimate: (code: EApplianceEstimateCode): string =>
        joinDataTestId(
          DATA_TEST_IDS.components.createSiteFormTechnicalCategory.component,
          "eligible-appliance-number",
          code,
        ),
    },
    applianceEstimatesFields: {
      error: (): string => "appliance-estimates-fields-error",
    },
    createSiteFormPartnershipCategory: {
      component: "create-site-form-partnership-category",
      title: (): string =>
        joinDataTestId(DATA_TEST_IDS.components.createSiteFormPartnershipCategory.component, "title"),
      operationInput: (): string =>
        joinDataTestId(DATA_TEST_IDS.components.createSiteFormPartnershipCategory.component, "operation-input"),
      operationPanel: (): string =>
        joinDataTestId(DATA_TEST_IDS.components.createSiteFormPartnershipCategory.component, "operation-panel"),
      operationParentOption: (name: string): string =>
        joinDataTestId(
          DATA_TEST_IDS.components.createSiteFormPartnershipCategory.component,
          "operation-parent-option",
          name,
        ),
      operationChildOption: (): string =>
        joinDataTestId(DATA_TEST_IDS.components.createSiteFormPartnershipCategory.component, "operation-child-option"),
      missionInput: (): string =>
        joinDataTestId(DATA_TEST_IDS.components.createSiteFormPartnershipCategory.component, "mission-input"),
      missionPanel: (): string =>
        joinDataTestId(DATA_TEST_IDS.components.createSiteFormPartnershipCategory.component, "mission-panel"),
      missionOption: (missionId: number | string): string =>
        joinDataTestId(
          DATA_TEST_IDS.components.createSiteFormPartnershipCategory.component,
          "mission-option",
          String(missionId),
        ),
    },
    createSiteFormAdditionalInfoCategory: {
      component: "create-site-form-additional-info-category",
      title: (): string =>
        joinDataTestId(DATA_TEST_IDS.components.createSiteFormAdditionalInfoCategory.component, "title"),
      occupancyTypeInput: (): string =>
        joinDataTestId(DATA_TEST_IDS.components.createSiteFormAdditionalInfoCategory.component, "occupancy-type-input"),
      surfaceInput: (): string =>
        joinDataTestId(DATA_TEST_IDS.components.createSiteFormAdditionalInfoCategory.component, "surface-input"),
      wasEquippedWithTiko: (value: boolean | string): string =>
        joinDataTestId(
          DATA_TEST_IDS.components.createSiteFormAdditionalInfoCategory.component,
          "was-equipped-with-tiko-checkbox",
          String(value),
        ),
      wasEquippedWithTikoLabel: (): string =>
        joinDataTestId(
          DATA_TEST_IDS.components.createSiteFormAdditionalInfoCategory.component,
          "was-equipped-with-tiko-checkbox-label",
        ),
      buildingMoreThan2YearsLabel: (): string =>
        joinDataTestId(
          DATA_TEST_IDS.components.createSiteFormAdditionalInfoCategory.component,
          "building-more-than-2-years-label",
        ),
      buildingMoreThan2YearsRadio: (value: string | boolean): string =>
        joinDataTestId(
          DATA_TEST_IDS.components.createSiteFormAdditionalInfoCategory.component,
          "building-more-than-2-years-radio",
          String(value),
        ),
    },
    siteConsumptionsCard: {
      component: "site-consumptions-card",
      title: (): string => joinDataTestId(DATA_TEST_IDS.components.siteConsumptionsCard.component, "title"),
      modulatorsDropdown: (): string =>
        joinDataTestId(DATA_TEST_IDS.components.siteConsumptionsCard.component, "modulators-dropdown"),
      calendar: (): string => joinDataTestId(DATA_TEST_IDS.components.siteConsumptionsCard.component, "calendar"),
      skeleton: (): string => joinDataTestId(DATA_TEST_IDS.components.siteConsumptionsCard.component, "skeleton"),
      chart: (): string => joinDataTestId(DATA_TEST_IDS.components.siteConsumptionsCard.component, "chart"),
    },
    editSitePostalAddressForm: {
      component: "edit-site-postal-address-form",

      addressLabel: (): string =>
        joinDataTestId(DATA_TEST_IDS.components.editSitePostalAddressForm.component, "address-label"),
      addressInput: (): string =>
        joinDataTestId(DATA_TEST_IDS.components.editSitePostalAddressForm.component, "address-input"),
      addressError: (): string =>
        joinDataTestId(DATA_TEST_IDS.components.editSitePostalAddressForm.component, "address-error"),
      addressComplementLabel: (): string =>
        joinDataTestId(DATA_TEST_IDS.components.editSitePostalAddressForm.component, "address-complement-label"),
      addressComplementInput: (): string =>
        joinDataTestId(DATA_TEST_IDS.components.editSitePostalAddressForm.component, "address-complement-input"),
      addressComplementError: (): string =>
        joinDataTestId(DATA_TEST_IDS.components.editSitePostalAddressForm.component, "address-complement-error"),
      digicodeLabel: (): string =>
        joinDataTestId(DATA_TEST_IDS.components.editSitePostalAddressForm.component, "digicode-label"),
      digicodeInput: (): string =>
        joinDataTestId(DATA_TEST_IDS.components.editSitePostalAddressForm.component, "digicode-input"),
      digicodeError: (): string =>
        joinDataTestId(DATA_TEST_IDS.components.editSitePostalAddressForm.component, "digicode-error"),
      stairwayInput: (): string =>
        joinDataTestId(DATA_TEST_IDS.components.editSitePostalAddressForm.component, "stairway-input"),
      stairwayError: (): string =>
        joinDataTestId(DATA_TEST_IDS.components.editSitePostalAddressForm.component, "stairway-error"),
      levelInput: (): string =>
        joinDataTestId(DATA_TEST_IDS.components.editSitePostalAddressForm.component, "level-input"),
      levelError: (): string =>
        joinDataTestId(DATA_TEST_IDS.components.editSitePostalAddressForm.component, "level-error"),
      buildingLabel: (): string =>
        joinDataTestId(DATA_TEST_IDS.components.editSitePostalAddressForm.component, "building-label"),
      buildingInput: (): string =>
        joinDataTestId(DATA_TEST_IDS.components.editSitePostalAddressForm.component, "building-input"),
      buildingError: (): string =>
        joinDataTestId(DATA_TEST_IDS.components.editSitePostalAddressForm.component, "building-error"),
      refTownInfoLabel: (): string =>
        joinDataTestId(DATA_TEST_IDS.components.editSitePostalAddressForm.component, "ref-town-info-label"),
      refTownInfoError: (): string =>
        joinDataTestId(DATA_TEST_IDS.components.editSitePostalAddressForm.component, "ref-town-info-error"),
      zipCodeInput: (): string =>
        joinDataTestId(DATA_TEST_IDS.components.editSitePostalAddressForm.component, "zip-code-input"),
      zipCodeTextInput: (): string =>
        joinDataTestId(DATA_TEST_IDS.components.editSitePostalAddressForm.component, "zip-code-text-input"),
      zipCodeError: (): string =>
        joinDataTestId(DATA_TEST_IDS.components.editSitePostalAddressForm.component, "zip-code-error"),
      refTownInfoCityLabel: (): string =>
        joinDataTestId(DATA_TEST_IDS.components.editSitePostalAddressForm.component, "ref-town-info-city-label"),
      cityInput: (): string =>
        joinDataTestId(DATA_TEST_IDS.components.editSitePostalAddressForm.component, "city-input"),
      cityLabel: (): string =>
        joinDataTestId(DATA_TEST_IDS.components.editSitePostalAddressForm.component, "city-label"),
      cityTextInput: (): string =>
        joinDataTestId(DATA_TEST_IDS.components.editSitePostalAddressForm.component, "city-text-input"),
      cityError: (): string =>
        joinDataTestId(DATA_TEST_IDS.components.editSitePostalAddressForm.component, "city-error"),
      updatePostalAddressToGeolocalisedAddressButton: (): string =>
        joinDataTestId(
          DATA_TEST_IDS.components.editSitePostalAddressForm.component,
          "update-postal-address-to-geolocalised-address-button",
        ),
      updateSitePostalAddress: (): string =>
        joinDataTestId(DATA_TEST_IDS.components.editSitePostalAddressForm.component, "update-site-postal-address"),
    },
    siteAddressInput: {
      component: "site-address-input",
      countrySelect: (): string =>
        joinDataTestId(DATA_TEST_IDS.components.siteAddressInput.component, "country-select"),
      addressAutocomplete: (): string =>
        joinDataTestId(DATA_TEST_IDS.components.siteAddressInput.component, "address-autocomplete"),
      addressAutocompletePanel: (): string =>
        joinDataTestId(DATA_TEST_IDS.components.siteAddressInput.component, "address-autocomplete-panel"),
      addressAutocompleteOption: (): string =>
        joinDataTestId(DATA_TEST_IDS.components.siteAddressInput.component, "address-autocomplete-option"),
      addressNotFoundButton: (): string =>
        joinDataTestId(DATA_TEST_IDS.components.siteAddressInput.component, "address-not-found-button"),
      exactAddressFoundButton: (): string =>
        joinDataTestId(DATA_TEST_IDS.components.siteAddressInput.component, "exact-address-found-button"),
      complementInput: (): string =>
        joinDataTestId(DATA_TEST_IDS.components.siteAddressInput.component, "complement-input"),
      digicodeInput: (): string =>
        joinDataTestId(DATA_TEST_IDS.components.siteAddressInput.component, "digicode-input"),
      stairwayInput: (): string =>
        joinDataTestId(DATA_TEST_IDS.components.siteAddressInput.component, "stairway-input"),
      levelInput: (): string => joinDataTestId(DATA_TEST_IDS.components.siteAddressInput.component, "level-input"),
      buildingInput: (): string =>
        joinDataTestId(DATA_TEST_IDS.components.siteAddressInput.component, "building-input"),
      addressLineInput: (): string =>
        joinDataTestId(DATA_TEST_IDS.components.siteAddressInput.component, "address-line-input"),
      zipCodeInput: (): string => joinDataTestId(DATA_TEST_IDS.components.siteAddressInput.component, "zip-code-input"),
      cityInput: (): string => joinDataTestId(DATA_TEST_IDS.components.siteAddressInput.component, "city-input"),
      nearAddressAutocomplete: (): string =>
        joinDataTestId(DATA_TEST_IDS.components.siteAddressInput.component, "near-address-autocomplete"),
    },
    createSubscriberForm: {
      component: "create-subscriber-form",
      civilityDropdown: (): string =>
        joinDataTestId(DATA_TEST_IDS.components.createSubscriberForm.component, "civility-dropdown"),
      civilityError: (): string =>
        joinDataTestId(DATA_TEST_IDS.components.createSubscriberForm.component, "civility-error"),
      firstnameInput: (): string =>
        joinDataTestId(DATA_TEST_IDS.components.createSubscriberForm.component, "firstname-input"),
      firstnameError: (): string =>
        joinDataTestId(DATA_TEST_IDS.components.createSubscriberForm.component, "firstname-error"),
      lastnameInput: (): string =>
        joinDataTestId(DATA_TEST_IDS.components.createSubscriberForm.component, "lastname-input"),
      lastnameError: (): string =>
        joinDataTestId(DATA_TEST_IDS.components.createSubscriberForm.component, "lastname-error"),
      defaultMailInput: (): string =>
        joinDataTestId(DATA_TEST_IDS.components.createSubscriberForm.component, "default-mail-input"),
      defaultMailError: (): string =>
        joinDataTestId(DATA_TEST_IDS.components.createSubscriberForm.component, "default-mail-error"),
      defaultPhoneInput: (): string =>
        joinDataTestId(DATA_TEST_IDS.components.createSubscriberForm.component, "default-phone-input"),
      defaultPhoneError: (): string =>
        joinDataTestId(DATA_TEST_IDS.components.createSubscriberForm.component, "default-phone-error"),
      submitBtn: (): string =>
        joinDataTestId(DATA_TEST_IDS.components.createSubscriberForm.component, "create-subscriber-btn"),
      form: (): string => joinDataTestId(DATA_TEST_IDS.components.createSubscriberForm.component, "form"),
    },
    uiCard: {
      component: "ui-card",
      header: (): string => joinDataTestId(DATA_TEST_IDS.components.uiCard.component, "header"),
      title: (): string => joinDataTestId(DATA_TEST_IDS.components.uiCard.component, "title"),
      description: (): string => joinDataTestId(DATA_TEST_IDS.components.uiCard.component, "description"),
      disclaimer: (): string => joinDataTestId(DATA_TEST_IDS.components.uiCard.component, "disclaimer"),
      content: (): string => joinDataTestId(DATA_TEST_IDS.components.uiCard.component, "content"),
      footer: (): string => joinDataTestId(DATA_TEST_IDS.components.uiCard.component, "footer"),
    },
    uiBaseInput: {
      component: "ui-base-input",
      label: (): string => joinDataTestId(DATA_TEST_IDS.components.uiBaseInput.component, "label"),
      error: (): string => joinDataTestId(DATA_TEST_IDS.components.uiBaseInput.component, "error"),
    },
    uiTextInput: {
      component: "ui-text-input",
    },
    uiNumberInput: {
      component: "ui-number-input",
      input: (): string => joinDataTestId(DATA_TEST_IDS.components.uiNumberInput.component, "input"),
    },
    uiDropdown: {
      component: "ui-dropdown",
    },
    uiRadioButton: {
      component: "ui-radio-button",
    },
    uiButtonSelect: buttonSelectDataTestIds,
    uiCustomInput: {
      component: "ui-custom-input",
    },
    uiStatus: {
      component: "ui-status",
      indicator: (): string => joinDataTestId(DATA_TEST_IDS.components.uiStatus.component, "indicator"),
      label: (): string => joinDataTestId(DATA_TEST_IDS.components.uiStatus.component, "label"),
    },
    uiAutoCompleteCustom: {
      component: "ui-auto-complete-custom",
      input: (): string => joinDataTestId(DATA_TEST_IDS.components.uiAutoCompleteCustom.component, "input"),
      dropdownBtn: (): string =>
        joinDataTestId(DATA_TEST_IDS.components.uiAutoCompleteCustom.component, "dropdown-btn"),
      suggestions: (): string => joinDataTestId(DATA_TEST_IDS.components.uiAutoCompleteCustom.component, "suggestions"),
      suggestionItem: (): string =>
        joinDataTestId(DATA_TEST_IDS.components.uiAutoCompleteCustom.component, "suggestion-item"),
      emptyMessage: (): string =>
        joinDataTestId(DATA_TEST_IDS.components.uiAutoCompleteCustom.component, "empty-message"),
      loadingSpinner: (): string =>
        joinDataTestId(DATA_TEST_IDS.components.uiAutoCompleteCustom.component, "loading-spinner"),
      clearBtn: (): string => joinDataTestId(DATA_TEST_IDS.components.uiAutoCompleteCustom.component, "clear-btn"),
    },
    uiCustomButton: {
      component: "ui-custom-button",
    },
    uiDynamicOverlayPanel: {
      component: "ui-dynamic-overlay-panel",
      content: (): string => joinDataTestId(DATA_TEST_IDS.components.uiDynamicOverlayPanel.component, "content"),
    },
    uiBadge: {
      component: "ui-badge",
      label: (): string => joinDataTestId(DATA_TEST_IDS.components.uiBadge.component, "label"),
    },
    uiStepper: {
      component: "ui-stepper",
      steps: (): string => joinDataTestId(DATA_TEST_IDS.components.uiStepper.component, "steps"),
      content: (): string => joinDataTestId(DATA_TEST_IDS.components.uiStepper.component, "content"),
    },
    activeSubscriptionCard: activeSubscriptionCardDataTestIds,
    subscriptionEmptyStateCard: subscriptionEmptyStateCardDataTestIds,
    subscriptionHistory: subscriptionHistoryDataTestIds,
    settingsModal: {
      component: "settings-modal",
      languageLabel: (): string => joinDataTestId(DATA_TEST_IDS.components.settingsModal.component, "language-label"),
      languageHelp: (): string => joinDataTestId(DATA_TEST_IDS.components.settingsModal.component, "language-help"),
      languageDropdown: (): string =>
        joinDataTestId(DATA_TEST_IDS.components.settingsModal.component, "language-dropdown"),
      fontSizeLabel: (): string => joinDataTestId(DATA_TEST_IDS.components.settingsModal.component, "font-size-label"),
      fontSizeHelp: (): string => joinDataTestId(DATA_TEST_IDS.components.settingsModal.component, "font-size-help"),
      fontSizeValue: (): string => joinDataTestId(DATA_TEST_IDS.components.settingsModal.component, "font-size-value"),
      fontSizeSlider: (): string =>
        joinDataTestId(DATA_TEST_IDS.components.settingsModal.component, "font-size-slider"),
      largeScreenLabel: (): string =>
        joinDataTestId(DATA_TEST_IDS.components.settingsModal.component, "large-screen-label"),
      largeScreenHelp: (): string =>
        joinDataTestId(DATA_TEST_IDS.components.settingsModal.component, "large-screen-help"),
      largeScreenSwitch: (): string =>
        joinDataTestId(DATA_TEST_IDS.components.settingsModal.component, "large-screen-switch"),
      closeBtn: (): string => joinDataTestId(DATA_TEST_IDS.components.settingsModal.component, "close-btn"),
      title: (): string => joinDataTestId(DATA_TEST_IDS.components.settingsModal.component, "title"),
    },
    loginForm: {
      component: "login-form",
      form: (): string => joinDataTestId(DATA_TEST_IDS.components.loginForm.component, "form"),
      usernameInputText: (): string =>
        joinDataTestId(DATA_TEST_IDS.components.loginForm.component, "username-input-text"),
      usernameLabel: (): string => joinDataTestId(DATA_TEST_IDS.components.loginForm.component, "username-label"),
      passwordLabel: (): string => joinDataTestId(DATA_TEST_IDS.components.loginForm.component, "password-label"),
      passwordInputText: (): string =>
        joinDataTestId(DATA_TEST_IDS.components.loginForm.component, "password-input-text"),
      passwordVisibilityToggle: (): string =>
        joinDataTestId(DATA_TEST_IDS.components.loginForm.component, "password-visibility-toggle"),
      passwordVisibilityShowIcon: (): string =>
        joinDataTestId(DATA_TEST_IDS.components.loginForm.component, "password-visibility-show-icon"),
      passwordVisibilityHideIcon: (): string =>
        joinDataTestId(DATA_TEST_IDS.components.loginForm.component, "password-visibility-hide-icon"),

      submitLoginBtn: (): string => joinDataTestId(DATA_TEST_IDS.components.loginForm.component, "submit-login-btn"),
      usernameError: "login-form-username-error",
      passwordError: "login-form-password-error",
    },
    openedFoldersCard: {
      component: "opened-folders-card",
      cardTitle: (): string => joinDataTestId(DATA_TEST_IDS.components.openedFoldersCard.component, "card-title"),
      cardHeader: (): string => joinDataTestId(DATA_TEST_IDS.components.openedFoldersCard.component, "card-header"),
      cardContent: (): string => joinDataTestId(DATA_TEST_IDS.components.openedFoldersCard.component, "card-content"),
      emptyMessage: (): string => joinDataTestId(DATA_TEST_IDS.components.openedFoldersCard.component, "empty-message"),
      emptyNoResultsTitle: (): string =>
        joinDataTestId(DATA_TEST_IDS.components.openedFoldersCard.component, "empty-no-results-title"),
      emptyNoResultsDesc: (): string =>
        joinDataTestId(DATA_TEST_IDS.components.openedFoldersCard.component, "empty-no-results-desc"),
    },
    openedFoldersDatatable: {
      component: "opened-folders-datatable",
      table: (): string => joinDataTestId(DATA_TEST_IDS.components.openedFoldersDatatable.component, "table"),
      tableRowId: (): string =>
        joinDataTestId(DATA_TEST_IDS.components.openedFoldersDatatable.component, "table-row-id"),
      firstName: (): string => joinDataTestId(DATA_TEST_IDS.components.openedFoldersDatatable.component, "first-name"),
      lastName: (): string => joinDataTestId(DATA_TEST_IDS.components.openedFoldersDatatable.component, "last-name"),
      address: (): string => joinDataTestId(DATA_TEST_IDS.components.openedFoldersDatatable.component, "address"),
      townInfoPostCode: (): string =>
        joinDataTestId(DATA_TEST_IDS.components.openedFoldersDatatable.component, "town-info-post-code"),
      townInfoName: (): string =>
        joinDataTestId(DATA_TEST_IDS.components.openedFoldersDatatable.component, "town-info-name"),
      siteCloseCallButton: (): string =>
        joinDataTestId(DATA_TEST_IDS.components.openedFoldersDatatable.component, "site-close-call-button"),
    },
    siteDetailsLayout: {
      component: "site-details-layout",
      pageTitle: (): string => joinDataTestId(DATA_TEST_IDS.components.siteDetailsLayout.component, "page-title"),
    },
    applianceAlreadyInstalledCountCard: {
      component: "appliance-already-installed-count-card",
      loadingSkeleton: (): string =>
        joinDataTestId(DATA_TEST_IDS.components.applianceAlreadyInstalledCountCard.component, "loading-skeleton"),
      cardTitle: (): string =>
        joinDataTestId(DATA_TEST_IDS.components.applianceAlreadyInstalledCountCard.component, "title"),
      cardHeader: (): string =>
        joinDataTestId(DATA_TEST_IDS.components.applianceAlreadyInstalledCountCard.component, "header"),
      radiatorsLabel: (): string =>
        joinDataTestId(DATA_TEST_IDS.components.applianceAlreadyInstalledCountCard.component, "radiators-label"),
      applianceNumber: (): string =>
        joinDataTestId(DATA_TEST_IDS.components.applianceAlreadyInstalledCountCard.component, "appliance-number"),
      waterHeaterLabel: (): string =>
        joinDataTestId(DATA_TEST_IDS.components.applianceAlreadyInstalledCountCard.component, "water-heater-label"),
      waterHeaterNumber: (): string =>
        joinDataTestId(DATA_TEST_IDS.components.applianceAlreadyInstalledCountCard.component, "water-heater-number"),
      pacLabel: (): string =>
        joinDataTestId(DATA_TEST_IDS.components.applianceAlreadyInstalledCountCard.component, "pac-label"),
      heatPumpNumber: (): string =>
        joinDataTestId(DATA_TEST_IDS.components.applianceAlreadyInstalledCountCard.component, "heat-pump-number"),
    },
    eligibleApplianceCountCard: {
      component: "eligible-appliance-count-card",
      loadingSkeleton: (): string =>
        joinDataTestId(DATA_TEST_IDS.components.eligibleApplianceCountCard.component, "loading-skeleton"),
      cardTitle: (): string =>
        joinDataTestId(DATA_TEST_IDS.components.eligibleApplianceCountCard.component, "card-title"),
      cardHeader: (): string =>
        joinDataTestId(DATA_TEST_IDS.components.eligibleApplianceCountCard.component, "card-header"),
      cardContent: (): string =>
        joinDataTestId(DATA_TEST_IDS.components.eligibleApplianceCountCard.component, "card-content"),
      radiatorsLabel: (): string =>
        joinDataTestId(DATA_TEST_IDS.components.eligibleApplianceCountCard.component, "radiators-label"),
      radiatorsNumber: (): string =>
        joinDataTestId(DATA_TEST_IDS.components.eligibleApplianceCountCard.component, "radiators-number"),
      waterHeaterLabel: (): string =>
        joinDataTestId(DATA_TEST_IDS.components.eligibleApplianceCountCard.component, "water-heater-label"),
      waterHeaterNumber: (): string =>
        joinDataTestId(DATA_TEST_IDS.components.eligibleApplianceCountCard.component, "water-heater-number"),
      pacLabel: (): string =>
        joinDataTestId(DATA_TEST_IDS.components.eligibleApplianceCountCard.component, "pac-label"),
      heatPumpNumber: (): string =>
        joinDataTestId(DATA_TEST_IDS.components.eligibleApplianceCountCard.component, "heat-pump-number"),
    },
    appointmentHistoryCard: {
      component: "appointment-history-card",
      cardHeader: (): string =>
        joinDataTestId(DATA_TEST_IDS.components.appointmentHistoryCard.component, "card-header"),
      cardTitle: (): string => joinDataTestId(DATA_TEST_IDS.components.appointmentHistoryCard.component, "card-title"),
      cardDescription: (): string =>
        joinDataTestId(DATA_TEST_IDS.components.appointmentHistoryCard.component, "card-description"),
      cardContent: (): string =>
        joinDataTestId(DATA_TEST_IDS.components.appointmentHistoryCard.component, "card-content"),
      loadingSkeleton: (): string =>
        joinDataTestId(DATA_TEST_IDS.components.appointmentHistoryCard.component, "loading-skeleton"),
      emptyTitle: (): string =>
        joinDataTestId(DATA_TEST_IDS.components.appointmentHistoryCard.component, "empty-title"),
      emptyDesc: (): string => joinDataTestId(DATA_TEST_IDS.components.appointmentHistoryCard.component, "empty-desc"),
      timeline: (): string => joinDataTestId(DATA_TEST_IDS.components.appointmentHistoryCard.component, "timeline"),
      appointmentHistoryItem: (): string =>
        joinDataTestId(DATA_TEST_IDS.components.appointmentHistoryCard.component, "appointment-history-item"),
    },
    appointmentManualBookingForm: {
      component: "appointment-manual-booking-form",
      form: (): string => joinDataTestId(DATA_TEST_IDS.components.appointmentManualBookingForm.component, "form"),
      minDurationLabel: (): string =>
        joinDataTestId(DATA_TEST_IDS.components.appointmentManualBookingForm.component, "min-duration-label"),
      durationWarningMessage: (): string =>
        joinDataTestId(DATA_TEST_IDS.components.appointmentManualBookingForm.component, "duration-warning-message"),
      dateLabel: (): string =>
        joinDataTestId(DATA_TEST_IDS.components.appointmentManualBookingForm.component, "date-label"),
      dateCalendar: (): string =>
        joinDataTestId(DATA_TEST_IDS.components.appointmentManualBookingForm.component, "date-calendar"),
      dateError: (): string =>
        joinDataTestId(DATA_TEST_IDS.components.appointmentManualBookingForm.component, "date-error"),
      startTimeLabel: (): string =>
        joinDataTestId(DATA_TEST_IDS.components.appointmentManualBookingForm.component, "start-time-label"),
      startTimeCalendar: (): string =>
        joinDataTestId(DATA_TEST_IDS.components.appointmentManualBookingForm.component, "start-time-calendar"),
      startTimeError: (): string =>
        joinDataTestId(DATA_TEST_IDS.components.appointmentManualBookingForm.component, "start-time-error"),
      endTimeLabel: (): string =>
        joinDataTestId(DATA_TEST_IDS.components.appointmentManualBookingForm.component, "end-time-label"),
      endTimeCalendar: (): string =>
        joinDataTestId(DATA_TEST_IDS.components.appointmentManualBookingForm.component, "end-time-calendar"),
      endTimeError: (): string =>
        joinDataTestId(DATA_TEST_IDS.components.appointmentManualBookingForm.component, "end-time-error"),
      commentLabel: (): string =>
        joinDataTestId(DATA_TEST_IDS.components.appointmentManualBookingForm.component, "comment-label"),
      commentTextarea: (): string =>
        joinDataTestId(DATA_TEST_IDS.components.appointmentManualBookingForm.component, "comment-textarea"),
      commentError: (): string =>
        joinDataTestId(DATA_TEST_IDS.components.appointmentManualBookingForm.component, "comment-error"),
      submitBtn: (): string =>
        joinDataTestId(DATA_TEST_IDS.components.appointmentManualBookingForm.component, "submit-btn"),
      forceAppointmentYes: (): string =>
        joinDataTestId(DATA_TEST_IDS.components.appointmentManualBookingForm.component, "force-appointment-yes"),
      forceAppointmentYesLabel: (): string =>
        joinDataTestId(DATA_TEST_IDS.components.appointmentManualBookingForm.component, "force-appointment-yes-label"),
      forceAppointmentNo: (): string =>
        joinDataTestId(DATA_TEST_IDS.components.appointmentManualBookingForm.component, "force-appointment-no"),
      forceAppointmentNoLabel: (): string =>
        joinDataTestId(DATA_TEST_IDS.components.appointmentManualBookingForm.component, "force-appointment-no-label"),
    },
    appointmentBookingModal: {
      component: "appointment-booking-modal",
      stepper: (): string => joinDataTestId(DATA_TEST_IDS.components.appointmentBookingModal.component, "stepper"),
      appointmentBookingAvailabilities: (): string =>
        joinDataTestId(
          DATA_TEST_IDS.components.appointmentBookingModal.component,
          "appointment-booking-availabilities",
        ),
      appointmentBookingConfirmation: (): string =>
        joinDataTestId(DATA_TEST_IDS.components.appointmentBookingModal.component, "appointment-booking-confirmation"),
      appointmentBookingBook: (): string =>
        joinDataTestId(DATA_TEST_IDS.components.appointmentBookingModal.component, "appointment-booking-book"),
    },
    appointmentManualBookingModal: {
      component: "appointment-manual-booking-modal",
      stepper: (): string =>
        joinDataTestId(DATA_TEST_IDS.components.appointmentManualBookingModal.component, "stepper"),
      appointmentManualBookingForm: (): string =>
        joinDataTestId(
          DATA_TEST_IDS.components.appointmentManualBookingModal.component,
          "appointment-manual-booking-form",
        ),
    },
    applianceLeftToInstallCountCard: {
      component: "appliance-left-to-install-count-card",
      loadingSkeleton: (): string =>
        joinDataTestId(DATA_TEST_IDS.components.applianceLeftToInstallCountCard.component, "loading-skeleton"),
      cardTitle: (): string =>
        joinDataTestId(DATA_TEST_IDS.components.applianceLeftToInstallCountCard.component, "cardTitle"),
      cardHeader: (): string =>
        joinDataTestId(DATA_TEST_IDS.components.applianceLeftToInstallCountCard.component, "cardHeader"),
      cardContent: (): string =>
        joinDataTestId(DATA_TEST_IDS.components.applianceLeftToInstallCountCard.component, "cardContent"),
      radiatorsLabel: (): string =>
        joinDataTestId(DATA_TEST_IDS.components.applianceLeftToInstallCountCard.component, "radiators-label"),
      radiatorsNumber: (): string =>
        joinDataTestId(DATA_TEST_IDS.components.applianceLeftToInstallCountCard.component, "radiators-number"),
      waterHeaterLabel: (): string =>
        joinDataTestId(DATA_TEST_IDS.components.applianceLeftToInstallCountCard.component, "water-heater-label"),
      waterHeaterNumber: (): string =>
        joinDataTestId(DATA_TEST_IDS.components.applianceLeftToInstallCountCard.component, "water-heater-number"),
      pacLabel: (): string =>
        joinDataTestId(DATA_TEST_IDS.components.applianceLeftToInstallCountCard.component, "pac-label"),
      heatPumpNumber: (): string =>
        joinDataTestId(DATA_TEST_IDS.components.applianceLeftToInstallCountCard.component, "heat-pump-number"),
    },
    appointmentAddressDiff: {
      component: "appointment-address-diff",
      address: (): string => joinDataTestId(DATA_TEST_IDS.components.appointmentAddressDiff.component, "address"),
      postalTitle: (): string =>
        joinDataTestId(DATA_TEST_IDS.components.appointmentAddressDiff.component, "postal-title"),
      editPostalBtn: (): string =>
        joinDataTestId(DATA_TEST_IDS.components.appointmentAddressDiff.component, "edit-postal-btn"),
      postalAddress: (): string =>
        joinDataTestId(DATA_TEST_IDS.components.appointmentAddressDiff.component, "postal-address"),
      postalAddressComplement: (): string =>
        joinDataTestId(DATA_TEST_IDS.components.appointmentAddressDiff.component, "postal-address-complement"),
      postalZipAndCity: (): string =>
        joinDataTestId(DATA_TEST_IDS.components.appointmentAddressDiff.component, "postal-zip-city"),
      postalCountry: (): string =>
        joinDataTestId(DATA_TEST_IDS.components.appointmentAddressDiff.component, "postal-country"),
      geolocTitle: (): string =>
        joinDataTestId(DATA_TEST_IDS.components.appointmentAddressDiff.component, "geoloc-title"),
      editGeolocBtn: (): string =>
        joinDataTestId(DATA_TEST_IDS.components.appointmentAddressDiff.component, "edit-geoloc-btn"),
      geolocEmpty: (): string =>
        joinDataTestId(DATA_TEST_IDS.components.appointmentAddressDiff.component, "geoloc-empty"),
      geolocAddress: (): string =>
        joinDataTestId(DATA_TEST_IDS.components.appointmentAddressDiff.component, "geoloc-address"),
      geolocZipAndCity: (): string =>
        joinDataTestId(DATA_TEST_IDS.components.appointmentAddressDiff.component, "geoloc-zip-city"),
      geolocCountry: (): string =>
        joinDataTestId(DATA_TEST_IDS.components.appointmentAddressDiff.component, "geoloc-country"),
    },
    appointmentBookingFormCard: {
      component: "appointment-booking-form-card",
      cardHeader: (): string =>
        joinDataTestId(DATA_TEST_IDS.components.appointmentBookingFormCard.component, "card-header"),
      cardTitle: (): string =>
        joinDataTestId(DATA_TEST_IDS.components.appointmentBookingFormCard.component, "card-title"),
      totalEligibleApplianceNumberError: (): string =>
        joinDataTestId(
          DATA_TEST_IDS.components.appointmentBookingFormCard.component,
          "total-eligible-appliance-number-error",
        ),
      title: (): string => joinDataTestId(DATA_TEST_IDS.components.appointmentBookingFormCard.component, "title"),
      form: (): string => joinDataTestId(DATA_TEST_IDS.components.appointmentBookingFormCard.component, "form"),
      eligibleApplianceNumberLabel: (): string =>
        joinDataTestId(
          DATA_TEST_IDS.components.appointmentBookingFormCard.component,
          "eligible-appliance-number-label",
        ),
      totalEligibleApplianceNumberLabel: (): string =>
        joinDataTestId(
          DATA_TEST_IDS.components.appointmentBookingFormCard.component,
          "total-eligible-appliance-number-label",
        ),
      applianceEstimateField: (code: EApplianceEstimateCode) =>
        joinDataTestId(DATA_TEST_IDS.components.appointmentBookingFormCard.component, "appliance-estimate-field", code),

      heatPumpBrand: (): string =>
        joinDataTestId(DATA_TEST_IDS.components.appointmentBookingFormCard.component, "heat-pump-brand"),
      heatPumpModel: (): string =>
        joinDataTestId(DATA_TEST_IDS.components.appointmentBookingFormCard.component, "heat-pump-model"),

      appointmentTypeLabel: (): string =>
        joinDataTestId(DATA_TEST_IDS.components.appointmentBookingFormCard.component, "appointment-type-label"),
      serviceProviderLabel: (): string =>
        joinDataTestId(DATA_TEST_IDS.components.appointmentBookingFormCard.component, "service-provider-label"),
      technicianInstructionsLabel: (): string =>
        joinDataTestId(DATA_TEST_IDS.components.appointmentBookingFormCard.component, "technician-instructions-label"),
      technicianInstructionsModifyBtn: (): string =>
        joinDataTestId(
          DATA_TEST_IDS.components.appointmentBookingFormCard.component,
          "technician-instructions-modify-btn",
        ),
      technicianInstructionsLoadingSkeleton: (): string =>
        joinDataTestId(
          DATA_TEST_IDS.components.appointmentBookingFormCard.component,
          "technician-instructions-loading-skeleton",
        ),
      hasESCSignatureInProgressMessage: (): string =>
        joinDataTestId(
          DATA_TEST_IDS.components.appointmentBookingFormCard.component,
          "has-esc-signature-in-progress-message",
        ),
      hasESCSignatureInProgressContinueFlowButton: (): string =>
        joinDataTestId(
          DATA_TEST_IDS.components.appointmentBookingFormCard.component,
          "has-esc-signature-in-progress-continue-flow-button",
        ),
      hasEscEligibilityAndNoSignatureInProgressMessage: (): string =>
        joinDataTestId(
          DATA_TEST_IDS.components.appointmentBookingFormCard.component,
          "has-esc-eligibility-and-no-signature-in-progress-message",
        ),
      hasEscEligibilityAndNoSignatureInProgressContinueFlowButton: (): string =>
        joinDataTestId(
          DATA_TEST_IDS.components.appointmentBookingFormCard.component,
          "has-esc-eligibility-and-no-signature-in-progress-continue-flow-button",
        ),
      appointmentLoadingSkeleton: (): string =>
        joinDataTestId(DATA_TEST_IDS.components.appointmentBookingFormCard.component, "appointment-loading-skeleton"),
      hasAlreadyBookedAppointmentMessage: (): string =>
        joinDataTestId(
          DATA_TEST_IDS.components.appointmentBookingFormCard.component,
          "has-already-booked-appointment-message",
        ),
      hasOperationMessage: (): string =>
        joinDataTestId(DATA_TEST_IDS.components.appointmentBookingFormCard.component, "has-operation-message"),
      hasNoPartnerAddressMessage: (): string =>
        joinDataTestId(DATA_TEST_IDS.components.appointmentBookingFormCard.component, "has-no-partner-address-message"),
      isHeatPumpNotEligibleMessage: (): string =>
        joinDataTestId(
          DATA_TEST_IDS.components.appointmentBookingFormCard.component,
          "is-heat-pump-not-eligible-message",
        ),
      hasHeatPumpToQualifyMessage: (): string =>
        joinDataTestId(
          DATA_TEST_IDS.components.appointmentBookingFormCard.component,
          "has-heat-pump-to-qualify-message",
        ),
      hasNoPhonesMessage: (): string =>
        joinDataTestId(DATA_TEST_IDS.components.appointmentBookingFormCard.component, "has-no-phones-message"),
      hasNoMailMessage: (): string =>
        joinDataTestId(DATA_TEST_IDS.components.appointmentBookingFormCard.component, "has-no-mail-message"),
      hasBookedAppointmentCompromisedMessage: (): string =>
        joinDataTestId(
          DATA_TEST_IDS.components.appointmentBookingFormCard.component,
          "has-booked-appointment-compromised-message",
        ),
      isSiteTypeLayoutUnknownMessage: (): string =>
        joinDataTestId(
          DATA_TEST_IDS.components.appointmentBookingFormCard.component,
          "is-site-type-layout-unknown-message",
        ),
      appointmentAddressDiff: (): string =>
        joinDataTestId(DATA_TEST_IDS.components.appointmentBookingFormCard.component, "appointment-address-diff"),
      heatPumpBrandInput: (): string =>
        joinDataTestId(DATA_TEST_IDS.components.appointmentBookingFormCard.component, "heat-pump-brand-input"),
      heatPumpModelInput: (): string =>
        joinDataTestId(DATA_TEST_IDS.components.appointmentBookingFormCard.component, "heat-pump-model-input"),
      editHeatPumpDeclarationButton: (): string =>
        joinDataTestId(
          DATA_TEST_IDS.components.appointmentBookingFormCard.component,
          "edit-heat-pump-declaration-button",
        ),
      searchTypeLabel: (): string =>
        joinDataTestId(DATA_TEST_IDS.components.appointmentBookingFormCard.component, "search-type-label"),
      searchTypeAutomaticRadio: (): string =>
        joinDataTestId(DATA_TEST_IDS.components.appointmentBookingFormCard.component, "search-type-automatic-radio"),
      searchTypeManualRadio: (): string =>
        joinDataTestId(DATA_TEST_IDS.components.appointmentBookingFormCard.component, "search-type-manual-radio"),
      searchBtn: (): string =>
        joinDataTestId(DATA_TEST_IDS.components.appointmentBookingFormCard.component, "search-btn"),
      appointmentTypeDropdown: (): string =>
        joinDataTestId(DATA_TEST_IDS.components.appointmentBookingFormCard.component, "appointment-type-dropdown"),
      appointmentTypeList: (): string =>
        joinDataTestId(DATA_TEST_IDS.components.appointmentBookingFormCard.component, "appointment-type-list"),

      appointmentTypeOption: (type: EAppointmentType): string =>
        joinDataTestId(
          DATA_TEST_IDS.components.appointmentBookingFormCard.component,
          "appointment-type-option",
          String(type),
        ),
      appointmentTypeError: (): string =>
        joinDataTestId(DATA_TEST_IDS.components.appointmentBookingFormCard.component, "appointment-type-error"),
      serviceProviderSelect: (): string =>
        joinDataTestId(DATA_TEST_IDS.components.appointmentBookingFormCard.component, "service-provider-select"),
      serviceProviderError: (): string =>
        joinDataTestId(DATA_TEST_IDS.components.appointmentBookingFormCard.component, "service-provider-error"),
      technicianInstructions: (): string =>
        joinDataTestId(DATA_TEST_IDS.components.appointmentBookingFormCard.component, "technician-instructions"),
      modifyTechnicianInstructionsButton: (): string =>
        joinDataTestId(
          DATA_TEST_IDS.components.appointmentBookingFormCard.component,
          "modify-technician-instructions-button",
        ),
      reinterventionCommentLoadingSkeleton: (): string =>
        joinDataTestId(
          DATA_TEST_IDS.components.appointmentBookingFormCard.component,
          "reintervention-comment-loading-skeleton",
        ),
      reinterventionTechnicianInstructions: (): string =>
        joinDataTestId(
          DATA_TEST_IDS.components.appointmentBookingFormCard.component,
          "reintervention-technician-instructions",
        ),
      siteReinterventionLink: (): string =>
        joinDataTestId(DATA_TEST_IDS.components.appointmentBookingFormCard.component, "site-reintervention-link"),
      technicianInstructionsError: (): string =>
        joinDataTestId(DATA_TEST_IDS.components.appointmentBookingFormCard.component, "technician-instructions-error"),
      reinterventionTechnicianInstructionsDate: (): string =>
        joinDataTestId(
          DATA_TEST_IDS.components.appointmentBookingFormCard.reinterventionTechnicianInstructions(),
          "date",
        ),
      reinterventionTechnicianInstructionsComment: (): string =>
        joinDataTestId(
          DATA_TEST_IDS.components.appointmentBookingFormCard.reinterventionTechnicianInstructions(),
          "comment",
        ),
    },
    appointmentHistoryItem: {
      component: "appointment-history-item",
      statusLabel: (): string =>
        joinDataTestId(DATA_TEST_IDS.components.appointmentHistoryItem.component, "status-label"),
      creationDate: (): string =>
        joinDataTestId(DATA_TEST_IDS.components.appointmentHistoryItem.component, "creation-date"),
      title: (): string => joinDataTestId(DATA_TEST_IDS.components.appointmentHistoryItem.component, "title"),
      dateRange: (): string => joinDataTestId(DATA_TEST_IDS.components.appointmentHistoryItem.component, "date-range"),
      confirmedDate: (): string =>
        joinDataTestId(DATA_TEST_IDS.components.appointmentHistoryItem.component, "confirmed-date"),
      provider: (): string => joinDataTestId(DATA_TEST_IDS.components.appointmentHistoryItem.component, "provider"),
      comment: (): string => joinDataTestId(DATA_TEST_IDS.components.appointmentHistoryItem.component, "comment"),
      menuBtn: (): string => joinDataTestId(DATA_TEST_IDS.components.appointmentHistoryItem.component, "menu-btn"),
    },
    appointmentHistoryCancelModal: {
      component: "appointment-history-cancel-modal",
      form: (): string => joinDataTestId(DATA_TEST_IDS.components.appointmentHistoryCancelModal.component, "form"),
      cancellingAppointmentDesc: (): string =>
        joinDataTestId(DATA_TEST_IDS.components.appointmentHistoryCancelModal.component, "cancelling-appointment-desc"),
      whatNextLabel: (): string =>
        joinDataTestId(DATA_TEST_IDS.components.appointmentHistoryCancelModal.component, "what-next-label"),
      reasonDropdown: (): string =>
        joinDataTestId(DATA_TEST_IDS.components.appointmentHistoryCancelModal.component, "reason-dropdown"),
      reasonTypeRadio: (): string =>
        joinDataTestId(DATA_TEST_IDS.components.appointmentHistoryCancelModal.component, "reason-type-radio"),
      commentLabel: (): string =>
        joinDataTestId(DATA_TEST_IDS.components.appointmentHistoryCancelModal.component, "comment-label"),
      cancelBtn: (): string =>
        joinDataTestId(DATA_TEST_IDS.components.appointmentHistoryCancelModal.component, "cancel-btn"),
      submitBtn: (): string =>
        joinDataTestId(DATA_TEST_IDS.components.appointmentHistoryCancelModal.component, "submit-btn"),
      reasonError: (): string =>
        joinDataTestId(DATA_TEST_IDS.components.appointmentHistoryCancelModal.component, "reason-error"),
      commentTextarea: (): string =>
        joinDataTestId(DATA_TEST_IDS.components.appointmentHistoryCancelModal.component, "comment-textarea"),
      commentError: (): string =>
        joinDataTestId(DATA_TEST_IDS.components.appointmentHistoryCancelModal.component, "comment-error"),
    },
    appointmentEscSignupModal: {
      component: "appointment-esc-signup-modal",
      loadingSkeleton: (): string =>
        joinDataTestId(DATA_TEST_IDS.components.appointmentEscSignupModal.component, "loading-skeleton"),
      actionStep: (): string =>
        joinDataTestId(DATA_TEST_IDS.components.appointmentEscSignupModal.component, "action-step"),
      otherActionBtn: (): string =>
        joinDataTestId(DATA_TEST_IDS.components.appointmentEscSignupModal.component, "other-action-btn"),
      startSignatureBtn: (): string =>
        joinDataTestId(DATA_TEST_IDS.components.appointmentEscSignupModal.component, "start-signature-btn"),
      signStep: (): string => joinDataTestId(DATA_TEST_IDS.components.appointmentEscSignupModal.component, "sign-step"),
      signStepMessage: (): string =>
        joinDataTestId(DATA_TEST_IDS.components.appointmentEscSignupModal.component, "sign-step-message"),
      yousignLink: (): string =>
        joinDataTestId(DATA_TEST_IDS.components.appointmentEscSignupModal.component, "yousign-link"),
      checkSignatureBtn: (): string =>
        joinDataTestId(DATA_TEST_IDS.components.appointmentEscSignupModal.component, "check-signature-btn"),
    },
    appointmentBookingAvailabilities: {
      component: "appointment-booking-availabilities",
      flexMessage: (): string =>
        joinDataTestId(DATA_TEST_IDS.components.appointmentBookingAvailabilities.component, "flex-message"),
      progressSpinner: (): string =>
        joinDataTestId(DATA_TEST_IDS.components.appointmentBookingAvailabilities.component, "progress-spinner"),
      flexBookBtn: (): string =>
        joinDataTestId(DATA_TEST_IDS.components.appointmentBookingAvailabilities.component, "flex-book-btn"),
      previousMonthBtn: (): string =>
        joinDataTestId(DATA_TEST_IDS.components.appointmentBookingAvailabilities.component, "previous-month-btn"),
      monthTitle: (): string =>
        joinDataTestId(DATA_TEST_IDS.components.appointmentBookingAvailabilities.component, "month-title"),
      nextMonthBtn: (): string =>
        joinDataTestId(DATA_TEST_IDS.components.appointmentBookingAvailabilities.component, "next-month-btn"),
      loadingContainer: (): string =>
        joinDataTestId(DATA_TEST_IDS.components.appointmentBookingAvailabilities.component, "loading-container"),
      errorContainer: (): string =>
        joinDataTestId(DATA_TEST_IDS.components.appointmentBookingAvailabilities.component, "error-container"),
      calendar: (): string =>
        joinDataTestId(DATA_TEST_IDS.components.appointmentBookingAvailabilities.component, "calendar"),
    },
    appointmentBookingBookSlot: {
      component: "appointment-booking-book-slot",
      pendingContainer: (): string =>
        joinDataTestId(DATA_TEST_IDS.components.appointmentBookingBookSlot.component, "pending-container"),
      doneContainer: (): string =>
        joinDataTestId(DATA_TEST_IDS.components.appointmentBookingBookSlot.component, "done-container"),
      errorContainer: (): string =>
        joinDataTestId(DATA_TEST_IDS.components.appointmentBookingBookSlot.component, "error-container"),
      errorTitle: (): string =>
        joinDataTestId(DATA_TEST_IDS.components.appointmentBookingBookSlot.component, "error-title"),
      errorLabel: (): string =>
        joinDataTestId(DATA_TEST_IDS.components.appointmentBookingBookSlot.component, "error-label"),
    },
    appointmentBookingConfirmation: {
      component: "appointment-booking-confirmation",
      title: (): string => joinDataTestId(DATA_TEST_IDS.components.appointmentBookingConfirmation.component, "title"),
      commentTextarea: (): string =>
        joinDataTestId(DATA_TEST_IDS.components.appointmentBookingConfirmation.component, "comment-textarea"),
      typeRow: (): string =>
        joinDataTestId(DATA_TEST_IDS.components.appointmentBookingConfirmation.component, "type-row"),
      dateRow: (): string =>
        joinDataTestId(DATA_TEST_IDS.components.appointmentBookingConfirmation.component, "date-row"),
      slotRow: (): string =>
        joinDataTestId(DATA_TEST_IDS.components.appointmentBookingConfirmation.component, "slot-row"),
      providerRow: (): string =>
        joinDataTestId(DATA_TEST_IDS.components.appointmentBookingConfirmation.component, "provider-row"),
      tooCloseWarning: (): string =>
        joinDataTestId(DATA_TEST_IDS.components.appointmentBookingConfirmation.component, "too-close-warning"),
      commentLabel: (): string =>
        joinDataTestId(DATA_TEST_IDS.components.appointmentBookingConfirmation.component, "comment-label"),
      cancelBtn: (): string =>
        joinDataTestId(DATA_TEST_IDS.components.appointmentBookingConfirmation.component, "cancel-btn"),
      nextBtn: (): string =>
        joinDataTestId(DATA_TEST_IDS.components.appointmentBookingConfirmation.component, "next-btn"),
    },
    appointmentUpdateBookingFormCard: {
      component: "appointment-update-booking-form-card",
      cardHeader: (): string =>
        joinDataTestId(DATA_TEST_IDS.components.appointmentUpdateBookingFormCard.component, "card-header"),
      cardTitle: (): string =>
        joinDataTestId(DATA_TEST_IDS.components.appointmentUpdateBookingFormCard.component, "card-title"),
      cardContent: (): string =>
        joinDataTestId(DATA_TEST_IDS.components.appointmentUpdateBookingFormCard.component, "card-content"),
      searchAppointmentButton: (): string =>
        joinDataTestId(
          DATA_TEST_IDS.components.appointmentUpdateBookingFormCard.component,
          "search-appointment-button",
        ),
      setAppointmentButton: (): string =>
        joinDataTestId(DATA_TEST_IDS.components.appointmentUpdateBookingFormCard.component, "set-appointment-button"),
      searchTypeError: (): string =>
        joinDataTestId(DATA_TEST_IDS.components.appointmentUpdateBookingFormCard.component, "search-type-error"),
      searchTypeAutomaticRadioButton: (): string =>
        joinDataTestId(
          DATA_TEST_IDS.components.appointmentUpdateBookingFormCard.component,
          "search-type-automatic-radio-button",
        ),
      searchTypeManualRadioButton: (): string =>
        joinDataTestId(
          DATA_TEST_IDS.components.appointmentUpdateBookingFormCard.component,
          "search-type-manual-radio-button",
        ),
      reinterventionCommentSkeleton: (): string =>
        joinDataTestId(
          DATA_TEST_IDS.components.appointmentUpdateBookingFormCard.component,
          "reintervention-comment-skeleton",
        ),
      modifyTechnicianInstructionsButton: (): string =>
        joinDataTestId(
          DATA_TEST_IDS.components.appointmentUpdateBookingFormCard.component,
          "modify-technician-instructions-button",
        ),
      serviceProviderMultiSelect: (): string =>
        joinDataTestId(
          DATA_TEST_IDS.components.appointmentUpdateBookingFormCard.component,
          "service-provider-multi-select",
        ),
      updateReasonDropdown: (): string =>
        joinDataTestId(DATA_TEST_IDS.components.appointmentUpdateBookingFormCard.component, "update-reason-dropdown"),
      serviceProviderError: (): string =>
        joinDataTestId(DATA_TEST_IDS.components.appointmentUpdateBookingFormCard.component, "service-provider-error"),
      appointmentTypeDropdown: (): string =>
        joinDataTestId(
          DATA_TEST_IDS.components.appointmentUpdateBookingFormCard.component,
          "appointment-type-dropdown",
        ),
      appointmentTypeDropdownError: (): string =>
        joinDataTestId(
          DATA_TEST_IDS.components.appointmentUpdateBookingFormCard.component,
          "appointment-type-dropdown-error",
        ),
      updateReasonError: (): string =>
        joinDataTestId(DATA_TEST_IDS.components.appointmentUpdateBookingFormCard.component, "update-reason-error"),
      updateCommentTextarea: (): string =>
        joinDataTestId(DATA_TEST_IDS.components.appointmentUpdateBookingFormCard.component, "update-comment-textarea"),
      updateCommentTextareaError: (): string =>
        joinDataTestId(
          DATA_TEST_IDS.components.appointmentUpdateBookingFormCard.component,
          "update-comment-textarea-error",
        ),
      appointmentTypeLabel: (): string =>
        joinDataTestId(DATA_TEST_IDS.components.appointmentUpdateBookingFormCard.component, "appointment-type-label"),
      form: (): string => joinDataTestId(DATA_TEST_IDS.components.appointmentUpdateBookingFormCard.component, "form"),
      updateReasonLabel: (): string =>
        joinDataTestId(DATA_TEST_IDS.components.appointmentUpdateBookingFormCard.component, "update-reason-label"),
      commentLabel: (): string =>
        joinDataTestId(DATA_TEST_IDS.components.appointmentUpdateBookingFormCard.component, "comment-label"),
      eligibleApplianceNumberLabel: (): string =>
        joinDataTestId(
          DATA_TEST_IDS.components.appointmentUpdateBookingFormCard.component,
          "eligible-appliance-number-label",
        ),
      eligibleApplianceInputNumber: (): string =>
        joinDataTestId(
          DATA_TEST_IDS.components.appointmentUpdateBookingFormCard.component,
          "eligible-appliance-input-number",
        ),
      eligibleApplianceInputNumberError: (): string =>
        joinDataTestId(
          DATA_TEST_IDS.components.appointmentUpdateBookingFormCard.component,
          "eligible-appliance-input-number-error",
        ),
      serviceProviderLabel: (): string =>
        joinDataTestId(DATA_TEST_IDS.components.appointmentUpdateBookingFormCard.component, "service-provider-label"),
      technicianInstructionsLabel: (): string =>
        joinDataTestId(
          DATA_TEST_IDS.components.appointmentUpdateBookingFormCard.component,
          "technician-instructions-label",
        ),
      technicianInstructionsContainer: (): string =>
        joinDataTestId(
          DATA_TEST_IDS.components.appointmentUpdateBookingFormCard.component,
          "technician-instructions-container",
        ),
      technicianInstructionsModifyBtn: (): string =>
        joinDataTestId(
          DATA_TEST_IDS.components.appointmentUpdateBookingFormCard.component,
          "technician-instructions-modify-btn",
        ),
      technicianInstructionsLoadingSkeleton: (): string =>
        joinDataTestId(
          DATA_TEST_IDS.components.appointmentUpdateBookingFormCard.component,
          "technician-instructions-loading-skeleton",
        ),
      searchTypeLabel: (): string =>
        joinDataTestId(DATA_TEST_IDS.components.appointmentUpdateBookingFormCard.component, "search-type-label"),
      searchTypeAutomaticRadio: (): string =>
        joinDataTestId(
          DATA_TEST_IDS.components.appointmentUpdateBookingFormCard.component,
          "search-type-automatic-radio",
        ),
      searchTypeManualRadio: (): string =>
        joinDataTestId(DATA_TEST_IDS.components.appointmentUpdateBookingFormCard.component, "search-type-manual-radio"),
      cancelBtn: (): string =>
        joinDataTestId(DATA_TEST_IDS.components.appointmentUpdateBookingFormCard.component, "cancel-btn"),
      searchBtn: (): string =>
        joinDataTestId(DATA_TEST_IDS.components.appointmentUpdateBookingFormCard.component, "search-btn"),
    },
    organizationSupportRequestForm: {
      component: "organization-support-request-form",
      form: (): string => joinDataTestId(DATA_TEST_IDS.components.organizationSupportRequestForm.component, "form"),
      siteIdDropdown: (): string =>
        joinDataTestId(DATA_TEST_IDS.components.organizationSupportRequestForm.component, "site-id-dropdown"),
      phoneNumberInputText: (): string =>
        joinDataTestId(DATA_TEST_IDS.components.organizationSupportRequestForm.component, "phone-number-input-text"),
      phoneNumberError: (): string =>
        joinDataTestId(DATA_TEST_IDS.components.organizationSupportRequestForm.component, "phone-number-error"),
      emailError: (): string =>
        joinDataTestId(DATA_TEST_IDS.components.organizationSupportRequestForm.component, "email-error"),
      descriptionError: (): string =>
        joinDataTestId(DATA_TEST_IDS.components.organizationSupportRequestForm.component, "description-error"),
      siteIdError: (): string =>
        joinDataTestId(DATA_TEST_IDS.components.organizationSupportRequestForm.component, "site-id-error"),
      emailInputText: (): string =>
        joinDataTestId(DATA_TEST_IDS.components.organizationSupportRequestForm.component, "email-input-text"),
      descriptionInputText: (): string =>
        joinDataTestId(DATA_TEST_IDS.components.organizationSupportRequestForm.component, "description-input-text"),
      submitBtn: (): string =>
        joinDataTestId(DATA_TEST_IDS.components.organizationSupportRequestForm.component, "submit-btn"),
    },
    organizationPilotOversightForm: {
      component: "organization-pilot-oversight-form",
      form: (): string => joinDataTestId(DATA_TEST_IDS.components.organizationPilotOversightForm.component, "form"),
      siteIdDropdown: (): string =>
        joinDataTestId(DATA_TEST_IDS.components.organizationPilotOversightForm.component, "site-id-dropdown"),
      authorizedPilotingRadio: (): string =>
        joinDataTestId(DATA_TEST_IDS.components.organizationPilotOversightForm.component, "authorized-piloting-radio"),
      notAuthorizedPilotingRadio: (): string =>
        joinDataTestId(
          DATA_TEST_IDS.components.organizationPilotOversightForm.component,
          "not-authorized-piloting-radio",
        ),
      siteIdError: (): string =>
        joinDataTestId(DATA_TEST_IDS.components.organizationPilotOversightForm.component, "site-id-error"),
      submitBtn: (): string =>
        joinDataTestId(DATA_TEST_IDS.components.organizationPilotOversightForm.component, "submit-btn"),
    },
    organizationSupportRequestCard: {
      component: "organization-support-request-card",
      organizationSupportRequestForm: (): string =>
        joinDataTestId(
          DATA_TEST_IDS.components.organizationSupportRequestCard.component,
          "organization-support-request-form",
        ),
      cardTitle: (): string =>
        joinDataTestId(DATA_TEST_IDS.components.organizationSupportRequestCard.component, "card-title"),
      cardContent: (): string =>
        joinDataTestId(DATA_TEST_IDS.components.organizationSupportRequestCard.component, "cardContent"),
      cardHeader: (): string =>
        joinDataTestId(DATA_TEST_IDS.components.organizationSupportRequestCard.component, "card-header"),
    },
    selectCountry: {
      component: "select-country",
      countryItemValue: () => joinDataTestId(DATA_TEST_IDS.components.selectCountry.component, "country-item-value"),
      countryItemOptionValue: () =>
        joinDataTestId(DATA_TEST_IDS.components.selectCountry.component, "country-item-option-value"),
    },
    hasReachedMaxInterventionLimitWarning: {
      component: "has-reached-max-intervention-limit-warning",
    },
    organizationHospitalityAccessButton: {
      component: "organization-hospitality-access-button",
      button: (): string =>
        joinDataTestId(DATA_TEST_IDS.components.organizationHospitalityAccessButton.component, "button"),
      confirmDialog: (): string =>
        joinDataTestId(DATA_TEST_IDS.components.organizationHospitalityAccessButton.component, "confirm-dialog"),
      confirmMessage: (): string =>
        joinDataTestId(DATA_TEST_IDS.components.organizationHospitalityAccessButton.component, "confirm-message"),
    },
    organizationSummaryTextCopyToClipboard: {
      component: "organization-summary-text-copy-to-clipboard",
      link: (): string =>
        joinDataTestId(DATA_TEST_IDS.components.organizationSummaryTextCopyToClipboard.component, "link"),
      content: (): string =>
        joinDataTestId(DATA_TEST_IDS.components.organizationSummaryTextCopyToClipboard.component, "content"),
      copyBtn: (): string =>
        joinDataTestId(DATA_TEST_IDS.components.organizationSummaryTextCopyToClipboard.component, "copy-btn"),
    },
    organizationSitesTable: {
      component: "organization-sites-table",
      table: (): string => joinDataTestId(DATA_TEST_IDS.components.organizationSitesTable.component, "table"),
      tableRowName: (): string =>
        joinDataTestId(DATA_TEST_IDS.components.organizationSitesTable.component, "table-row-name"),
      tableRowNumDevices: (): string =>
        joinDataTestId(DATA_TEST_IDS.components.organizationSitesTable.component, "table-row-num-devices"),
      tableRowOrganizationId: (): string =>
        joinDataTestId(DATA_TEST_IDS.components.organizationSitesTable.component, "table-row-organization-id"),
      tableRowOrganizationName: (): string =>
        joinDataTestId(DATA_TEST_IDS.components.organizationSitesTable.component, "table-row-organization-name"),
      cardHeader: (): string =>
        joinDataTestId(DATA_TEST_IDS.components.organizationSitesTable.component, "card-header"),
      cardTitle: (): string => joinDataTestId(DATA_TEST_IDS.components.organizationSitesTable.component, "card-title"),
      cardContent: (): string =>
        joinDataTestId(DATA_TEST_IDS.components.organizationSitesTable.component, "card-content"),
    },
    pilotOversightCard: {
      component: "pilot-oversight-card",
      pilotOversightForm: (): string =>
        joinDataTestId(DATA_TEST_IDS.components.pilotOversightCard.component, "pilot-oversight-form"),
      cardHeader: (): string => joinDataTestId(DATA_TEST_IDS.components.pilotOversightCard.component, "card-header"),
      cardTitle: (): string => joinDataTestId(DATA_TEST_IDS.components.pilotOversightCard.component, "card-title"),
      cardContent: (): string => joinDataTestId(DATA_TEST_IDS.components.pilotOversightCard.component, "card-content"),
    },
    personalizationComfortHistoryItem: {
      component: "personalization-comfort-history-item",
      statusLabel: (): string =>
        joinDataTestId(DATA_TEST_IDS.components.personalizationComfortHistoryItem.component, "status-label"),
      creationDate: (): string =>
        joinDataTestId(DATA_TEST_IDS.components.personalizationComfortHistoryItem.component, "creation-date"),
      title: (): string =>
        joinDataTestId(DATA_TEST_IDS.components.personalizationComfortHistoryItem.component, "title"),
      dateRange: (): string =>
        joinDataTestId(DATA_TEST_IDS.components.personalizationComfortHistoryItem.component, "date-range"),
      cancelledDate: (): string =>
        joinDataTestId(DATA_TEST_IDS.components.personalizationComfortHistoryItem.component, "cancelled-date"),
      creatorLabel: (): string =>
        joinDataTestId(DATA_TEST_IDS.components.personalizationComfortHistoryItem.component, "creator-label"),
      comment: (): string =>
        joinDataTestId(DATA_TEST_IDS.components.personalizationComfortHistoryItem.component, "comment"),
      menuBtn: (): string =>
        joinDataTestId(DATA_TEST_IDS.components.personalizationComfortHistoryItem.component, "menu-btn"),
    },
    personalizationComfortHistoryCard: {
      component: "personalization-comfort-history-card",
      cardTitle: (): string =>
        joinDataTestId(DATA_TEST_IDS.components.personalizationComfortHistoryCard.component, "card-title"),
      cardHeader: (): string =>
        joinDataTestId(DATA_TEST_IDS.components.personalizationComfortHistoryCard.component, "card-header"),
      cardContent: (): string =>
        joinDataTestId(DATA_TEST_IDS.components.personalizationComfortHistoryCard.component, "card-content"),
      emptyTitle: (): string =>
        joinDataTestId(DATA_TEST_IDS.components.personalizationComfortHistoryCard.component, "empty-title"),
      emptyDesc: (): string =>
        joinDataTestId(DATA_TEST_IDS.components.personalizationComfortHistoryCard.component, "empty-desc"),
      skeleton: (): string =>
        joinDataTestId(DATA_TEST_IDS.components.personalizationComfortHistoryCard.component, "skeleton"),
      timeline: (): string =>
        joinDataTestId(DATA_TEST_IDS.components.personalizationComfortHistoryCard.component, "timeline"),
      personalizationComfortHistoryItem: (): string =>
        joinDataTestId(
          DATA_TEST_IDS.components.personalizationComfortHistoryCard.component,
          "personalization-comfort-history-item",
        ),
    },
    personalizationComfortFormCard: {
      component: "personalization-comfort-form-card",
      cardHeader: (): string =>
        joinDataTestId(DATA_TEST_IDS.components.personalizationComfortFormCard.component, "card-header"),
      cardTitle: (): string =>
        joinDataTestId(DATA_TEST_IDS.components.personalizationComfortFormCard.component, "card-title"),
      cardContent: (): string =>
        joinDataTestId(DATA_TEST_IDS.components.personalizationComfortFormCard.component, "card-content"),
      personalizationComfortForm: (): string =>
        joinDataTestId(
          DATA_TEST_IDS.components.personalizationComfortFormCard.component,
          "personalization-comfort-form",
        ),
    },
    personalizationComfortForm: {
      component: "personalization-comfort-form",
    },
    deliveryPointManualUpdateForm: {
      component: "delivery-point-manual-update-form",
      form: (): string => joinDataTestId(DATA_TEST_IDS.components.deliveryPointManualUpdateForm.component, "form"),
      deliveryPointLabel: (): string =>
        joinDataTestId(DATA_TEST_IDS.components.deliveryPointManualUpdateForm.component, "delivery-point-label"),
      deliveryPointInputText: (): string =>
        joinDataTestId(DATA_TEST_IDS.components.deliveryPointManualUpdateForm.component, "delivery-point-input-text"),
      deliveryPointError: (): string =>
        joinDataTestId(DATA_TEST_IDS.components.deliveryPointManualUpdateForm.component, "delivery-point-error"),
      submitBtn: (): string =>
        joinDataTestId(DATA_TEST_IDS.components.deliveryPointManualUpdateForm.component, "submit-btn"),
    },
    deliveryPointSiteInfoMessage: {
      component: "delivery-point-site-info-message",
      title: (): string => joinDataTestId(DATA_TEST_IDS.components.deliveryPointSiteInfoMessage.component, "title"),
      subscriberTitle: (): string =>
        joinDataTestId(DATA_TEST_IDS.components.deliveryPointSiteInfoMessage.component, "subscriber-title"),
      subscriberName: (): string =>
        joinDataTestId(DATA_TEST_IDS.components.deliveryPointSiteInfoMessage.component, "subscriber-name"),
      address: (): string => joinDataTestId(DATA_TEST_IDS.components.deliveryPointSiteInfoMessage.component, "address"),
      postCodeCity: (): string =>
        joinDataTestId(DATA_TEST_IDS.components.deliveryPointSiteInfoMessage.component, "post-code-city"),
      country: (): string => joinDataTestId(DATA_TEST_IDS.components.deliveryPointSiteInfoMessage.component, "country"),
      pdlTitle: (): string =>
        joinDataTestId(DATA_TEST_IDS.components.deliveryPointSiteInfoMessage.component, "pdl-title"),
      pdlValue: (): string =>
        joinDataTestId(DATA_TEST_IDS.components.deliveryPointSiteInfoMessage.component, "pdl-value"),
    },
    deliveryPointSearchResultBtn: {
      component: "delivery-point-search-result-btn",
      contractOwner: (): string =>
        joinDataTestId(DATA_TEST_IDS.components.deliveryPointSearchResultBtn.component, "contract-owner"),
      pdl: (): string => joinDataTestId(DATA_TEST_IDS.components.deliveryPointSearchResultBtn.component, "pdl"),
      address: (): string => joinDataTestId(DATA_TEST_IDS.components.deliveryPointSearchResultBtn.component, "address"),
      addressContainer: (): string =>
        joinDataTestId(DATA_TEST_IDS.components.deliveryPointSearchResultBtn.component, "address-container"),
      addressComplementBuilding: (): string =>
        joinDataTestId(DATA_TEST_IDS.components.deliveryPointSearchResultBtn.component, "address-complement-building"),
      addressComplementLevel: (): string =>
        joinDataTestId(DATA_TEST_IDS.components.deliveryPointSearchResultBtn.component, "address-complement-level"),
      zipCode: (): string =>
        joinDataTestId(DATA_TEST_IDS.components.deliveryPointSearchResultBtn.component, "zip-code"),
    },
    deliveryPointSearchUpdateForm: {
      component: "delivery-point-search-update-form",
      form: (): string => joinDataTestId(DATA_TEST_IDS.components.deliveryPointSearchUpdateForm.component, "form"),
      lastnameLabel: (): string =>
        joinDataTestId(DATA_TEST_IDS.components.deliveryPointSearchUpdateForm.component, "lastname-label"),
      lastnameInput: (): string =>
        joinDataTestId(DATA_TEST_IDS.components.deliveryPointSearchUpdateForm.component, "lastname-input-text"),
      lastnameError: (): string =>
        joinDataTestId(DATA_TEST_IDS.components.deliveryPointSearchUpdateForm.component, "lastname-error"),
      addressLabel: (): string =>
        joinDataTestId(DATA_TEST_IDS.components.deliveryPointSearchUpdateForm.component, "address-label"),
      noResultsContainer: (): string =>
        joinDataTestId(DATA_TEST_IDS.components.deliveryPointSearchUpdateForm.component, "no-results-container"),
      deliveryPointSearchResultBtn: (): string =>
        joinDataTestId(
          DATA_TEST_IDS.components.deliveryPointSearchUpdateForm.component,
          "delivery-point-search-result-btn",
        ),
      updateDeliveryPointBtn: (): string =>
        joinDataTestId(DATA_TEST_IDS.components.deliveryPointSearchUpdateForm.component, "update-delivery-point-btn"),
      addressContainer: (): string =>
        joinDataTestId(DATA_TEST_IDS.components.deliveryPointSearchUpdateForm.component, "address-container"),
      addressInput: (): string =>
        joinDataTestId(DATA_TEST_IDS.components.deliveryPointSearchUpdateForm.component, "address-input-text"),
      addressError: (): string =>
        joinDataTestId(DATA_TEST_IDS.components.deliveryPointSearchUpdateForm.component, "address-error"),
      searchBtn: (): string =>
        joinDataTestId(DATA_TEST_IDS.components.deliveryPointSearchUpdateForm.component, "search-btn"),
      pendingDivider: (): string =>
        joinDataTestId(DATA_TEST_IDS.components.deliveryPointSearchUpdateForm.component, "pending-divider"),
      resultsTitle: (): string =>
        joinDataTestId(DATA_TEST_IDS.components.deliveryPointSearchUpdateForm.component, "results-title"),
      spinner: (): string =>
        joinDataTestId(DATA_TEST_IDS.components.deliveryPointSearchUpdateForm.component, "spinner"),
      resultsGrid: (): string =>
        joinDataTestId(DATA_TEST_IDS.components.deliveryPointSearchUpdateForm.component, "results-grid"),
      submitBtn: (): string =>
        joinDataTestId(DATA_TEST_IDS.components.deliveryPointSearchUpdateForm.component, "submit-btn"),
      emptyTitle: (): string =>
        joinDataTestId(DATA_TEST_IDS.components.deliveryPointSearchUpdateForm.component, "empty-title"),
      emptyDesc: (): string =>
        joinDataTestId(DATA_TEST_IDS.components.deliveryPointSearchUpdateForm.component, "empty-desc"),
    },
    deliveryPointAssignCard: {
      component: "delivery-point-assign-card",
      cardHeader: (): string =>
        joinDataTestId(DATA_TEST_IDS.components.deliveryPointAssignCard.component, "card-header"),
      cardTitle: (): string => joinDataTestId(DATA_TEST_IDS.components.deliveryPointAssignCard.component, "card-title"),
      tabView: (): string => joinDataTestId(DATA_TEST_IDS.components.deliveryPointAssignCard.component, "tab-view"),
      delivePointManualForm: (): string =>
        joinDataTestId(DATA_TEST_IDS.components.deliveryPointAssignCard.component, "delivery-point-manual-form"),
      searchTab: (): string => joinDataTestId(DATA_TEST_IDS.components.deliveryPointAssignCard.component, "search-tab"),
      delivePointSearchForm: (): string =>
        joinDataTestId(DATA_TEST_IDS.components.deliveryPointAssignCard.component, "delivery-point-search-form"),
      manualTab: (): string => joinDataTestId(DATA_TEST_IDS.components.deliveryPointAssignCard.component, "manual-tab"),
    },
    operationCascadeSelect: {
      component: "operation-cascade-select",
      loadingSkeleton: (): string =>
        joinDataTestId(DATA_TEST_IDS.components.operationCascadeSelect.component, "loading-skeleton"),
    },
    operationEditCard: {
      component: "operation-edit-card",
      form: (): string => joinDataTestId(DATA_TEST_IDS.components.operationEditCard.component, "form"),
      cardHeader: (): string => joinDataTestId(DATA_TEST_IDS.components.operationEditCard.component, "card-header"),
      cardTitle: (): string => joinDataTestId(DATA_TEST_IDS.components.operationEditCard.component, "card-title"),
      cardContent: (): string => joinDataTestId(DATA_TEST_IDS.components.operationEditCard.component, "card-content"),
      operationLabel: (): string =>
        joinDataTestId(DATA_TEST_IDS.components.operationEditCard.component, "operation-label"),
      operationDropdown: (): string =>
        joinDataTestId(DATA_TEST_IDS.components.operationEditCard.component, "operation-dropdown"),
      operationOption: (operationType: string): string =>
        joinDataTestId(DATA_TEST_IDS.components.operationEditCard.component, "operation-option", operationType),
      operationError: (): string =>
        joinDataTestId(DATA_TEST_IDS.components.operationEditCard.component, "operation-error"),
      missionLabel: (): string => joinDataTestId(DATA_TEST_IDS.components.operationEditCard.component, "mission-label"),
      missionDropdown: (): string =>
        joinDataTestId(DATA_TEST_IDS.components.operationEditCard.component, "mission-dropdown"),
      missionPanel: (): string => joinDataTestId(DATA_TEST_IDS.components.operationEditCard.component, "mission-panel"),
      missionOption: (missionId: number | string): string =>
        joinDataTestId(DATA_TEST_IDS.components.operationEditCard.component, "mission-option", String(missionId)),
      missionError: (): string => joinDataTestId(DATA_TEST_IDS.components.operationEditCard.component, "mission-error"),
      cancelBtn: (): string => joinDataTestId(DATA_TEST_IDS.components.operationEditCard.component, "cancel-btn"),
      submitBtn: (): string => joinDataTestId(DATA_TEST_IDS.components.operationEditCard.component, "submit-btn"),
      loadingSkeleton: (): string =>
        joinDataTestId(DATA_TEST_IDS.components.operationEditCard.component, "loading-skeleton"),
    },
    sensorLinkedToDeviceCountCard: {
      component: "sensor-linked-to-device-count-card",
      title: (): string => joinDataTestId(DATA_TEST_IDS.components.sensorLinkedToDeviceCountCard.component, "title"),
      description: (): string =>
        joinDataTestId(DATA_TEST_IDS.components.sensorLinkedToDeviceCountCard.component, "description"),
      countValue: (): string =>
        joinDataTestId(DATA_TEST_IDS.components.sensorLinkedToDeviceCountCard.component, "count-value"),
      loadingSkeleton: (): string =>
        joinDataTestId(DATA_TEST_IDS.components.sensorLinkedToDeviceCountCard.component, "loading-skeleton"),
      cardContent: (): string =>
        joinDataTestId(DATA_TEST_IDS.components.sensorLinkedToDeviceCountCard.component, "card-content"),
      cardHeader: (): string =>
        joinDataTestId(DATA_TEST_IDS.components.sensorLinkedToDeviceCountCard.component, "card-header"),
    },
    sensorRequestFormCard: {
      component: "sensor-request-form-card",
      cardTitle: (): string => joinDataTestId(DATA_TEST_IDS.components.sensorRequestFormCard.component, "card-title"),
      modulatorCheckbox: (): string =>
        joinDataTestId(DATA_TEST_IDS.components.sensorRequestFormCard.component, "modulator-checkbox"),
      modulatorNameLabel: (): string =>
        joinDataTestId(DATA_TEST_IDS.components.sensorRequestFormCard.component, "modulator-name-label"),
      cardDescription: (): string =>
        joinDataTestId(DATA_TEST_IDS.components.sensorRequestFormCard.component, "card-description"),
      cardHeader: (): string => joinDataTestId(DATA_TEST_IDS.components.sensorRequestFormCard.component, "card-header"),
      cardContent: (): string =>
        joinDataTestId(DATA_TEST_IDS.components.sensorRequestFormCard.component, "card-content"),
      warning: (): string => joinDataTestId(DATA_TEST_IDS.components.sensorRequestFormCard.component, "warning"),
      form: (): string => joinDataTestId(DATA_TEST_IDS.components.sensorRequestFormCard.component, "form"),
      numberLabel: (): string =>
        joinDataTestId(DATA_TEST_IDS.components.sensorRequestFormCard.component, "number-label"),
      numberInput: (): string =>
        joinDataTestId(DATA_TEST_IDS.components.sensorRequestFormCard.component, "number-input"),
      numberError: (): string =>
        joinDataTestId(DATA_TEST_IDS.components.sensorRequestFormCard.component, "number-error"),
      modulatorsError: (): string =>
        joinDataTestId(DATA_TEST_IDS.components.sensorRequestFormCard.component, "modulators-error"),
      modulatorsLabel: (): string =>
        joinDataTestId(DATA_TEST_IDS.components.sensorRequestFormCard.component, "modulators-label"),
      modulatorsEmptyMessage: (): string =>
        joinDataTestId(DATA_TEST_IDS.components.sensorRequestFormCard.component, "modulators-empty-message"),
      modulatorRow: (): string =>
        joinDataTestId(DATA_TEST_IDS.components.sensorRequestFormCard.component, "modulator-row"),
      commentsLabel: (): string =>
        joinDataTestId(DATA_TEST_IDS.components.sensorRequestFormCard.component, "comments-label"),
      commentsInput: (): string =>
        joinDataTestId(DATA_TEST_IDS.components.sensorRequestFormCard.component, "comments-input"),
      commentsError: (): string =>
        joinDataTestId(DATA_TEST_IDS.components.sensorRequestFormCard.component, "comments-error"),
      submitBtn: (): string => joinDataTestId(DATA_TEST_IDS.components.sensorRequestFormCard.component, "submit-btn"),
    },
    gatewayRequestFormCard: {
      component: "gateway-request-form-card",
      cardTitle: (): string => joinDataTestId(DATA_TEST_IDS.components.gatewayRequestFormCard.component, "card-title"),
      gatewayModelRadio: (): string =>
        joinDataTestId(DATA_TEST_IDS.components.gatewayRequestFormCard.component, "gateway-model-radio"),
      gatewayModelNameLabel: (): string =>
        joinDataTestId(DATA_TEST_IDS.components.gatewayRequestFormCard.component, "gateway-model-name-label"),
      cardDescription: (): string =>
        joinDataTestId(DATA_TEST_IDS.components.gatewayRequestFormCard.component, "card-description"),
      cardHeader: (): string =>
        joinDataTestId(DATA_TEST_IDS.components.gatewayRequestFormCard.component, "card-header"),
      cardContent: (): string =>
        joinDataTestId(DATA_TEST_IDS.components.gatewayRequestFormCard.component, "card-content"),
      warning: (): string => joinDataTestId(DATA_TEST_IDS.components.gatewayRequestFormCard.component, "warning"),
      form: (): string => joinDataTestId(DATA_TEST_IDS.components.gatewayRequestFormCard.component, "form"),
      numberLabel: (): string =>
        joinDataTestId(DATA_TEST_IDS.components.gatewayRequestFormCard.component, "number-label"),
      numberInput: (): string =>
        joinDataTestId(DATA_TEST_IDS.components.gatewayRequestFormCard.component, "number-input"),
      numberError: (): string =>
        joinDataTestId(DATA_TEST_IDS.components.gatewayRequestFormCard.component, "number-error"),
      gatewayModelError: (): string =>
        joinDataTestId(DATA_TEST_IDS.components.gatewayRequestFormCard.component, "gateway-model-error"),
      gatewayModelLabel: (): string =>
        joinDataTestId(DATA_TEST_IDS.components.gatewayRequestFormCard.component, "gateway-model-label"),
      gatewayModelEmptyMessage: (): string =>
        joinDataTestId(DATA_TEST_IDS.components.gatewayRequestFormCard.component, "gateway-model-empty-message"),
      gatewayModelRow: (): string =>
        joinDataTestId(DATA_TEST_IDS.components.gatewayRequestFormCard.component, "gateway-model-row"),
      commentsLabel: (): string =>
        joinDataTestId(DATA_TEST_IDS.components.gatewayRequestFormCard.component, "comments-label"),
      commentsInput: (): string =>
        joinDataTestId(DATA_TEST_IDS.components.gatewayRequestFormCard.component, "comments-input"),
      commentsError: (): string =>
        joinDataTestId(DATA_TEST_IDS.components.gatewayRequestFormCard.component, "comments-error"),
      submitBtn: (): string => joinDataTestId(DATA_TEST_IDS.components.gatewayRequestFormCard.component, "submit-btn"),
    },
    sensorActiveInSideCountCard: {
      component: "sensor-active-in-side-count-card",
      cardTitle: (): string =>
        joinDataTestId(DATA_TEST_IDS.components.sensorActiveInSideCountCard.component, "card-title"),
      cardHeader: (): string =>
        joinDataTestId(DATA_TEST_IDS.components.sensorActiveInSideCountCard.component, "card-header"),
      cardContent: (): string =>
        joinDataTestId(DATA_TEST_IDS.components.sensorActiveInSideCountCard.component, "card-content"),
      description: (): string =>
        joinDataTestId(DATA_TEST_IDS.components.sensorActiveInSideCountCard.component, "description"),
      countValue: (): string =>
        joinDataTestId(DATA_TEST_IDS.components.sensorActiveInSideCountCard.component, "count-value"),
      loadingSkeleton: (): string =>
        joinDataTestId(DATA_TEST_IDS.components.sensorActiveInSideCountCard.component, "loading-skeleton"),
    },
    sensorSentByCustomerServiceCountCard: {
      component: "sensor-sent-by-customer-service-count-card",
      title: (): string =>
        joinDataTestId(DATA_TEST_IDS.components.sensorSentByCustomerServiceCountCard.component, "title"),
      cardHeader: (): string =>
        joinDataTestId(DATA_TEST_IDS.components.sensorSentByCustomerServiceCountCard.component, "card-header"),
      cardContent: (): string =>
        joinDataTestId(DATA_TEST_IDS.components.sensorSentByCustomerServiceCountCard.component, "card-content"),
      description: (): string =>
        joinDataTestId(DATA_TEST_IDS.components.sensorSentByCustomerServiceCountCard.component, "description"),
      countValue: (): string =>
        joinDataTestId(DATA_TEST_IDS.components.sensorSentByCustomerServiceCountCard.component, "count-value"),
      loadingSkeleton: (): string =>
        joinDataTestId(DATA_TEST_IDS.components.sensorSentByCustomerServiceCountCard.component, "loading-skeleton"),
    },
    sensorRequestHistoryCard: {
      component: "sensor-request-history-card",
      title: (): string => joinDataTestId(DATA_TEST_IDS.components.sensorRequestHistoryCard.component, "title"),
      description: (): string =>
        joinDataTestId(DATA_TEST_IDS.components.sensorRequestHistoryCard.component, "description"),
      content: (): string => joinDataTestId(DATA_TEST_IDS.components.sensorRequestHistoryCard.component, "content"),
      emptyTitle: (): string =>
        joinDataTestId(DATA_TEST_IDS.components.sensorRequestHistoryCard.component, "empty-title"),
      emptyDesc: (): string =>
        joinDataTestId(DATA_TEST_IDS.components.sensorRequestHistoryCard.component, "empty-desc"),
      itemDate: (): string => joinDataTestId(DATA_TEST_IDS.components.sensorRequestHistoryCard.component, "item-date"),
      itemTitle: (): string =>
        joinDataTestId(DATA_TEST_IDS.components.sensorRequestHistoryCard.component, "item-title"),
      devicesBtn: (): string =>
        joinDataTestId(DATA_TEST_IDS.components.sensorRequestHistoryCard.component, "devices-btn"),
      commentBtn: (): string =>
        joinDataTestId(DATA_TEST_IDS.components.sensorRequestHistoryCard.component, "comment-btn"),
      appliancesList: (): string =>
        joinDataTestId(DATA_TEST_IDS.components.sensorRequestHistoryCard.component, "appliances-list"),
      commentContent: (): string =>
        joinDataTestId(DATA_TEST_IDS.components.sensorRequestHistoryCard.component, "comment-content"),
      dynamicOverlayPanelComment: (): string =>
        joinDataTestId(DATA_TEST_IDS.components.sensorRequestHistoryCard.component, "dynanmic-overlay-panel-comment"),
      applianceItem: (): string =>
        joinDataTestId(DATA_TEST_IDS.components.sensorRequestHistoryCard.component, "appliance-item"),
      dynamicOverlayPanel: (): string =>
        joinDataTestId(DATA_TEST_IDS.components.sensorRequestHistoryCard.component, "dynamic-overlay-panel"),
      timeline: (): string => joinDataTestId(DATA_TEST_IDS.components.sensorRequestHistoryCard.component, "timeline"),
      skeleton: (): string => joinDataTestId(DATA_TEST_IDS.components.sensorRequestHistoryCard.component, "skeleton"),
    },
    gatewayRequestHistoryCard: {
      component: "gateway-request-history-card",
      title: (): string => joinDataTestId(DATA_TEST_IDS.components.gatewayRequestHistoryCard.component, "title"),
      description: (): string =>
        joinDataTestId(DATA_TEST_IDS.components.gatewayRequestHistoryCard.component, "description"),
      content: (): string => joinDataTestId(DATA_TEST_IDS.components.gatewayRequestHistoryCard.component, "content"),
      emptyTitle: (): string =>
        joinDataTestId(DATA_TEST_IDS.components.gatewayRequestHistoryCard.component, "empty-title"),
      emptyDesc: (): string =>
        joinDataTestId(DATA_TEST_IDS.components.gatewayRequestHistoryCard.component, "empty-desc"),
      itemDate: (): string => joinDataTestId(DATA_TEST_IDS.components.gatewayRequestHistoryCard.component, "item-date"),
      itemTitle: (): string =>
        joinDataTestId(DATA_TEST_IDS.components.gatewayRequestHistoryCard.component, "item-title"),
      detailsBtn: (): string =>
        joinDataTestId(DATA_TEST_IDS.components.gatewayRequestHistoryCard.component, "details-btn"),
      commentBtn: (): string =>
        joinDataTestId(DATA_TEST_IDS.components.gatewayRequestHistoryCard.component, "comment-btn"),
      detailsList: (): string =>
        joinDataTestId(DATA_TEST_IDS.components.gatewayRequestHistoryCard.component, "details-list"),
      commentContent: (): string =>
        joinDataTestId(DATA_TEST_IDS.components.gatewayRequestHistoryCard.component, "comment-content"),
      dynamicOverlayPanelComment: (): string =>
        joinDataTestId(DATA_TEST_IDS.components.gatewayRequestHistoryCard.component, "dynamic-overlay-panel-comment"),
      detailItem: (): string =>
        joinDataTestId(DATA_TEST_IDS.components.gatewayRequestHistoryCard.component, "detail-item"),
      dynamicOverlayPanel: (): string =>
        joinDataTestId(DATA_TEST_IDS.components.gatewayRequestHistoryCard.component, "dynamic-overlay-panel"),
      timeline: (): string => joinDataTestId(DATA_TEST_IDS.components.gatewayRequestHistoryCard.component, "timeline"),
      skeleton: (): string => joinDataTestId(DATA_TEST_IDS.components.gatewayRequestHistoryCard.component, "skeleton"),
    },
    appointmentCalendar: appointmentCalendarDataTestIds,
    weekChip: weekChipDataTestIds,
    weekGrid: weekGridDataTestIds,
    slotButton: slotButtonDataTestIds,
    starsRating: starsRatingDataTestIds,
    kiliDataTableReaderDropdownFilter: {
      component: "kili-data-table-reader-dropdown-filter",
      loadingSkeleton: (): string =>
        joinDataTestId(DATA_TEST_IDS.components.kiliDataTableReaderDropdownFilter.component, "loading-skeleton"),
      autoComplete: (): string =>
        joinDataTestId(DATA_TEST_IDS.components.kiliDataTableReaderDropdownFilter.component, "auto-complete"),
    },
    kiliDataTableReader: {
      component: "kili-data-table-reader",
      table: (): string => joinDataTestId(DATA_TEST_IDS.components.kiliDataTableReader.component, "table"),
      header: (): string => joinDataTestId(DATA_TEST_IDS.components.kiliDataTableReader.component, "header"),
      refreshBtn: (): string => joinDataTestId(DATA_TEST_IDS.components.kiliDataTableReader.component, "refresh-btn"),
      toggleFiltersBtn: (): string =>
        joinDataTestId(DATA_TEST_IDS.components.kiliDataTableReader.component, "toggle-filters-btn"),
      resetFiltersBtn: (): string =>
        joinDataTestId(DATA_TEST_IDS.components.kiliDataTableReader.component, "reset-filters-btn"),
      searchBtn: (): string => joinDataTestId(DATA_TEST_IDS.components.kiliDataTableReader.component, "search-btn"),
      filtersContainer: (): string =>
        joinDataTestId(DATA_TEST_IDS.components.kiliDataTableReader.component, "filters-container"),
      paginator: (): string => joinDataTestId(DATA_TEST_IDS.components.kiliDataTableReader.component, "paginator"),
    },
    dropzone: {
      component: "dropzone",
      dragArea: (): string => joinDataTestId(DATA_TEST_IDS.components.dropzone.component, "drag-area"),
      dropZoneFile: (): string => joinDataTestId(DATA_TEST_IDS.components.dropzone.component, "drop-zone-file"),
      dynamicInput: (): string => joinDataTestId(DATA_TEST_IDS.components.dropzone.component, "dynamic-input"),
      fileDropZoneText: (): string =>
        joinDataTestId(DATA_TEST_IDS.components.dropzone.component, "file-drop-zone-text"),
      fileRejectionErrorFilename: (): string =>
        joinDataTestId(DATA_TEST_IDS.components.dropzone.component, "file-rejection-error-filename"),
      fileDragAndDropText: (): string =>
        joinDataTestId(DATA_TEST_IDS.components.dropzone.component, "file-drag-and-drop-text"),
      fileRejectionError: (): string =>
        joinDataTestId(DATA_TEST_IDS.components.dropzone.component, "file-rejection-error"),
      dropZoneError: (): string => joinDataTestId(DATA_TEST_IDS.components.dropzone.component, "drop-zone-error"),
      maxFileSizeLabel: (): string =>
        joinDataTestId(DATA_TEST_IDS.components.dropzone.component, "max-file-size-label"),
      authorizedTypesLabel: (): string =>
        joinDataTestId(DATA_TEST_IDS.components.dropzone.component, "authorized-types-label"),
      errorList: (): string => joinDataTestId(DATA_TEST_IDS.components.dropzone.component, "error-list"),
      clearAllBtn: (): string => joinDataTestId(DATA_TEST_IDS.components.dropzone.component, "clear-all-btn"),
      fileRow: (idx: string | number): string =>
        joinDataTestId(DATA_TEST_IDS.components.dropzone.component, "file-row", String(idx)),
    },
    dropzoneFile: {
      component: "dropzone-file",
      filename: (): string => joinDataTestId(DATA_TEST_IDS.components.dropzoneFile.component, "filename"),
      sizeLabel: (): string => joinDataTestId(DATA_TEST_IDS.components.dropzoneFile.component, "size-label"),
      removeBtn: (): string => joinDataTestId(DATA_TEST_IDS.components.dropzoneFile.component, "remove-btn"),
      progressBar: (): string => joinDataTestId(DATA_TEST_IDS.components.dropzoneFile.component, "progress-bar"),
    },
    markdownViewer: {
      component: "markdown-viewer",
    },
    leftNavbar: {
      component: "left-navbar",
      link: (): string => joinDataTestId(DATA_TEST_IDS.components.leftNavbar.component, "link"),
      toggleBtn: (): string => joinDataTestId(DATA_TEST_IDS.components.leftNavbar.component, "toggle-btn"),
      versionLabel: (): string => joinDataTestId(DATA_TEST_IDS.components.leftNavbar.component, "version-label"),
      dashboardIcon: (): string => joinDataTestId(DATA_TEST_IDS.components.leftNavbar.component, "dashboard-icon"),
      sitesIcon: (): string => joinDataTestId(DATA_TEST_IDS.components.leftNavbar.component, "sites-icon"),
      accommodationSitesIcon: (): string =>
        joinDataTestId(DATA_TEST_IDS.components.leftNavbar.component, "accommodation-sites-icon"),
      dashboardLabel: (): string => joinDataTestId(DATA_TEST_IDS.components.leftNavbar.component, "dashboard-label"),
      sitesLabel: (): string => joinDataTestId(DATA_TEST_IDS.components.leftNavbar.component, "sites-label"),
      accommodationSitesLabel: (): string =>
        joinDataTestId(DATA_TEST_IDS.components.leftNavbar.component, "accommodation-sites-label"),
    },
    baseHeader: {
      component: "base-header",
      logoLink: (): string => joinDataTestId(DATA_TEST_IDS.components.baseHeader.component, "logo-link"),
      logoImg: (): string => joinDataTestId(DATA_TEST_IDS.components.baseHeader.logoLink(), "img"),
      menuBtn: (): string => joinDataTestId(DATA_TEST_IDS.components.baseHeader.component, "menu-btn"),
      userMenuBtn: (): string => joinDataTestId(DATA_TEST_IDS.components.baseHeader.component, "user-menu-btn"),
      themeToggleBtn: (): string => joinDataTestId(DATA_TEST_IDS.components.baseHeader.component, "theme-toggle-btn"),
      closeCallBtn: (): string => joinDataTestId(DATA_TEST_IDS.components.baseHeader.component, "close-call-btn"),
      settingsBtn: () => joinDataTestId(DATA_TEST_IDS.components.baseHeader.component, "settings-btn"),
      logoutBtn: (): string => joinDataTestId(DATA_TEST_IDS.components.baseHeader.component, "logout-btn"),
    },
    livevxCard: {
      component: "livevx-card",
      label: (): string => joinDataTestId(DATA_TEST_IDS.components.livevxCard.component, "label"),
      cardHeader: (): string => joinDataTestId(DATA_TEST_IDS.components.livevxCard.component, "card-header"),
      cardTitle: (): string => joinDataTestId(DATA_TEST_IDS.components.livevxCard.component, "card-title"),
      cardContent: (): string => joinDataTestId(DATA_TEST_IDS.components.livevxCard.component, "card-content"),
      dropdown: (): string => joinDataTestId(DATA_TEST_IDS.components.livevxCard.component, "dropdown"),
      tabView: (): string => joinDataTestId(DATA_TEST_IDS.components.livevxCard.component, "tab-view"),
      tabRealtime: (): string => joinDataTestId(DATA_TEST_IDS.components.livevxCard.component, "tab-realtime"),
      tabHistory: (): string => joinDataTestId(DATA_TEST_IDS.components.livevxCard.component, "tab-history"),
    },
    livevxControlMonitoring: {
      component: "livevx-control-monitoring",
      skeleton: (): string => joinDataTestId(DATA_TEST_IDS.components.livevxControlMonitoring.component, "skeleton"),
      errorMessage: (): string =>
        joinDataTestId(DATA_TEST_IDS.components.livevxControlMonitoring.component, "error-message"),
      headerLabel: (): string =>
        joinDataTestId(DATA_TEST_IDS.components.livevxControlMonitoring.component, "header-label"),
      refreshBtn: (): string =>
        joinDataTestId(DATA_TEST_IDS.components.livevxControlMonitoring.component, "refresh-btn"),
      relayStateRow: (): string =>
        joinDataTestId(DATA_TEST_IDS.components.livevxControlMonitoring.component, "relay-state-row"),
      wireCommandModeRow: (): string =>
        joinDataTestId(DATA_TEST_IDS.components.livevxControlMonitoring.component, "wire-command-mode-row"),
      modulationProcessingLabel: (): string =>
        joinDataTestId(DATA_TEST_IDS.components.livevxControlMonitoring.component, "modulation-processing-label"),
      timeSlotFrom: (): string =>
        joinDataTestId(DATA_TEST_IDS.components.livevxControlMonitoring.component, "time-slot-from"),
      timeSlotTo: (): string =>
        joinDataTestId(DATA_TEST_IDS.components.livevxControlMonitoring.component, "time-slot-to"),
      originModulation: (): string =>
        joinDataTestId(DATA_TEST_IDS.components.livevxControlMonitoring.component, "origin-modulation"),
      noConsoleDiagAlert: (): string =>
        joinDataTestId(DATA_TEST_IDS.components.livevxControlMonitoring.component, "no-console-diag-alert"),
      autoTestingRow: (): string =>
        joinDataTestId(DATA_TEST_IDS.components.livevxControlMonitoring.component, "auto-testing-row"),
      activePowerSection: (): string =>
        joinDataTestId(DATA_TEST_IDS.components.livevxControlMonitoring.component, "active-power-section"),
      activePowerRow: (idx: string | number): string =>
        joinDataTestId(DATA_TEST_IDS.components.livevxControlMonitoring.component, "active-power-row", String(idx)),
      activePowerDate: (idx: string | number): string =>
        joinDataTestId(DATA_TEST_IDS.components.livevxControlMonitoring.component, "active-power-date", String(idx)),
      activePowerValue: (idx: string | number): string =>
        joinDataTestId(DATA_TEST_IDS.components.livevxControlMonitoring.component, "active-power-value", String(idx)),
    },
    livevxModulatorGraph: {
      component: "livevx-modulator-graph",
      calendar: (): string => joinDataTestId(DATA_TEST_IDS.components.livevxModulatorGraph.component, "calendar"),
      skeleton: (): string => joinDataTestId(DATA_TEST_IDS.components.livevxModulatorGraph.component, "skeleton"),
      errorMessage: (): string =>
        joinDataTestId(DATA_TEST_IDS.components.livevxModulatorGraph.component, "error-message"),
      chart: (): string => joinDataTestId(DATA_TEST_IDS.components.livevxModulatorGraph.component, "chart"),
      chartTooltip: (): string =>
        joinDataTestId(DATA_TEST_IDS.components.livevxModulatorGraph.component, "chart-tooltip"),
    },
    searchSiteModal: {
      component: "search-site-modal",
      container: (): string => joinDataTestId(DATA_TEST_IDS.components.searchSiteModal.component, "container"),
      searchInput: (): string => joinDataTestId(DATA_TEST_IDS.components.searchSiteModal.component, "search-input"),
      resultsContainer: (): string =>
        joinDataTestId(DATA_TEST_IDS.components.searchSiteModal.component, "results-container"),
      emptyMessage: (): string => joinDataTestId(DATA_TEST_IDS.components.searchSiteModal.component, "empty-message"),
      resultsCount: (): string => joinDataTestId(DATA_TEST_IDS.components.searchSiteModal.component, "results-count"),
      row: (): string => joinDataTestId(DATA_TEST_IDS.components.searchSiteModal.component, "row"),
      rowCell: (column: string): string =>
        joinDataTestId(DATA_TEST_IDS.components.searchSiteModal.component, "row", column),
    },
    heatpumpModelAutocomplete: {
      component: "heatpump-model-autocomplete",
      input: (): string => joinDataTestId(DATA_TEST_IDS.components.heatpumpModelAutocomplete.component, "input"),
      textInput: (): string =>
        joinDataTestId(DATA_TEST_IDS.components.heatpumpModelAutocomplete.component, "text-input"),
      option: (modelId: number): string =>
        joinDataTestId(DATA_TEST_IDS.components.heatpumpModelAutocomplete.component, "option", `${modelId}`),
    },
    // New components for heatpump, mission, realtime, working-condition
    heatpumpBrandAutocomplete: {
      component: "heatpump-brand-autocomplete",
      input: (): string => joinDataTestId(DATA_TEST_IDS.components.heatpumpBrandAutocomplete.component, "input"),
      dropdownButton: (): string =>
        joinDataTestId(DATA_TEST_IDS.components.heatpumpBrandAutocomplete.component, "dropdown-button"),
      textInput: (): string =>
        joinDataTestId(DATA_TEST_IDS.components.heatpumpBrandAutocomplete.component, "text-input"),
      option: (brandId: number): string =>
        joinDataTestId(DATA_TEST_IDS.components.heatpumpBrandAutocomplete.component, "option", `${brandId}`),
    },
    missionDropdown: {
      component: "mission-dropdown",
      skeleton: (): string => joinDataTestId(DATA_TEST_IDS.components.missionDropdown.component, "skeleton"),
      input: (): string => joinDataTestId(DATA_TEST_IDS.components.missionDropdown.component, "input"),
    },
    stompProvider: {
      component: "stomp-provider",
    },
    // Added for site components annotations
    networkManagerDropdown: {
      component: "network-manager-dropdown",
      skeleton: (): string => joinDataTestId(DATA_TEST_IDS.components.networkManagerDropdown.component, "skeleton"),
      dropdown: (): string => joinDataTestId(DATA_TEST_IDS.components.networkManagerDropdown.component, "dropdown"),
    },
    siteAutoDiagCard: {
      component: "site-auto-diag-card",
      header: (): string => joinDataTestId(DATA_TEST_IDS.components.siteAutoDiagCard.component, "header"),
      title: (): string => joinDataTestId(DATA_TEST_IDS.components.siteAutoDiagCard.component, "title"),
      disclaimer: (): string => joinDataTestId(DATA_TEST_IDS.components.siteAutoDiagCard.component),
      description: (): string => joinDataTestId(DATA_TEST_IDS.components.siteAutoDiagCard.component, "description"),
      syncMyVBtn: (): string => joinDataTestId(DATA_TEST_IDS.components.siteAutoDiagCard.component, "sync-myv-btn"),
      table: (): string => joinDataTestId(DATA_TEST_IDS.components.siteAutoDiagCard.component, "table"),
      row: (id: string | number): string =>
        joinDataTestId(DATA_TEST_IDS.components.siteAutoDiagCard.component, "row", String(id)),
      rowCell: (id: string | number, column: string): string =>
        joinDataTestId(DATA_TEST_IDS.components.siteAutoDiagCard.component, "row", String(id), column),
      actionsSpinner: (): string =>
        joinDataTestId(DATA_TEST_IDS.components.siteAutoDiagCard.component, "actions-spinner"),
      startDiagBtn: (id: string | number): string =>
        joinDataTestId(DATA_TEST_IDS.components.siteAutoDiagCard.component, "start-diag-btn", String(id)),
      technicianInstructionsBtn: (): string =>
        joinDataTestId(DATA_TEST_IDS.components.siteAutoDiagCard.component, "technician-instructions-btn"),
      deviceNameCell: (id: string | number): string =>
        DATA_TEST_IDS.components.siteAutoDiagCard.rowCell(id, "device-name"),
      resultCell: (id: string | number): string => DATA_TEST_IDS.components.siteAutoDiagCard.rowCell(id, "result"),
      dateCell: (id: string | number): string => DATA_TEST_IDS.components.siteAutoDiagCard.rowCell(id, "date"),
      energyManagerCell: (id: string | number): string =>
        DATA_TEST_IDS.components.siteAutoDiagCard.rowCell(id, "energy-manager"),
      myVProgramCell: (id: string | number): string =>
        DATA_TEST_IDS.components.siteAutoDiagCard.rowCell(id, "myv-program"),
      syncCell: (id: string | number): string => DATA_TEST_IDS.components.siteAutoDiagCard.rowCell(id, "sync"),
    },
    siteEscCard: {
      component: "site-esc-card",
      header: (): string => joinDataTestId(DATA_TEST_IDS.components.siteEscCard.component, "header"),
      title: (): string => joinDataTestId(DATA_TEST_IDS.components.siteEscCard.component, "title"),
      cardContent: (): string => joinDataTestId(DATA_TEST_IDS.components.siteEscCard.component, "card-content"),
      escStateSkeleton: (): string =>
        joinDataTestId(DATA_TEST_IDS.components.siteEscCard.component, "esc-state-skeleton"),
      withdrawalSkeleton: (): string =>
        joinDataTestId(DATA_TEST_IDS.components.siteEscCard.component, "withdrawal-skeleton"),
      errorSurfaceField: (): string =>
        joinDataTestId(DATA_TEST_IDS.components.siteEscCard.component, "error-surface-field"),
      errorSubscriberField: (): string =>
        joinDataTestId(DATA_TEST_IDS.components.siteEscCard.component, "error-subscriber-field"),
      errorSubscriberCompanyField: (): string =>
        joinDataTestId(DATA_TEST_IDS.components.siteEscCard.component, "error-subscriber-company-field"),
      errorIsNotInFranceField: (): string =>
        joinDataTestId(DATA_TEST_IDS.components.siteEscCard.component, "error-is-not-in-france-field"),
      eligibilityValue: (): string =>
        joinDataTestId(DATA_TEST_IDS.components.siteEscCard.component, "eligibility-value"),
      signedValue: (): string => joinDataTestId(DATA_TEST_IDS.components.siteEscCard.component, "signed-value"),
      withdrawalTitle: (): string => joinDataTestId(DATA_TEST_IDS.components.siteEscCard.component, "withdrawal-title"),
      withdrawalDate: (): string => joinDataTestId(DATA_TEST_IDS.components.siteEscCard.component, "withdrawal-date"),
      generateQuoteBtn: (): string =>
        joinDataTestId(DATA_TEST_IDS.components.siteEscCard.component, "generate-quote-btn"),
      restartQuoteBtn: (): string =>
        joinDataTestId(DATA_TEST_IDS.components.siteEscCard.component, "restart-quote-btn"),
      errorMessage: (): string => joinDataTestId(DATA_TEST_IDS.components.siteEscCard.component, "error-message"),
    },
    siteInstallationStatusCard: {
      component: "site-installation-status-card",
      header: (): string => joinDataTestId(DATA_TEST_IDS.components.siteInstallationStatusCard.component, "header"),
      title: (): string => joinDataTestId(DATA_TEST_IDS.components.siteInstallationStatusCard.component, "title"),
      content: (): string => joinDataTestId(DATA_TEST_IDS.components.siteInstallationStatusCard.component, "content"),
      flowValue: (): string =>
        joinDataTestId(DATA_TEST_IDS.components.siteInstallationStatusCard.component, "flow-value"),
      statusValue: (): string =>
        joinDataTestId(DATA_TEST_IDS.components.siteInstallationStatusCard.component, "status-value"),
      documentTitle: (): string =>
        joinDataTestId(DATA_TEST_IDS.components.siteInstallationStatusCard.component, "document-title"),
      descriptionRow: (index: string | number): string =>
        joinDataTestId(DATA_TEST_IDS.components.siteInstallationStatusCard.component, "description-row", String(index)),
      actionBtn: (key: string): string =>
        joinDataTestId(DATA_TEST_IDS.components.siteInstallationStatusCard.component, "action-btn", key),
      bonusTransferTitle: (): string =>
        joinDataTestId(DATA_TEST_IDS.components.siteInstallationStatusCard.component, "bonus-transfer-title"),
      resendMessageBtn: (): string =>
        joinDataTestId(DATA_TEST_IDS.components.siteInstallationStatusCard.component, "resend-message-btn"),
      linkToEligibilityForm: (): string =>
        joinDataTestId(DATA_TEST_IDS.components.siteInstallationStatusCard.component, "link-to-eligibility-form"),
    },
    siteInstallationStatusCardProvider: {
      component: "site-installation-status-card-provider",
      skeleton: (): string =>
        joinDataTestId(DATA_TEST_IDS.components.siteInstallationStatusCardProvider.component, "skeleton"),
      card: (): string => joinDataTestId(DATA_TEST_IDS.components.siteInstallationStatusCardProvider.component, "card"),
    },
    siteEventsCard: {
      component: "site-events-card",
      header: (): string => joinDataTestId(DATA_TEST_IDS.components.siteEventsCard.component, "header"),
      title: (): string => joinDataTestId(DATA_TEST_IDS.components.siteEventsCard.component, "title"),
      loadingSkeleton: (): string =>
        joinDataTestId(DATA_TEST_IDS.components.siteEventsCard.component, "loading-skeleton"),
      expandAllBtn: (): string => joinDataTestId(DATA_TEST_IDS.components.siteEventsCard.component, "expand-all-btn"),
      collapseAllBtn: (): string =>
        joinDataTestId(DATA_TEST_IDS.components.siteEventsCard.component, "collapse-all-btn"),
      eventTypeDropdown: (): string =>
        joinDataTestId(DATA_TEST_IDS.components.siteEventsCard.component, "event-type-dropdown"),
      eventTypeOption: (eventType: string): string =>
        joinDataTestId(DATA_TEST_IDS.components.siteEventsCard.component, "event-type-option", eventType),
      eventCategory: (): string => joinDataTestId(DATA_TEST_IDS.components.siteEventsCard.component, "event-category"),
      selectedEventCategory: (): string =>
        joinDataTestId(DATA_TEST_IDS.components.siteEventsCard.component, "selected-event-category"),
      eventRow: (): string => joinDataTestId(DATA_TEST_IDS.components.siteEventsCard.component, "event-row"),
      rootEventRow: (): string => joinDataTestId(DATA_TEST_IDS.components.siteEventsCard.component, "root-event-row"),
      childEventRow: (): string => joinDataTestId(DATA_TEST_IDS.components.siteEventsCard.component, "child-event-row"),
      eventRowByCategory: (category: string): string =>
        joinDataTestId(DATA_TEST_IDS.components.siteEventsCard.component, "event-row-by-category", category),
      tree: (): string => joinDataTestId(DATA_TEST_IDS.components.siteEventsCard.component, "tree"),
      categories: (): string => joinDataTestId(DATA_TEST_IDS.components.siteEventsCard.component, "categories"),
      eventDateAndName: (): string =>
        joinDataTestId(DATA_TEST_IDS.components.siteEventsCard.component, "event-date-and-name"),
      eventUser: (): string => joinDataTestId(DATA_TEST_IDS.components.siteEventsCard.component, "event-user"),
      detailsPanelTitle: (): string =>
        joinDataTestId(DATA_TEST_IDS.components.siteEventsCard.component, "details-panel-title"),
      detailsCreationDate: (): string =>
        joinDataTestId(DATA_TEST_IDS.components.siteEventsCard.component, "details-creation-date"),
      detailsCategory: (): string =>
        joinDataTestId(DATA_TEST_IDS.components.siteEventsCard.component, "details-category"),
      detailsOperator: (): string =>
        joinDataTestId(DATA_TEST_IDS.components.siteEventsCard.component, "details-operator"),
      detailsPriority: (): string =>
        joinDataTestId(DATA_TEST_IDS.components.siteEventsCard.component, "details-priority"),
      detailsStatus: (): string => joinDataTestId(DATA_TEST_IDS.components.siteEventsCard.component, "details-status"),
      detailsDescription: (): string =>
        joinDataTestId(DATA_TEST_IDS.components.siteEventsCard.component, "details-description"),
      zendeskLink: (): string => joinDataTestId(DATA_TEST_IDS.components.siteEventsCard.component, "zendesk-link"),
      filteredEventRow: (): string =>
        joinDataTestId(DATA_TEST_IDS.components.siteEventsCard.component, "filtered-event-row"),
    },
    siteEscEligibilityModal: {
      component: "site-esc-eligibility-modal",
      dialog: (): string => joinDataTestId(DATA_TEST_IDS.components.siteEscEligibilityModal.component, "dialog"),
      content: (): string => joinDataTestId(DATA_TEST_IDS.components.siteEscEligibilityModal.component, "content"),
      cancelBtn: (): string => joinDataTestId(DATA_TEST_IDS.components.siteEscEligibilityModal.component, "cancel-btn"),
      upgradeBtn: (): string =>
        joinDataTestId(DATA_TEST_IDS.components.siteEscEligibilityModal.component, "upgrade-btn"),
    },
    siteDeliveryPointCardSkeleton: {
      component: "site-delivery-point-card-skeleton",
      card: (): string => joinDataTestId(DATA_TEST_IDS.components.siteDeliveryPointCardSkeleton.component, "card"),
      title: (): string => joinDataTestId(DATA_TEST_IDS.components.siteDeliveryPointCardSkeleton.component, "title"),
      pdlNumberTitle: (): string =>
        joinDataTestId(DATA_TEST_IDS.components.siteDeliveryPointCardSkeleton.component, "pdl-number-title"),
      statusTitle: (): string =>
        joinDataTestId(DATA_TEST_IDS.components.siteDeliveryPointCardSkeleton.component, "status-title"),
      consentTitle: (): string =>
        joinDataTestId(DATA_TEST_IDS.components.siteDeliveryPointCardSkeleton.component, "consent-title"),
      subscriptionTitle: (): string =>
        joinDataTestId(DATA_TEST_IDS.components.siteDeliveryPointCardSkeleton.component, "subscription-title"),
      skeleton: (name: string): string =>
        joinDataTestId(DATA_TEST_IDS.components.siteDeliveryPointCardSkeleton.component, "skeleton", name),
    },
    siteDeliveryPointCard: {
      component: "site-delivery-point-card",
      card: (): string => joinDataTestId(DATA_TEST_IDS.components.siteDeliveryPointCard.component, "card"),
      header: (): string => joinDataTestId(DATA_TEST_IDS.components.siteDeliveryPointCard.component, "header"),
      title: (): string => joinDataTestId(DATA_TEST_IDS.components.siteDeliveryPointCard.component, "title"),
      editBtn: (): string => joinDataTestId(DATA_TEST_IDS.components.siteDeliveryPointCard.component, "edit-btn"),
      noPdlText: (): string => joinDataTestId(DATA_TEST_IDS.components.siteDeliveryPointCard.component, "no-pdl-text"),
      pdlNumberTitle: (): string =>
        joinDataTestId(DATA_TEST_IDS.components.siteDeliveryPointCard.component, "pdl-number-title"),
      pdlNumberValue: (): string =>
        joinDataTestId(DATA_TEST_IDS.components.siteDeliveryPointCard.component, "pdl-number-value"),
      statusTitle: (): string =>
        joinDataTestId(DATA_TEST_IDS.components.siteDeliveryPointCard.component, "status-title"),
      statusValue: (): string =>
        joinDataTestId(DATA_TEST_IDS.components.siteDeliveryPointCard.component, "status-value"),
      invalidateBtn: (): string =>
        joinDataTestId(DATA_TEST_IDS.components.siteDeliveryPointCard.component, "invalidate-btn"),
      validateBtn: (): string =>
        joinDataTestId(DATA_TEST_IDS.components.siteDeliveryPointCard.component, "validate-btn"),
      consentTitle: (): string =>
        joinDataTestId(DATA_TEST_IDS.components.siteDeliveryPointCard.component, "consent-title"),
      consentSkeleton: (): string =>
        joinDataTestId(DATA_TEST_IDS.components.siteDeliveryPointCard.component, "consent-skeleton"),
      consentValue: (): string =>
        joinDataTestId(DATA_TEST_IDS.components.siteDeliveryPointCard.component, "consent-value"),
      subscriptionTitle: (): string =>
        joinDataTestId(DATA_TEST_IDS.components.siteDeliveryPointCard.component, "subscription-title"),
      subscriptionSkeleton: (): string =>
        joinDataTestId(DATA_TEST_IDS.components.siteDeliveryPointCard.component, "subscription-skeleton"),
      subscriptionValue: (): string =>
        joinDataTestId(DATA_TEST_IDS.components.siteDeliveryPointCard.component, "subscription-value"),
      skeleton: (name: string): string =>
        joinDataTestId(DATA_TEST_IDS.components.siteDeliveryPointCard.component, "skeleton", name),
    },
    siteModulationWorkingConditionCard: {
      component: "site-modulation-working-condition-card",
      card: (): string => joinDataTestId(DATA_TEST_IDS.components.siteModulationWorkingConditionCard.component, "card"),
      header: (): string =>
        joinDataTestId(DATA_TEST_IDS.components.siteModulationWorkingConditionCard.component, "header"),
      title: (): string =>
        joinDataTestId(DATA_TEST_IDS.components.siteModulationWorkingConditionCard.component, "title"),
      moreBtn: (): string =>
        joinDataTestId(DATA_TEST_IDS.components.siteModulationWorkingConditionCard.component, "more-btn"),
      description: (): string =>
        joinDataTestId(DATA_TEST_IDS.components.siteModulationWorkingConditionCard.component, "description"),
      content: (): string =>
        joinDataTestId(DATA_TEST_IDS.components.siteModulationWorkingConditionCard.component, "content"),
      chart: (): string =>
        joinDataTestId(DATA_TEST_IDS.components.siteModulationWorkingConditionCard.component, "chart"),
      skeleton: (): string =>
        joinDataTestId(DATA_TEST_IDS.components.siteModulationWorkingConditionCard.component, "skeleton"),
    },
    siteSummaryTextCopyToClipboard: {
      component: "site-summary-text-copy-to-clipboard",
      link: (): string => joinDataTestId(DATA_TEST_IDS.components.siteSummaryTextCopyToClipboard.component, "link"),
      textValue: (): string =>
        joinDataTestId(DATA_TEST_IDS.components.siteSummaryTextCopyToClipboard.component, "text-value"),
      copyBtn: (): string =>
        joinDataTestId(DATA_TEST_IDS.components.siteSummaryTextCopyToClipboard.component, "copy-btn"),
    },
    searchSiteTabs: {
      component: "search-site-tabs",
      tabView: (): string => joinDataTestId(DATA_TEST_IDS.components.searchSiteTabs.component, "tab-view"),
      searchTab: (): string => joinDataTestId(DATA_TEST_IDS.components.searchSiteTabs.component, "search-tab"),
      openedFoldersTab: (): string =>
        joinDataTestId(DATA_TEST_IDS.components.searchSiteTabs.component, "opened-folders-tab"),
      searchButton: (): string => joinDataTestId(DATA_TEST_IDS.components.searchSiteTabs.component, "search-button"),
      advancedSearch: (): string =>
        joinDataTestId(DATA_TEST_IDS.components.searchSiteTabs.component, "advanced-search"),
      modal: (): string => joinDataTestId(DATA_TEST_IDS.components.searchSiteTabs.component, "modal"),
    },
    siteDetailsCard: {
      component: "site-details-card",
      card: (): string => joinDataTestId(DATA_TEST_IDS.components.siteDetailsCard.component, "card"),
      header: (): string => joinDataTestId(DATA_TEST_IDS.components.siteDetailsCard.component, "header"),
      title: (): string => joinDataTestId(DATA_TEST_IDS.components.siteDetailsCard.component, "title"),
      editBtn: (): string => joinDataTestId(DATA_TEST_IDS.components.siteDetailsCard.component, "edit-btn"),
      missionTitle: (): string => joinDataTestId(DATA_TEST_IDS.components.siteDetailsCard.component, "mission-title"),
      missionValue: (): string => joinDataTestId(DATA_TEST_IDS.components.siteDetailsCard.component, "mission-value"),
      missionSkeleton: (): string =>
        joinDataTestId(DATA_TEST_IDS.components.siteDetailsCard.component, "mission-skeleton"),
      missionEmpty: (): string => joinDataTestId(DATA_TEST_IDS.components.siteDetailsCard.component, "mission-empty"),
      idTitle: (): string => joinDataTestId(DATA_TEST_IDS.components.siteDetailsCard.component, "id-title"),
      idSkeleton: (): string => joinDataTestId(DATA_TEST_IDS.components.siteDetailsCard.component, "id-skeleton"),
      idValue: (): string => joinDataTestId(DATA_TEST_IDS.components.siteDetailsCard.component, "id-value"),
      externIdTitle: (): string =>
        joinDataTestId(DATA_TEST_IDS.components.siteDetailsCard.component, "extern-id-title"),
      externIdSkeleton: (): string =>
        joinDataTestId(DATA_TEST_IDS.components.siteDetailsCard.component, "extern-id-skeleton"),
      externIdValue: (): string =>
        joinDataTestId(DATA_TEST_IDS.components.siteDetailsCard.component, "extern-id-value"),
      companyTitle: (): string => joinDataTestId(DATA_TEST_IDS.components.siteDetailsCard.component, "company-title"),
      companySkeleton: (): string =>
        joinDataTestId(DATA_TEST_IDS.components.siteDetailsCard.component, "company-skeleton"),
      companyValue: (): string => joinDataTestId(DATA_TEST_IDS.components.siteDetailsCard.component, "company-value"),
      postalAddressTitle: (): string =>
        joinDataTestId(DATA_TEST_IDS.components.siteDetailsCard.component, "postal-address-title"),
      postalAddressSkeleton: (): string =>
        joinDataTestId(DATA_TEST_IDS.components.siteDetailsCard.component, "postal-address-skeleton"),
      postalAddressValue: (part: "address" | "complement" | "city-zip" | "country"): string =>
        joinDataTestId(DATA_TEST_IDS.components.siteDetailsCard.component, "postal-address", part),
      geoAddressTitle: (): string =>
        joinDataTestId(DATA_TEST_IDS.components.siteDetailsCard.component, "geo-address-title"),
      geoAddressSkeleton: (): string =>
        joinDataTestId(DATA_TEST_IDS.components.siteDetailsCard.component, "geo-address-skeleton"),
      geoAddressValue: (part: "address" | "city-zip" | "country"): string =>
        joinDataTestId(DATA_TEST_IDS.components.siteDetailsCard.component, "geo-address", part),
      subscribedPowerTitle: (): string =>
        joinDataTestId(DATA_TEST_IDS.components.siteDetailsCard.component, "subscribed-power-title"),
      subscribedPowerSkeleton: (): string =>
        joinDataTestId(DATA_TEST_IDS.components.siteDetailsCard.component, "subscribed-power-skeleton"),
      subscribedPowerValue: (): string =>
        joinDataTestId(DATA_TEST_IDS.components.siteDetailsCard.component, "subscribed-power-value"),
      siteTypeLayoutTitle: (): string =>
        joinDataTestId(DATA_TEST_IDS.components.siteDetailsCard.component, "site-type-layout-title"),
      siteTypeLayoutSkeleton: (): string =>
        joinDataTestId(DATA_TEST_IDS.components.siteDetailsCard.component, "site-type-layout-skeleton"),
      siteTypeLayoutValue: (): string =>
        joinDataTestId(DATA_TEST_IDS.components.siteDetailsCard.component, "site-type-layout-value"),
      siteLayoutTitle: (): string =>
        joinDataTestId(DATA_TEST_IDS.components.siteDetailsCard.component, "site-layout-title"),
      siteLayoutSkeleton: (): string =>
        joinDataTestId(DATA_TEST_IDS.components.siteDetailsCard.component, "site-layout-skeleton"),
      siteLayoutValue: (): string =>
        joinDataTestId(DATA_TEST_IDS.components.siteDetailsCard.component, "site-layout-value"),
      surfaceTitle: (): string => joinDataTestId(DATA_TEST_IDS.components.siteDetailsCard.component, "surface-title"),
      surfaceSkeleton: (): string =>
        joinDataTestId(DATA_TEST_IDS.components.siteDetailsCard.component, "surface-skeleton"),
      surfaceValue: (): string => joinDataTestId(DATA_TEST_IDS.components.siteDetailsCard.component, "surface-value"),
      badges: (): string => joinDataTestId(DATA_TEST_IDS.components.siteDetailsCard.component, "badges"),
      toggleNearbyGeolocationBtn: (): string =>
        joinDataTestId(DATA_TEST_IDS.components.siteDetailsCard.component, "toggle-nearby-geolocation-btn"),
      toggleNearbyGeolocationLabel: (): string =>
        joinDataTestId(DATA_TEST_IDS.components.siteDetailsCard.component, "toggle-nearby-geolocation-label"),
    },
    siteOccupancyTypeDropdown: {
      component: "site-occupancy-type-dropdown",
      dropdown: (): string => joinDataTestId(DATA_TEST_IDS.components.siteOccupancyTypeDropdown.component, "dropdown"),
      option: (index: number | string): string =>
        joinDataTestId(DATA_TEST_IDS.components.siteOccupancyTypeDropdown.component, "option", String(index)),
    },
    sitePartnershipCard: {
      component: "site-partnership-card",
      card: (): string => joinDataTestId(DATA_TEST_IDS.components.sitePartnershipCard.component, "card"),
      header: (): string => joinDataTestId(DATA_TEST_IDS.components.sitePartnershipCard.component, "header"),
      title: (): string => joinDataTestId(DATA_TEST_IDS.components.sitePartnershipCard.component, "title"),
      editBtn: (): string => joinDataTestId(DATA_TEST_IDS.components.sitePartnershipCard.component, "edit-btn"),
      partnerTitle: (): string =>
        joinDataTestId(DATA_TEST_IDS.components.sitePartnershipCard.component, "partner-title"),
      partnerSkeleton: (): string =>
        joinDataTestId(DATA_TEST_IDS.components.sitePartnershipCard.component, "partner-skeleton"),
      partnerValue: (): string =>
        joinDataTestId(DATA_TEST_IDS.components.sitePartnershipCard.component, "partner-value"),
      operationTitle: (): string =>
        joinDataTestId(DATA_TEST_IDS.components.sitePartnershipCard.component, "operation-title"),
      operationSkeleton: (): string =>
        joinDataTestId(DATA_TEST_IDS.components.sitePartnershipCard.component, "operation-skeleton"),
      operationValue: (): string =>
        joinDataTestId(DATA_TEST_IDS.components.sitePartnershipCard.component, "operation-value"),
      missionTitle: (): string =>
        joinDataTestId(DATA_TEST_IDS.components.sitePartnershipCard.component, "mission-title"),
      missionSkeleton: (): string =>
        joinDataTestId(DATA_TEST_IDS.components.sitePartnershipCard.component, "mission-skeleton"),
      missionValue: (): string =>
        joinDataTestId(DATA_TEST_IDS.components.sitePartnershipCard.component, "mission-value"),
    },
    siteAddressPopper: {
      component: "site-address-popper",
      trigger: (): string => joinDataTestId(DATA_TEST_IDS.components.siteAddressPopper.component, "trigger"),
      popper: (): string => joinDataTestId(DATA_TEST_IDS.components.siteAddressPopper.component, "popper"),
      postalTitle: (): string => joinDataTestId(DATA_TEST_IDS.components.siteAddressPopper.component, "postal-title"),
      postalValue: (part: "address" | "complement" | "zip" | "city"): string =>
        joinDataTestId(DATA_TEST_IDS.components.siteAddressPopper.component, "postal-value", part),
      geoTitle: (): string => joinDataTestId(DATA_TEST_IDS.components.siteAddressPopper.component, "geocoded-title"),
      geoValue: (part: "address" | "zip" | "city"): string =>
        joinDataTestId(DATA_TEST_IDS.components.siteAddressPopper.component, "geocoded-value", part),
    },
    editSitePostalAddressModal: {
      component: "edit-site-postal-address-modal",
      dialog: (): string => joinDataTestId(DATA_TEST_IDS.components.editSitePostalAddressModal.component, "dialog"),
      form: (): string => joinDataTestId(DATA_TEST_IDS.components.editSitePostalAddressModal.component, "form"),
    },
    siteAlerts: {
      component: "site-alerts",
      message: (): string => joinDataTestId(DATA_TEST_IDS.components.siteAlerts.component, "message"),
      markdown: (): string => joinDataTestId(DATA_TEST_IDS.components.siteAlerts.component, "markdown"),
      pager: (): string => joinDataTestId(DATA_TEST_IDS.components.siteAlerts.component, "pager"),
      prevBtn: (): string => joinDataTestId(DATA_TEST_IDS.components.siteAlerts.component, "prev-btn"),
      nextBtn: (): string => joinDataTestId(DATA_TEST_IDS.components.siteAlerts.component, "next-btn"),
      counter: (): string => joinDataTestId(DATA_TEST_IDS.components.siteAlerts.component, "counter"),
    },
    siteConstraintsSummaryCard: {
      component: "site-constraints-summary-card",
      header: (): string => joinDataTestId(DATA_TEST_IDS.components.siteConstraintsSummaryCard.component, "header"),
      title: (): string => joinDataTestId(DATA_TEST_IDS.components.siteConstraintsSummaryCard.component, "title"),
      content: (): string => joinDataTestId(DATA_TEST_IDS.components.siteConstraintsSummaryCard.component, "content"),
      loadingSkeleton: (): string =>
        joinDataTestId(DATA_TEST_IDS.components.siteConstraintsSummaryCard.component, "loading-skeleton"),
      nothingText: (): string =>
        joinDataTestId(DATA_TEST_IDS.components.siteConstraintsSummaryCard.component, "nothing-text"),
      list: (): string => joinDataTestId(DATA_TEST_IDS.components.siteConstraintsSummaryCard.component, "list"),
      listItem: (name: "definitive" | "links" | "adjusting" | "planning" | "temporary"): string =>
        joinDataTestId(DATA_TEST_IDS.components.siteConstraintsSummaryCard.component, "list-item", name),
    },
    siteRecentActionsCard: {
      component: "site-recent-actions-card",
      header: (): string => joinDataTestId(DATA_TEST_IDS.components.siteRecentActionsCard.component, "header"),
      title: (): string => joinDataTestId(DATA_TEST_IDS.components.siteRecentActionsCard.component, "title"),
      lastCallTitle: (): string =>
        joinDataTestId(DATA_TEST_IDS.components.siteRecentActionsCard.component, "last-call-title"),
      lastCallValue: (): string =>
        joinDataTestId(DATA_TEST_IDS.components.siteRecentActionsCard.component, "last-call-value"),
      lastHardwareConfigTitle: (): string =>
        joinDataTestId(DATA_TEST_IDS.components.siteRecentActionsCard.component, "last-hw-config-title"),
      lastHardwareConfigValue: (): string =>
        joinDataTestId(DATA_TEST_IDS.components.siteRecentActionsCard.component, "last-hw-config-value"),
      lastSoftwareUpdateTitle: (): string =>
        joinDataTestId(DATA_TEST_IDS.components.siteRecentActionsCard.component, "last-sw-update-title"),
      lastSoftwareUpdateValue: (): string =>
        joinDataTestId(DATA_TEST_IDS.components.siteRecentActionsCard.component, "last-sw-update-value"),
      loadingSkeleton: (name: string): string =>
        joinDataTestId(DATA_TEST_IDS.components.siteRecentActionsCard.component, "loading-skeleton", name),
    },
    siteAutoDiagMemoModal: {
      component: "site-auto-diag-memo-modal",
      infoBtn: (): string => joinDataTestId(DATA_TEST_IDS.components.siteAutoDiagMemoModal.component, "info-btn"),
      dialog: (): string => joinDataTestId(DATA_TEST_IDS.components.siteAutoDiagMemoModal.component, "dialog"),
      content: (): string => joinDataTestId(DATA_TEST_IDS.components.siteAutoDiagMemoModal.component, "content"),
    },
    editHeatpumpDeclarationForm: {
      component: "edit-heatpump-declaration-form",
      form: (): string => joinDataTestId(DATA_TEST_IDS.components.editHeatpumpDeclarationForm.component, "form"),
      heatPumpInSiteLabel: (): string =>
        joinDataTestId(DATA_TEST_IDS.components.editHeatpumpDeclarationForm.component, "heat-pump-in-site-label"),
      heatPumpInSiteRadio: (value: string | boolean): string =>
        joinDataTestId(
          DATA_TEST_IDS.components.editHeatpumpDeclarationForm.component,
          "heat-pump-in-site-radio",
          String(value),
        ),
      heatPumpBrandLabel: (): string =>
        joinDataTestId(DATA_TEST_IDS.components.editHeatpumpDeclarationForm.component, "heat-pump-brand-label"),
      heatPumpBrandInput: (): string =>
        joinDataTestId(DATA_TEST_IDS.components.editHeatpumpDeclarationForm.component, "heat-pump-brand-input"),
      heatPumpModelLabel: (): string =>
        joinDataTestId(DATA_TEST_IDS.components.editHeatpumpDeclarationForm.component, "heat-pump-model-label"),
      heatPumpModelInput: (): string =>
        joinDataTestId(DATA_TEST_IDS.components.editHeatpumpDeclarationForm.component, "heat-pump-model-input"),
      createBrandBtn: (): string =>
        joinDataTestId(DATA_TEST_IDS.components.editHeatpumpDeclarationForm.component, "create-brand-btn"),
      createModelBtn: (): string =>
        joinDataTestId(DATA_TEST_IDS.components.editHeatpumpDeclarationForm.component, "create-model-btn"),
      notEligibleWarning: (): string =>
        joinDataTestId(DATA_TEST_IDS.components.editHeatpumpDeclarationForm.component, "not-eligible-warning"),
      submitBtn: (): string =>
        joinDataTestId(DATA_TEST_IDS.components.editHeatpumpDeclarationForm.component, "submit-btn"),
    },
    workingConditionModulationCard: {
      component: "working-condition-modulation-card",
      header: (): string => joinDataTestId(DATA_TEST_IDS.components.workingConditionModulationCard.component, "header"),
      title: (): string => joinDataTestId(DATA_TEST_IDS.components.workingConditionModulationCard.component, "title"),
      content: (): string =>
        joinDataTestId(DATA_TEST_IDS.components.workingConditionModulationCard.component, "content"),
      description: (): string =>
        joinDataTestId(DATA_TEST_IDS.components.workingConditionModulationCard.component, "description"),
      momboxPresence: (): string =>
        joinDataTestId(DATA_TEST_IDS.components.workingConditionModulationCard.component, "mombox-presence"),
      table: (): string => joinDataTestId(DATA_TEST_IDS.components.workingConditionModulationCard.component, "table"),
      row: (id: string | number): string =>
        joinDataTestId(DATA_TEST_IDS.components.workingConditionModulationCard.component, "row", String(id)),
      rowCell: (column: string): string =>
        joinDataTestId(DATA_TEST_IDS.components.workingConditionModulationCard.component, "row", column),
    },
    acquisitionSensorTable: {
      component: "acquisition-sensor-table",
      table: (): string => joinDataTestId(DATA_TEST_IDS.components.acquisitionSensorTable.component, "table"),
      row: (id: string | number): string =>
        joinDataTestId(DATA_TEST_IDS.components.acquisitionSensorTable.component, "row", String(id)),
      rowCell: (column: string): string =>
        joinDataTestId(DATA_TEST_IDS.components.acquisitionSensorTable.component, "row", column),
    },
    acquisitionModulatorTable: {
      component: "acquisition-modulator-table",
      table: (): string => joinDataTestId(DATA_TEST_IDS.components.acquisitionModulatorTable.component, "table"),
      row: (id: string | number): string =>
        joinDataTestId(DATA_TEST_IDS.components.acquisitionModulatorTable.component, "row", String(id)),
      rowCell: (column: string): string =>
        joinDataTestId(DATA_TEST_IDS.components.acquisitionModulatorTable.component, "row", column),
      toggleInstallationTests: (rowId: string): string =>
        joinDataTestId(
          DATA_TEST_IDS.components.acquisitionModulatorTable.component,
          "toggle-installation-tests",
          rowId,
        ),
      installationTestsExpansion: (rowId: string): string =>
        joinDataTestId(
          DATA_TEST_IDS.components.acquisitionModulatorTable.component,
          "installation-tests-expansion",
          rowId,
        ),
    },
    installationTestDetails: {
      component: "installation-test-details",
      table: (): string => joinDataTestId(DATA_TEST_IDS.components.installationTestDetails.component, "table"),
      loading: (): string => joinDataTestId(DATA_TEST_IDS.components.installationTestDetails.component, "loading"),
      empty: (): string => joinDataTestId(DATA_TEST_IDS.components.installationTestDetails.component, "empty"),
      error: (): string => joinDataTestId(DATA_TEST_IDS.components.installationTestDetails.component, "error"),
      partial: (): string => joinDataTestId(DATA_TEST_IDS.components.installationTestDetails.component, "partial"),
      retry: (): string => joinDataTestId(DATA_TEST_IDS.components.installationTestDetails.component, "retry"),
      rowCell: (rowKey: string, column: string): string =>
        joinDataTestId(DATA_TEST_IDS.components.installationTestDetails.component, "row", rowKey, column),
      status: (rowKey: string): string =>
        joinDataTestId(DATA_TEST_IDS.components.installationTestDetails.component, "status", rowKey),
    },
    updateVersionButton: {
      component: "update-version-button",
      updatingIcon: (): string =>
        joinDataTestId(DATA_TEST_IDS.components.updateVersionButton.component, "updating-icon"),
      okIcon: (): string => joinDataTestId(DATA_TEST_IDS.components.updateVersionButton.component, "ok-icon"),
      koIcon: (): string => joinDataTestId(DATA_TEST_IDS.components.updateVersionButton.component, "ko-icon"),
      updateBtn: (): string => joinDataTestId(DATA_TEST_IDS.components.updateVersionButton.component, "update-btn"),
    },
    acquisitionMomboxTable: {
      component: "acquisition-mombox-table",
      table: (): string => joinDataTestId(DATA_TEST_IDS.components.acquisitionMomboxTable.component, "table"),
      row: (id: string | number): string =>
        joinDataTestId(DATA_TEST_IDS.components.acquisitionMomboxTable.component, "row", String(id)),
      rowCell: (column: string): string =>
        joinDataTestId(DATA_TEST_IDS.components.acquisitionMomboxTable.component, "row", column),
    },
    pingModulatorButton: {
      component: "ping-modulator-button",
      pendingIcon: (): string => joinDataTestId(DATA_TEST_IDS.components.pingModulatorButton.component, "pending-icon"),
      button: (): string => joinDataTestId(DATA_TEST_IDS.components.pingModulatorButton.component, "button"),
      status: (): string => joinDataTestId(DATA_TEST_IDS.components.pingModulatorButton.component, "status"),
    },
    workingConditionAcquisitionCard: {
      component: "working-condition-acquisition-card",
      header: (): string =>
        joinDataTestId(DATA_TEST_IDS.components.workingConditionAcquisitionCard.component, "header"),
      title: (): string => joinDataTestId(DATA_TEST_IDS.components.workingConditionAcquisitionCard.component, "title"),
      content: (): string =>
        joinDataTestId(DATA_TEST_IDS.components.workingConditionAcquisitionCard.component, "content"),
      description: (): string =>
        joinDataTestId(DATA_TEST_IDS.components.workingConditionAcquisitionCard.component, "description"),
      tabModulators: (): string =>
        joinDataTestId(DATA_TEST_IDS.components.workingConditionAcquisitionCard.component, "tab-modulators"),
      tabMomboxs: (): string =>
        joinDataTestId(DATA_TEST_IDS.components.workingConditionAcquisitionCard.component, "tab-momboxs"),
      tabSensors: (): string =>
        joinDataTestId(DATA_TEST_IDS.components.workingConditionAcquisitionCard.component, "tab-sensors"),
    },
    // Modulator popper (used by working-condition tables)
    modulatorPopper: {
      component: "modulator-popper",
      buttonLabel: (): string => joinDataTestId(DATA_TEST_IDS.components.modulatorPopper.component, "button-label"),
      popper: (): string => joinDataTestId(DATA_TEST_IDS.components.modulatorPopper.component, "popper"),
    },
    // New ones for this task
    fkSelectCountry: {
      component: "fk-select-country",
      input: (): string => joinDataTestId(DATA_TEST_IDS.components.fkSelectCountry.component, "input"),
    },
    minYearDropdown: {
      component: "min-year-dropdown",
      input: (): string => joinDataTestId(DATA_TEST_IDS.components.minYearDropdown.component, "input"),
    },
    monthDropdown: {
      component: "month-dropdown",
      input: (): string => joinDataTestId(DATA_TEST_IDS.components.monthDropdown.component, "input"),
    },
    countryBasedInput: {
      component: "country-based-input",
      countrySelect: (): string =>
        joinDataTestId(DATA_TEST_IDS.components.countryBasedInput.component, "country-select"),
      slot: (): string => joinDataTestId(DATA_TEST_IDS.components.countryBasedInput.component, "slot"),
    },
    countryBasedInputPhone: {
      component: "country-based-input-phone",
      input: (): string => joinDataTestId(DATA_TEST_IDS.components.countryBasedInputPhone.component, "input"),
    },
    inputPhone: {
      component: "input-phone",
      countryDropdown: (): string => joinDataTestId(DATA_TEST_IDS.components.inputPhone.component, "country-dropdown"),
      inputField: (): string => joinDataTestId(DATA_TEST_IDS.components.inputPhone.component, "input-field"),
      inputsContainer: (): string => joinDataTestId(DATA_TEST_IDS.components.inputPhone.component, "inputs-container"),
    },
    dynamicForm: {
      component: "dynamic-form",
      form: (): string => joinDataTestId(DATA_TEST_IDS.components.dynamicForm.component, "form"),
      row: (name: string): string => joinDataTestId(DATA_TEST_IDS.components.dynamicForm.component, "row", name),
      label: (name: string): string => joinDataTestId(DATA_TEST_IDS.components.dynamicForm.component, "label", name),
      field: (name: string): string => joinDataTestId(DATA_TEST_IDS.components.dynamicForm.component, "field", name),
      error: (name: string): string => joinDataTestId(DATA_TEST_IDS.components.dynamicForm.component, "error", name),
      submitButton: (): string => joinDataTestId(DATA_TEST_IDS.components.dynamicForm.component, "submit-button"),
      radio: (name: string, value: string): string =>
        joinDataTestId(DATA_TEST_IDS.components.dynamicForm.component, "radio", name, value),
    },
    dtdCard: {
      component: "dtd-card",
      cardTitle: (): string => joinDataTestId(DATA_TEST_IDS.components.dtdCard.component, "card-title"),
      dropdown: (): string => joinDataTestId(DATA_TEST_IDS.components.dtdCard.component, "dropdown"),
      dropdownError: (): string => joinDataTestId(DATA_TEST_IDS.components.dtdCard.component, "dropdown-error"),
      calendarError: (): string => joinDataTestId(DATA_TEST_IDS.components.dtdCard.component, "calendar-error"),
      dtdAnalysisContainer: (): string =>
        joinDataTestId(DATA_TEST_IDS.components.dtdCard.component, "dtd-analysis-container"),
      dtdAnalysisStateErrorContainer: (): string =>
        joinDataTestId(DATA_TEST_IDS.components.dtdCard.component, "dtd-analysis-state-error-container"),
      analyzeButton: (): string => joinDataTestId(DATA_TEST_IDS.components.dtdCard.component, "analyze-button"),
      skeleton: (): string => joinDataTestId(DATA_TEST_IDS.components.dtdCard.component, "skeleton"),
      calendar: (): string => joinDataTestId(DATA_TEST_IDS.components.dtdCard.component, "calendar"),
      cardHeader: (): string => joinDataTestId(DATA_TEST_IDS.components.dtdCard.component, "card-header"),
      modulatorDropdownError: (): string =>
        joinDataTestId(DATA_TEST_IDS.components.dtdCard.component, "card-modulator-dropdown-error"),
      cardDescription: (): string => joinDataTestId(DATA_TEST_IDS.components.dtdCard.component, "card-description"),
      form: (): string => joinDataTestId(DATA_TEST_IDS.components.dtdCard.component, "form"),
      modulatorDropdown: (): string => joinDataTestId(DATA_TEST_IDS.components.dtdCard.component, "modulator-dropdown"),
      datetimeCalendar: (): string => joinDataTestId(DATA_TEST_IDS.components.dtdCard.component, "datetime-calendar"),
      typeDropdown: (): string => joinDataTestId(DATA_TEST_IDS.components.dtdCard.component, "type-dropdown"),
      analyzeBtn: (): string => joinDataTestId(DATA_TEST_IDS.components.dtdCard.component, "analyze-btn"),
      proposedActionsTitle: (): string =>
        joinDataTestId(DATA_TEST_IDS.components.dtdCard.component, "proposed-actions-title"),
      pendingSkeleton: (): string => joinDataTestId(DATA_TEST_IDS.components.dtdCard.component, "pending-skeleton"),
      errorContainer: (): string => joinDataTestId(DATA_TEST_IDS.components.dtdCard.component, "error-container"),
      analysisBlock: (): string => joinDataTestId(DATA_TEST_IDS.components.dtdCard.component, "analysis-block"),
    },
    baseLayout: {
      component: "base-layout",
      container: (): string => joinDataTestId(DATA_TEST_IDS.components.baseLayout.component, "container"),
      main: (): string => joinDataTestId(DATA_TEST_IDS.components.baseLayout.component, "main"),
    },
    editSiteGeolocalizedAddressModal: {
      component: "edit-site-geolocalized-address-modal",
      dialog: (): string =>
        joinDataTestId(DATA_TEST_IDS.components.editSiteGeolocalizedAddressModal.component, "dialog"),
      mainForm: (): string =>
        joinDataTestId(DATA_TEST_IDS.components.editSiteGeolocalizedAddressModal.component, "main-form"),
      divider: (): string =>
        joinDataTestId(DATA_TEST_IDS.components.editSiteGeolocalizedAddressModal.component, "divider"),
      suggestionsTitle: (): string =>
        joinDataTestId(DATA_TEST_IDS.components.editSiteGeolocalizedAddressModal.component, "suggestions-title"),
      suggestionsForm: (): string =>
        joinDataTestId(DATA_TEST_IDS.components.editSiteGeolocalizedAddressModal.component, "suggestions-form"),
    },
    siteReinterventionSidebar: {
      component: "site-reintervention-sidebar",
      sidebar: (): string => joinDataTestId(DATA_TEST_IDS.components.siteReinterventionSidebar.component, "sidebar"),
      form: (): string => joinDataTestId(DATA_TEST_IDS.components.siteReinterventionSidebar.component, "form"),
      reasonLabel: (): string =>
        joinDataTestId(DATA_TEST_IDS.components.siteReinterventionSidebar.component, "reason-label"),
      reasonDropdown: (): string =>
        joinDataTestId(DATA_TEST_IDS.components.siteReinterventionSidebar.component, "reason-dropdown"),
      reasonError: (): string =>
        joinDataTestId(DATA_TEST_IDS.components.siteReinterventionSidebar.component, "reason-error"),
      commentLabel: (): string =>
        joinDataTestId(DATA_TEST_IDS.components.siteReinterventionSidebar.component, "comment-label"),
      commentTextarea: (): string =>
        joinDataTestId(DATA_TEST_IDS.components.siteReinterventionSidebar.component, "comment-textarea"),
      commentError: (): string =>
        joinDataTestId(DATA_TEST_IDS.components.siteReinterventionSidebar.component, "comment-error"),
      roomsHeader: (): string =>
        joinDataTestId(DATA_TEST_IDS.components.siteReinterventionSidebar.component, "rooms-header"),
      addRoomBtn: (): string =>
        joinDataTestId(DATA_TEST_IDS.components.siteReinterventionSidebar.component, "add-room-btn"),
      emptyStateTitle: (): string =>
        joinDataTestId(DATA_TEST_IDS.components.siteReinterventionSidebar.component, "empty-title"),
      emptyStateText: (): string =>
        joinDataTestId(DATA_TEST_IDS.components.siteReinterventionSidebar.component, "empty-text"),
      roomRow: (id: string | number): string =>
        joinDataTestId(DATA_TEST_IDS.components.siteReinterventionSidebar.component, "room", String(id)),
      roomRemoveBtn: (id: string | number): string =>
        joinDataTestId(DATA_TEST_IDS.components.siteReinterventionSidebar.component, "room", String(id), "remove-btn"),
      roomDropdown: (id: string | number): string =>
        joinDataTestId(DATA_TEST_IDS.components.siteReinterventionSidebar.component, "room", String(id), "dropdown"),
      roomDropdownValue: (id: string | number): string =>
        joinDataTestId(
          DATA_TEST_IDS.components.siteReinterventionSidebar.component,
          "room",
          String(id),
          "dropdown-value",
        ),
      roomCommentLabel: (id: string | number): string =>
        joinDataTestId(
          DATA_TEST_IDS.components.siteReinterventionSidebar.component,
          "room",
          String(id),
          "comment-label",
        ),
      roomCommentField: (id: string | number): string =>
        joinDataTestId(
          DATA_TEST_IDS.components.siteReinterventionSidebar.component,
          "room",
          String(id),
          "comment-field",
        ),
      submitBtn: (): string =>
        joinDataTestId(DATA_TEST_IDS.components.siteReinterventionSidebar.component, "submit-btn"),
      roomCommentTextarea: (id: number | string): string =>
        joinDataTestId(
          DATA_TEST_IDS.components.siteReinterventionSidebar.component,
          "room",
          String(id),
          "comment-textarea",
        ),
      closeBtn: (): string => joinDataTestId(DATA_TEST_IDS.components.siteReinterventionSidebar.component, "close-btn"),
    },
    siteLayoutCascadeSelect: {
      component: "site-layout-cascade-select",
      skeleton: (): string => joinDataTestId(DATA_TEST_IDS.components.siteLayoutCascadeSelect.component, "skeleton"),
      select: (): string => joinDataTestId(DATA_TEST_IDS.components.siteLayoutCascadeSelect.component, "select"),
    },
    sitePushButtonCard: {
      component: "site-push-button-card",
      loadingSkeleton: (): string =>
        joinDataTestId(DATA_TEST_IDS.components.sitePushButtonCard.component, "loading-skeleton"),
      card: (): string => joinDataTestId(DATA_TEST_IDS.components.sitePushButtonCard.component, "card"),
      header: (): string => joinDataTestId(DATA_TEST_IDS.components.sitePushButtonCard.component, "header"),
      title: (): string => joinDataTestId(DATA_TEST_IDS.components.sitePushButtonCard.component, "title"),
      content: (): string => joinDataTestId(DATA_TEST_IDS.components.sitePushButtonCard.component, "content"),
      lastPushTitle: (): string =>
        joinDataTestId(DATA_TEST_IDS.components.sitePushButtonCard.component, "last-push-title"),
      lastPushValue: (): string =>
        joinDataTestId(DATA_TEST_IDS.components.sitePushButtonCard.component, "last-push-value"),
      last72hTitle: (): string =>
        joinDataTestId(DATA_TEST_IDS.components.sitePushButtonCard.component, "last-72h-title"),
      last72hValue: (): string =>
        joinDataTestId(DATA_TEST_IDS.components.sitePushButtonCard.component, "last-72h-value"),
      errorSkeleton: (): string =>
        joinDataTestId(DATA_TEST_IDS.components.sitePushButtonCard.component, "error-skeleton"),
    },
    advancedSubscriberSearch: {
      component: "advanced-subscriber-search",
      card: (): string => joinDataTestId(DATA_TEST_IDS.components.advancedSubscriberSearch.component, "card"),
      header: (): string => joinDataTestId(DATA_TEST_IDS.components.advancedSubscriberSearch.component, "header"),
      title: (): string => joinDataTestId(DATA_TEST_IDS.components.advancedSubscriberSearch.component, "title"),
      content: (): string => joinDataTestId(DATA_TEST_IDS.components.advancedSubscriberSearch.component, "content"),
      form: (): string => joinDataTestId(DATA_TEST_IDS.components.advancedSubscriberSearch.component, "form"),
      selectFieldsHeader: (): string =>
        joinDataTestId(DATA_TEST_IDS.components.advancedSubscriberSearch.component, "select-fields-header"),
      addFieldBtn: (): string =>
        joinDataTestId(DATA_TEST_IDS.components.advancedSubscriberSearch.component, "add-field-btn"),
      fieldRow: (idx: number): string =>
        joinDataTestId(DATA_TEST_IDS.components.advancedSubscriberSearch.component, "field-row", String(idx)),
      fieldDropdown: (idx: number): string =>
        joinDataTestId(DATA_TEST_IDS.components.advancedSubscriberSearch.component, "field-dropdown", String(idx)),
      filterTypeDropdown: (idx: number): string =>
        joinDataTestId(
          DATA_TEST_IDS.components.advancedSubscriberSearch.component,
          "filter-type-dropdown",
          String(idx),
        ),
      valuePhone: (idx: number): string =>
        joinDataTestId(DATA_TEST_IDS.components.advancedSubscriberSearch.component, "value-phone", String(idx)),
      valueInput: (idx: number): string =>
        joinDataTestId(DATA_TEST_IDS.components.advancedSubscriberSearch.component, "value-input", String(idx)),
      removeFieldBtn: (idx: number): string =>
        joinDataTestId(DATA_TEST_IDS.components.advancedSubscriberSearch.component, "remove-field-btn", String(idx)),
      submitBtn: (): string =>
        joinDataTestId(DATA_TEST_IDS.components.advancedSubscriberSearch.component, "submit-btn"),
      resultsTitle: (): string =>
        joinDataTestId(DATA_TEST_IDS.components.advancedSubscriberSearch.component, "results-title"),
      loadingSpinner: (): string =>
        joinDataTestId(DATA_TEST_IDS.components.advancedSubscriberSearch.component, "loading-spinner"),
      noResultsTitle: (): string =>
        joinDataTestId(DATA_TEST_IDS.components.advancedSubscriberSearch.component, "no-results-title"),
      noResultsText: (): string =>
        joinDataTestId(DATA_TEST_IDS.components.advancedSubscriberSearch.component, "no-results-text"),
      table: (): string => joinDataTestId(DATA_TEST_IDS.components.advancedSubscriberSearch.component, "table"),
      attachBtn: (): string =>
        joinDataTestId(DATA_TEST_IDS.components.advancedSubscriberSearch.component, "attach-btn"),
    },
    siteMyvAccessButton: {
      component: "site-myv-access-button",
      button: (): string => joinDataTestId(DATA_TEST_IDS.components.siteMyvAccessButton.component, "button"),
      message: (): string => joinDataTestId(DATA_TEST_IDS.components.siteMyvAccessButton.component, "message"),
      redirectText: (): string =>
        joinDataTestId(DATA_TEST_IDS.components.siteMyvAccessButton.component, "redirect-text"),
      agreementText: (): string =>
        joinDataTestId(DATA_TEST_IDS.components.siteMyvAccessButton.component, "agreement-text"),
    },
    subscriberFeedbackCard: {
      component: "subscriber-feedback-card",
      card: (): string => joinDataTestId(DATA_TEST_IDS.components.subscriberFeedbackCard.component, "card"),
      header: (): string => joinDataTestId(DATA_TEST_IDS.components.subscriberFeedbackCard.component, "header"),
      title: (): string => joinDataTestId(DATA_TEST_IDS.components.subscriberFeedbackCard.component, "title"),
      content: (): string => joinDataTestId(DATA_TEST_IDS.components.subscriberFeedbackCard.component, "content"),
      subjectLabel: (): string =>
        joinDataTestId(DATA_TEST_IDS.components.subscriberFeedbackCard.component, "subject-label"),
      subjectDropdown: (): string =>
        joinDataTestId(DATA_TEST_IDS.components.subscriberFeedbackCard.component, "subject-dropdown"),
      divider: (): string => joinDataTestId(DATA_TEST_IDS.components.subscriberFeedbackCard.component, "divider"),
      subjectOption: (value: string): string =>
        joinDataTestId(DATA_TEST_IDS.components.subscriberFeedbackCard.component, "subject-option", value),
    },
    supportFormCard: {
      component: "support-form-card",
      title: (): string => joinDataTestId(DATA_TEST_IDS.components.supportFormCard.component, "title"),
      header: (): string => joinDataTestId(DATA_TEST_IDS.components.supportFormCard.component, "header"),
      content: (): string => joinDataTestId(DATA_TEST_IDS.components.supportFormCard.component, "content"),
      form: (): string => joinDataTestId(DATA_TEST_IDS.components.supportFormCard.component, "form"),
      supportTypeLabel: (): string =>
        joinDataTestId(DATA_TEST_IDS.components.supportFormCard.form(), "support-type-label"),
      supportTypeDropdown: (): string =>
        joinDataTestId(DATA_TEST_IDS.components.supportFormCard.form(), "support-type-dropdown"),
      supportTypeError: (): string =>
        joinDataTestId(DATA_TEST_IDS.components.supportFormCard.form(), "support-type-error"),
      subscriberNeedLabel: (): string =>
        joinDataTestId(DATA_TEST_IDS.components.supportFormCard.form(), "subscriber-need-label"),
      subscriberNeedDropdown: (): string =>
        joinDataTestId(DATA_TEST_IDS.components.supportFormCard.form(), "subscriber-need-dropdown"),
      commentLabel: (): string => joinDataTestId(DATA_TEST_IDS.components.supportFormCard.form(), "comment-label"),
      commentTextarea: (): string =>
        joinDataTestId(DATA_TEST_IDS.components.supportFormCard.form(), "comment-text-area"),
      commentError: (): string => joinDataTestId(DATA_TEST_IDS.components.supportFormCard.form(), "comment-error"),
      submitBtn: (): string => joinDataTestId(DATA_TEST_IDS.components.supportFormCard.form(), "submit-btn"),
    },
    siteDocuments: {
      component: "site-documents",
    },
    siteDocumentsEmpty: {
      component: "site-documents-empty",
      title: (): string => joinDataTestId(DATA_TEST_IDS.components.siteDocumentsEmpty.component, "title"),
      message: (): string => joinDataTestId(DATA_TEST_IDS.components.siteDocumentsEmpty.component, "message"),
    },
    siteDocumentsTable: {
      component: "site-documents-table",
      table: (): string => joinDataTestId(DATA_TEST_IDS.components.siteDocumentsTable.component, "table"),
      rowCell: (id: string | number, column: string): string =>
        joinDataTestId(DATA_TEST_IDS.components.siteDocumentsTable.component, "row", String(id), column),
    },
    escContextualActionButton: {
      component: "contextual-action-button",
      button: (actionType: string): string =>
        joinDataTestId(DATA_TEST_IDS.components.escContextualActionButton.component, actionType),
      optionsCount: (actionType: string): string =>
        joinDataTestId(DATA_TEST_IDS.components.escContextualActionButton.component, "options-count", actionType),
      option: (actionType: string): string =>
        joinDataTestId(DATA_TEST_IDS.components.escContextualActionButton.component, "option", actionType),
    },
    escHierarchicalEvent: {
      component: "hierarchical-event",
      date: (eventId: string | number): string =>
        joinDataTestId(DATA_TEST_IDS.components.escHierarchicalEvent.component, "date", String(eventId)),
      time: (eventId: string | number): string =>
        joinDataTestId(DATA_TEST_IDS.components.escHierarchicalEvent.component, "time", String(eventId)),
      actionType: (eventId: string | number): string =>
        joinDataTestId(DATA_TEST_IDS.components.escHierarchicalEvent.component, "action-type", String(eventId)),
      application: (eventId: string | number): string =>
        joinDataTestId(DATA_TEST_IDS.components.escHierarchicalEvent.component, "application", String(eventId)),
      systemAction: (eventId: string | number): string =>
        joinDataTestId(DATA_TEST_IDS.components.escHierarchicalEvent.component, "system-action", String(eventId)),
      actorLabel: (eventId: string | number): string =>
        joinDataTestId(DATA_TEST_IDS.components.escHierarchicalEvent.component, "actor-label", String(eventId)),
      toggleContext: (eventId: string | number): string =>
        joinDataTestId(DATA_TEST_IDS.components.escHierarchicalEvent.component, "toggle-context", String(eventId)),
      contextKeysCount: (eventId: string | number): string =>
        joinDataTestId(DATA_TEST_IDS.components.escHierarchicalEvent.component, "context-keys-count", String(eventId)),
      contextData: (eventId: string | number): string =>
        joinDataTestId(DATA_TEST_IDS.components.escHierarchicalEvent.component, "context-data", String(eventId)),
    },
    escFolderStageTimelineCircular: {
      component: "esc-folder-stage-timeline-circular",
      statusBanner: (stage: string): string =>
        joinDataTestId(DATA_TEST_IDS.components.escFolderStageTimelineCircular.component, "status-banner", stage),
      statusLabel: (stage: string): string =>
        joinDataTestId(DATA_TEST_IDS.components.escFolderStageTimelineCircular.component, "status-label", stage),
      groupLabel: (groupId: string | number): string =>
        joinDataTestId(
          DATA_TEST_IDS.components.escFolderStageTimelineCircular.component,
          "group-label",
          String(groupId),
        ),
      substageLabel: (groupId: string | number, stage: string): string =>
        joinDataTestId(
          DATA_TEST_IDS.components.escFolderStageTimelineCircular.component,
          "substage-label",
          String(groupId),
          stage,
        ),
    },
    escEventContextDetails: {
      component: "event-context-details",
      toggleButton: (eventId: string | number): string =>
        joinDataTestId(DATA_TEST_IDS.components.escEventContextDetails.component, "toggle-button", String(eventId)),
      contextDataPre: (eventId: string | number): string =>
        joinDataTestId(DATA_TEST_IDS.components.escEventContextDetails.component, "context-data-pre", String(eventId)),
    },
    escFolderInfoBadges: {
      component: "folder-info-badges",
      phaseBadge: (): string => joinDataTestId(DATA_TEST_IDS.components.escFolderInfoBadges.component, "phase-badge"),
      phaseTooltipButton: (): string =>
        joinDataTestId(DATA_TEST_IDS.components.escFolderInfoBadges.component, "phase-tooltip-button"),
      engagementDateBadge: (): string =>
        joinDataTestId(DATA_TEST_IDS.components.escFolderInfoBadges.component, "engagement-date-badge"),
      engagementDateTooltipButton: (): string =>
        joinDataTestId(DATA_TEST_IDS.components.escFolderInfoBadges.component, "engagement-date-tooltip-button"),
      escTypesBadge: (): string =>
        joinDataTestId(DATA_TEST_IDS.components.escFolderInfoBadges.component, "esc-types-badge"),
      escTypesTooltipButton: (): string =>
        joinDataTestId(DATA_TEST_IDS.components.escFolderInfoBadges.component, "esc-types-tooltip-button"),
    },
    escHistoryFilters: {
      component: "history-filters",
      displayModeListButton: (): string =>
        joinDataTestId(DATA_TEST_IDS.components.escHistoryFilters.component, "display-mode-list-button"),
      displayModeHierarchyButton: (): string =>
        joinDataTestId(DATA_TEST_IDS.components.escHistoryFilters.component, "display-mode-hierarchy-button"),
      applicationFilterButton: (application: string): string =>
        joinDataTestId(DATA_TEST_IDS.components.escHistoryFilters.component, "application-filter-button", application),
      actorFilterButton: (actorKey: string): string =>
        joinDataTestId(DATA_TEST_IDS.components.escHistoryFilters.component, "actor-filter-button", actorKey),
      clearFiltersButton: (): string =>
        joinDataTestId(DATA_TEST_IDS.components.escHistoryFilters.component, "clear-filters-button"),
    },
    escFolderGeneralInfoCard: {
      component: "folder-general-info-card",
      card: (): string => joinDataTestId(DATA_TEST_IDS.components.escFolderGeneralInfoCard.component, "card"),
      statusIcon: (): string =>
        joinDataTestId(DATA_TEST_IDS.components.escFolderGeneralInfoCard.component, "status-icon"),
      statusText: (): string =>
        joinDataTestId(DATA_TEST_IDS.components.escFolderGeneralInfoCard.component, "status-text"),
      actionsSection: (): string =>
        joinDataTestId(DATA_TEST_IDS.components.escFolderGeneralInfoCard.component, "actions-section"),
      toggleActionsButton: (): string =>
        joinDataTestId(DATA_TEST_IDS.components.escFolderGeneralInfoCard.component, "toggle-actions-button"),
    },
    reloadPrompt: {
      component: "reload-prompt",
      refreshButton: (): string => joinDataTestId(DATA_TEST_IDS.components.reloadPrompt.component, "refresh-button"),
      dismissButton: (): string => joinDataTestId(DATA_TEST_IDS.components.reloadPrompt.component, "dismiss-button"),
    },
    updateNotification: {
      component: "update-notification",
      button: (): string => joinDataTestId(DATA_TEST_IDS.components.updateNotification.component, "button"),
      badge: (): string => joinDataTestId(DATA_TEST_IDS.components.updateNotification.component, "badge"),
      popover: (): string => joinDataTestId(DATA_TEST_IDS.components.updateNotification.component, "popover"),
      currentVersion: (): string =>
        joinDataTestId(DATA_TEST_IDS.components.updateNotification.component, "current-version"),
      newVersion: (): string => joinDataTestId(DATA_TEST_IDS.components.updateNotification.component, "new-version"),
      cancelButton: (): string =>
        joinDataTestId(DATA_TEST_IDS.components.updateNotification.component, "cancel-button"),
      updateButton: (): string =>
        joinDataTestId(DATA_TEST_IDS.components.updateNotification.component, "update-button"),
    },
    formErrorWitness: formErrorWitnessDataTestIds,
    editApplianceEstimatesModalForm: editApplianceEstimatesModalForm,
  },
};
